import { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  LogOut, Home, Loader2, Users, FileText, Calendar, Mail, Plus, Trash2, Pencil,
  LayoutDashboard, Check, Upload, Download, ClipboardList, RefreshCw, Camera, CreditCard, Image as ImageIcon, Save,
  Send, UserPlus, Settings, X, History, Phone, ShieldAlert, MessageSquare, ChevronDown, ChevronUp, AlertTriangle,
  Search, ShieldCheck, Lightbulb, FileDown,
} from "lucide-react";
import { toast } from "sonner";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Badge } from "../../components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../../components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "../../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import api, { formatApiError } from "../../lib/api";
import { formatDate as fmt } from "../../lib/date";
import { KeyRound } from "lucide-react";
import { User } from "lucide-react";
import { useAuth } from "../../context/AuthContext";
import { useContent } from "../../context/ContentContext";
import HeroBackdrop from "../../components/HeroBackdrop";
import { GuideCard } from "../../components/GuideCard";
import { ImageCropDialog } from "../../components/ImageCropDialog";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const resolvePhoto = (u) => (u && u.startsWith("/") ? `${BACKEND}${u}` : u);
const memInitials = (name) => (name || "?").split(" ").map((n) => n[0]).join("").slice(0, 2).toUpperCase();

const APP_STAGE_SLA_DAYS = 14;
const appOverdue = (a) => {
  if (a.converted_user_id || (a.payment_check && a.payment_check.done)) return false;
  const base = (a.driver_check && a.driver_check.done && a.driver_check.at) || a.created_at;
  if (!base) return false;
  return Date.now() - new Date(base).getTime() > APP_STAGE_SLA_DAYS * 86400000;
};
const appMatches = (a, f) => {
  const driver = a.driver_check && a.driver_check.done;
  const payment = a.payment_check && a.payment_check.done;
  if (f === "awaiting-driver") return !driver;
  if (f === "awaiting-payment") return driver && !payment;
  if (f === "ready") return payment;
  if (f === "overdue") return appOverdue(a);
  return true;
};

const APP_STATUS = ["new", "reviewed", "approved", "rejected"];const STATUS_COLOR = {
  new: "bg-blue-100 text-blue-700", reviewed: "bg-amber-100 text-amber-700",
  approved: "bg-green-100 text-green-700", rejected: "bg-red-100 text-red-700",
};
const RECERT_BADGE = {
  overdue: "bg-red-100 text-red-700", due_soon: "bg-amber-100 text-amber-700",
  ok: "bg-green-100 text-green-700", none: "bg-slate-100 text-slate-500",
};
const ENQ_BADGE = { open: "bg-blue-100 text-blue-700", pending: "bg-amber-100 text-amber-700", closed: "bg-green-100 text-green-700" };
const MEMBER_BADGE = { expired: "bg-red-100 text-red-700", due_soon: "bg-amber-100 text-amber-700", ok: "bg-green-100 text-green-700", none: "bg-slate-100 text-slate-500" };

export default function AdminDashboard() {
  const { logout, user } = useAuth();
  const can = (k) => !!(user && (user.is_owner || (user.permissions || []).includes(k)));
  useEffect(() => {
    if (!user || user.is_owner) return;
    const order = ["applications", "members", "tutors", "events", "news", "gallery", "documents", "content", "backgrounds", "enquiries", "community", "payments", "reports", "feedback", "privacy", "settings", "people", "activity"];
    const allowed = order.filter(can);
    if (!allowed.includes(tab) && tab !== "admins") setTab(allowed[0] || "settings");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);
  const { t } = useContent();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [applications, setApplications] = useState([]);
  const [members, setMembers] = useState([]);
  const [events, setEvents] = useState([]);
  const [news, setNews] = useState([]);
  const [gallery, setGallery] = useState([]);
  const [enquiries, setEnquiries] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [payments, setPayments] = useState(null);
  const [loading, setLoading] = useState(true);
  const [settings, setSettings] = useState({});
  const [appFilter, setAppFilter] = useState("all");
  const [tab, setTab] = useState("applications");
  const [enqFilter, setEnqFilter] = useState("all");

  const loadAll = () => {
    if (!user) { setLoading(false); return; }
    const jobs = {
      stats: api.get("/admin/stats"),
      members: (can("members") || can("tutors")) ? api.get("/admin/members") : null,
      applications: can("applications") ? api.get("/admin/applications") : null,
      events: can("events") ? api.get("/admin/events") : null,
      news: can("news") ? api.get("/news") : null,
      gallery: can("gallery") ? api.get("/gallery") : null,
      enquiries: can("enquiries") ? api.get("/admin/enquiries") : null,
      documents: can("documents") ? api.get("/documents") : null,
      payments: can("payments") ? api.get("/admin/payments") : null,
      settings: api.get("/admin/settings"),
    };
    const keys = Object.keys(jobs).filter((k) => jobs[k]);
    Promise.allSettled(keys.map((k) => jobs[k])).then((res) => {
      const setters = { stats: setStats, members: setMembers, applications: setApplications, events: setEvents, news: setNews, gallery: setGallery, enquiries: setEnquiries, documents: setDocuments, payments: setPayments, settings: setSettings };
      keys.forEach((k, i) => { if (res[i].status === "fulfilled") setters[k](res[i].value.data); });
    }).finally(() => setLoading(false));
  };
  useEffect(loadAll, [user]);
  const roleNames = {
    secretary: settings.role_membership_secretary || "",
    treasurer: settings.role_treasurer || "",
    controller: settings.role_associate_controller || "",
  };

  const doLogout = async () => { await logout(); navigate("/"); };

  const setAppStatus = async (id, status) => {
    try { await api.patch(`/admin/applications/${id}`, { status }); toast.success("Updated"); loadAll(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const approveApp = async (a) => {
    if (!window.confirm(`Create a member account for ${a.name} and email them a "set your password" link?`)) return;
    try {
      const { data } = await api.post(`/admin/applications/${a.id}/approve`);
      toast.success(data.created ? "Member created — welcome email sent" : "Membership activated");
      loadAll();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const rejectApp = async (a, payload) => {
    try {
      await api.post(`/admin/applications/${a.id}/reject`, payload);
      toast.success("Application declined — applicant notified");
      loadAll();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const delItem = async (path) => {
    try { await api.delete(path); toast.success("Deleted"); loadAll(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  if (loading)
    return <div className="min-h-screen flex items-center justify-center bg-slate-50"><Loader2 className="h-8 w-8 animate-spin text-[#0F2744]" /></div>;

  const todayStr = new Date().toISOString().slice(0, 10);
  const renewalsDueCount = members.filter((m) => ["due_soon", "expired"].includes(m.membership_expiry_status)).length;
  const openEnquiriesCount = enquiries.filter((q) => (q.status || "open") === "open").length;
  const upcomingEventsCount = events.filter((e) => (e.date || "") >= todayStr).length;
  const tutorLoads = members.filter((x) => x.is_tutor).reduce((acc, tu) => {
    acc[tu.name] = { count: members.filter((x) => x.tutor_assigned === tu.name).length, capacity: tu.tutor_capacity ?? 6 };
    return acc;
  }, {});

  return (
    <div className="min-h-screen bg-slate-50" data-testid="admin-dashboard">
      <header className="bg-[#0F2744] sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2.5">
            <img src="/rospa-logo.png" alt="Rospa Advanced Drivers and Riders" className="h-9 w-9 object-contain" />
            <span className="text-white font-heading font-bold">Admin Console</span>
          </Link>
          <div className="flex items-center gap-1 sm:gap-2">
            <Button asChild variant="ghost" className="text-slate-200 hover:bg-white/10 px-2 sm:px-3"><Link to="/" data-testid="admin-home-btn"><Home className="h-4 w-4 sm:mr-1.5" /><span className="hidden sm:inline">Site</span></Link></Button>
            <Button asChild variant="ghost" className="text-slate-200 hover:bg-white/10 px-2 sm:px-3"><Link to="/messaging" data-testid="admin-messaging-btn"><Mail className="h-4 w-4 sm:mr-1.5" /><span className="hidden sm:inline">Messaging</span></Link></Button>
            <Button asChild variant="ghost" className="text-slate-200 hover:bg-white/10 px-2 sm:px-3"><Link to="/member" data-testid="admin-memberview-btn"><LayoutDashboard className="h-4 w-4 sm:mr-1.5" /><span className="hidden sm:inline">Member view</span></Link></Button>
            <Button asChild variant="ghost" className="text-slate-200 hover:bg-white/10 px-2 sm:px-3"><Link to="/profile" data-testid="admin-profile-btn"><User className="h-4 w-4 sm:mr-1.5" /><span className="hidden sm:inline">Your Profile</span></Link></Button>
            <Button onClick={doLogout} variant="ghost" className="text-slate-200 hover:bg-white/10 px-2 sm:px-3" data-testid="admin-logout-btn"><LogOut className="h-4 w-4 sm:mr-1.5" /><span className="hidden sm:inline">Sign out</span></Button>
          </div>
        </div>
      </header>

      <div className="relative overflow-hidden bg-[#0F2744]">
        <HeroBackdrop image={t("img.admin", "https://images.unsplash.com/photo-1552664730-d307ca884978?auto=format&fit=crop&q=85&w=1600")} opacity={0.3} />
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <h1 className="text-2xl font-extrabold text-white">Club administration</h1>
          <p className="text-slate-300 mt-1">Manage applications, members, events, content and backgrounds.</p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {stats && (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-8">
          {can("applications") && <Stat icon={FileText} label="Applications" value={stats.applications} sub={`${stats.new_applications} new`} badge={stats.new_applications} onClick={() => setTab("applications")} />}
          {can("members") && <Stat icon={Users} label="Members" value={stats.members} sub={renewalsDueCount ? `${renewalsDueCount} due` : ""} badge={renewalsDueCount} onClick={() => setTab("members")} />}
          {can("events") && <Stat icon={Calendar} label="Events" value={stats.events} sub={upcomingEventsCount ? `${upcomingEventsCount} upcoming` : ""} badge={upcomingEventsCount} onClick={() => setTab("events")} />}
          {can("events") && <Stat icon={Check} label="Bookings" value={stats.bookings} onClick={() => setTab("events")} />}
          {can("enquiries") && <Stat icon={Mail} label="Enquiries" value={stats.enquiries} sub={openEnquiriesCount ? `${openEnquiriesCount} open` : ""} badge={openEnquiriesCount} onClick={() => { setEnqFilter("open"); setTab("enquiries"); }} />}
          {can("documents") && <Stat icon={FileText} label="Documents" value={documents.length} onClick={() => setTab("documents")} />}
        </div>
        )}

        <Tabs value={tab} onValueChange={setTab}>
          <div className="space-y-2" data-testid="admin-tabs">
            <div className="rounded-xl bg-white border border-slate-200 p-2">
              <div className="text-[10px] font-bold uppercase tracking-wider text-slate-400 px-1 pb-1.5">Front-end · public site</div>
              <TabsList className="flex flex-wrap h-auto gap-1 bg-transparent p-0">
                {can("events") && <TabsTrigger value="events" data-testid="tab-events">Events</TabsTrigger>}
                {can("news") && <TabsTrigger value="news" data-testid="tab-news">News</TabsTrigger>}
                {can("gallery") && <TabsTrigger value="gallery" data-testid="tab-gallery">Gallery</TabsTrigger>}
                {can("documents") && <TabsTrigger value="documents" data-testid="tab-documents">Documents</TabsTrigger>}
                {can("content") && <TabsTrigger value="content" data-testid="tab-content">Content</TabsTrigger>}
                {can("backgrounds") && <TabsTrigger value="backgrounds" data-testid="tab-backgrounds">Backgrounds</TabsTrigger>}
                <TabsTrigger value="guides" data-testid="tab-guides">Guides</TabsTrigger>
              </TabsList>
            </div>
            <div className="rounded-xl bg-white border border-slate-200 p-2">
              <div className="text-[10px] font-bold uppercase tracking-wider text-slate-400 px-1 pb-1.5">Back-end · club administration</div>
              <TabsList className="flex flex-wrap h-auto gap-1 bg-transparent p-0">
                {can("applications") && <TabsTrigger value="applications" data-testid="tab-applications">Applications</TabsTrigger>}
                {can("members") && <TabsTrigger value="members" data-testid="tab-members">Members</TabsTrigger>}
                {can("tutors") && <TabsTrigger value="tutors" data-testid="tab-tutors">Tutors</TabsTrigger>}
                {can("enquiries") && <TabsTrigger value="enquiries" data-testid="tab-enquiries">Enquiries</TabsTrigger>}
                {can("feedback") && <TabsTrigger value="feedback" data-testid="tab-feedback">Feedback</TabsTrigger>}
                {can("community") && <TabsTrigger value="community" data-testid="tab-community">Community</TabsTrigger>}
                {can("payments") && <TabsTrigger value="payments" data-testid="tab-payments">Payments</TabsTrigger>}
                {can("reports") && <TabsTrigger value="reports" data-testid="tab-reports">Reports</TabsTrigger>}
                {can("privacy") && <TabsTrigger value="privacy" data-testid="tab-privacy">Privacy</TabsTrigger>}
                {can("activity") && <TabsTrigger value="activity" data-testid="tab-activity">Activity</TabsTrigger>}
                {can("settings") && <TabsTrigger value="settings" data-testid="tab-settings">Settings</TabsTrigger>}
                {can("people") && <TabsTrigger value="people" data-testid="tab-people">People</TabsTrigger>}
                {can("manage_admins") && <TabsTrigger value="admins" data-testid="tab-admins">Admins</TabsTrigger>}
              </TabsList>
            </div>
          </div>

          {/* APPLICATIONS */}
          <TabsContent value="applications" className="mt-6 space-y-4">
            <a href="/joining-guide.html" target="_blank" rel="noopener noreferrer" className="flex items-center justify-between gap-3 rounded-xl bg-[#0F2744] text-white px-4 py-3 hover:bg-[#0B192C] transition-colors" data-testid="joining-guide-link">
              <span className="flex items-center gap-2 text-sm font-semibold"><FileDown className="h-4 w-4 text-amber-400" /> How joining works — committee guide (PDF)</span>
              <span className="text-xs text-slate-300">Open &amp; print →</span>
            </a>
            {applications.length > 0 && (() => {
              const awaitingDriver = applications.filter((a) => !(a.driver_check && a.driver_check.done)).length;
              const awaitingPayment = applications.filter((a) => a.driver_check && a.driver_check.done && !(a.payment_check && a.payment_check.done)).length;
              const readyApprove = applications.filter((a) => a.payment_check && a.payment_check.done).length;
              const overdue = applications.filter(appOverdue).length;
              const pills = [
                { key: "all", label: "All", n: applications.length, cls: "bg-slate-100 text-slate-700" },
                { key: "awaiting-driver", label: "Awaiting driver check", n: awaitingDriver, cls: "bg-slate-100 text-slate-700" },
                { key: "awaiting-payment", label: "Awaiting payment check", n: awaitingPayment, cls: "bg-amber-100 text-amber-700" },
                { key: "ready", label: "Ready to approve", n: readyApprove, cls: "bg-green-100 text-green-700" },
                ...(overdue ? [{ key: "overdue", label: "Overdue 14d+", n: overdue, cls: "bg-red-100 text-red-700" }] : []),
              ];
              return (
                <div className="flex flex-wrap items-center gap-2 rounded-2xl bg-white border border-slate-200 p-3" data-testid="applications-pipeline">
                  <span className="text-xs font-semibold text-slate-500 mr-1">Pipeline:</span>
                  {pills.map((p) => (
                    <button key={p.key} type="button" onClick={() => setAppFilter(p.key)} className={`px-3 py-1 rounded-full text-xs font-semibold transition-all ${p.cls} ${appFilter === p.key ? "ring-2 ring-offset-1 ring-[#0F2744]" : "opacity-80 hover:opacity-100"}`} data-testid={`pipeline-${p.key}`}>
                      {p.n} · {p.label}
                    </button>
                  ))}
                  {appFilter !== "all" && <button type="button" onClick={() => setAppFilter("all")} className="text-xs text-slate-400 hover:underline ml-1" data-testid="pipeline-clear">Clear filter</button>}
                </div>
              );
            })()}
            {applications.filter((a) => appMatches(a, appFilter)).map((a) => (
              <div key={a.id} className="rounded-2xl bg-white border border-slate-200 p-5" data-testid={`admin-app-${a.id}`}>
                <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
                  <div>
                    <div className="flex items-center gap-2 flex-wrap">
                      <h3 className="font-bold text-[#0F2744]">{a.name}</h3>
                      <Badge className={`${STATUS_COLOR[a.status]} hover:${STATUS_COLOR[a.status]}`}>{a.status}</Badge>
                      <Badge variant="outline" className="capitalize">{a.pathway}</Badge>
                      {(a.payment_status === "paid" || (a.payment_check && a.payment_check.done)) ? (
                        <Badge className="bg-green-100 text-green-700 hover:bg-green-100" data-testid={`app-paybadge-${a.id}`}>Paid {t("fee.join", "£65")}</Badge>
                      ) : a.payment_link_sent_at ? (
                        <Badge className="bg-blue-100 text-blue-700 hover:bg-blue-100" data-testid={`app-paybadge-${a.id}`}>Link sent</Badge>
                      ) : (
                        <Badge className="bg-amber-100 text-amber-700 hover:bg-amber-100" data-testid={`app-paybadge-${a.id}`}>Awaiting payment</Badge>
                      )}
                      {a.converted_user_id && <Badge className="bg-[#0F2744] text-white hover:bg-[#0F2744]">Member created</Badge>}
                      {appOverdue(a) && <Badge className="bg-red-100 text-red-700 hover:bg-red-100 flex items-center gap-1" data-testid={`app-overdue-${a.id}`}><AlertTriangle className="h-3 w-3" /> Overdue</Badge>}
                    </div>
                    <div className="mt-2 text-sm text-slate-500 space-y-1">
                      <div><a href={`mailto:${a.email}`} className="text-blue-600">{a.email}</a> · {a.phone}</div>
                      {a.experience && <div>Experience: {a.experience}</div>}
                      <div>Grade: {a.current_grade} · {fmt(a.created_at)}</div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <ApplicationDialog app={a} canEmergency={can("emergency")} />
                    {a.converted_user_id ? (
                      <span className="text-xs font-semibold text-green-700 flex items-center gap-1"><Check className="h-4 w-4" /> Member created</span>
                    ) : (
                      <>
                        <Button onClick={() => approveApp(a)} disabled={!(a.payment_check && a.payment_check.done)} title={a.payment_check && a.payment_check.done ? "" : "Complete the payment check first"} className="bg-green-600 hover:bg-green-700 text-white disabled:opacity-50" data-testid={`approve-app-${a.id}`}>
                          <UserPlus className="h-4 w-4 mr-1.5" /> Approve &amp; make member
                        </Button>
                        <RejectDialog application={a} onReject={(reason) => rejectApp(a, reason)} />
                      </>
                    )}
                  </div>
                </div>
                <ApplicationStages a={a} onChanged={loadAll} canEmergency={can("emergency")} roleNames={roleNames} />
              </div>
            ))}
            {applications.filter((a) => appMatches(a, appFilter)).length === 0 && <Empty text={appFilter === "all" ? "No live applications right now." : "No applications in this view."} />}
            <ApprovedHistory />
            <DeclinedHistory />
          </TabsContent>

          {/* MEMBERS */}
          <TabsContent value="members" className="mt-6 space-y-3">
            <PiiRequests />
            <div className="flex justify-end gap-2"><ImportMembersDialog onDone={loadAll} /><BulkResendButton /></div>
            <RenewalsDue members={members} onChanged={loadAll} />
            <AwaitingTutor members={members} tutors={members.filter((x) => x.is_tutor)} onChanged={loadAll} />
            {members.map((m) => <MemberRow key={m.id} m={m} tutors={members.filter((x) => x.is_tutor)} tutorLoads={tutorLoads} canEmergency={can("emergency")} roleNames={roleNames} onChanged={loadAll} />)}
          </TabsContent>

          {/* TUTORS */}
          <TabsContent value="tutors" className="mt-6 space-y-3">
            <CapacityDashboard members={members} />
            {members.filter((m) => m.is_tutor).length === 0 && <Empty text="No tutors yet. Tick 'Available as a tutor' on a member to add them here." />}
            {members.filter((m) => m.is_tutor).map((tu) => {
              const associates = members.filter((x) => x.tutor_assigned === tu.name);
              return (
                <div key={tu.id} className="rounded-2xl bg-white border border-slate-200 p-5" data-testid={`admin-tutor-${tu.id}`}>
                  <div className="flex items-start justify-between gap-4">
                    <div className="min-w-0">
                      <h3 className="font-bold text-[#0F2744]">{tu.name}</h3>
                      <p className="text-sm text-slate-500 capitalize">{tu.email} · {tu.pathway} · Grade {tu.grade || "none"}</p>
                    </div>
                    <div className="text-right">
                      <div className="text-xs text-slate-400 mb-1">Available days</div>
                      <div className="flex gap-1.5 justify-end flex-wrap">
                        {(tu.tutor_days || []).length ? (tu.tutor_days).map((d) => <span key={d} className="px-2.5 py-1 rounded-full text-xs font-semibold bg-slate-100 text-[#0F2744]">{d}</span>) : <span className="text-xs text-slate-400">Not set</span>}
                      </div>
                    </div>
                  </div>
                  <div className="mt-4 border-t border-slate-100 pt-3">
                    <div className="flex items-center justify-between mb-2">
                      <div className="text-xs font-semibold text-slate-400">
                        Assigned associates <span className={associates.length > (tu.tutor_capacity || 6) ? "text-red-600 font-bold" : "text-[#0F2744]"}>({associates.length} / {tu.tutor_capacity || 6})</span>
                      </div>
                      <TutorCapacity tutor={tu} onChanged={loadAll} />
                    </div>
                    {associates.length === 0 ? (
                      <p className="text-sm text-slate-400">No associates assigned yet.</p>
                    ) : (
                      <div className="space-y-1.5">
                        {associates.map((a) => (
                          <div key={a.id} className="flex items-center justify-between gap-2 text-sm" data-testid={`tutor-associate-${tu.id}-${a.id}`}>
                            <span className="font-medium text-[#0F2744] truncate">{a.name} <span className="text-slate-400 font-normal capitalize">· {a.pathway} · Grade {a.grade || "none"}</span></span>
                            <span className="text-xs text-slate-500 shrink-0">Joined {fmt(a.created_at)}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                  <RebalanceSuggestion tutor={tu} associates={associates} tutors={members.filter((x) => x.is_tutor)} members={members} onChanged={loadAll} />
                </div>
              );
            })}
          </TabsContent>

          {/* EVENTS */}
          <TabsContent value="events" className="mt-6">
            <div className="flex justify-end mb-4"><EventDialog onSaved={loadAll} /></div>
            <div className="space-y-3">
              {events.map((e) => (
                <div key={e.id} className="rounded-2xl bg-white border border-slate-200 p-5 flex items-center justify-between gap-4" data-testid={`admin-event-${e.id}`}>
                  <div className="flex items-center gap-4 min-w-0">
                    <img src={e.image_url} alt="" className="h-14 w-14 rounded-lg object-cover shrink-0" />
                    <div className="min-w-0">
                      <div className="flex items-center gap-2">
                        <h3 className="font-bold text-[#0F2744] truncate">{e.title}</h3>
                        {!e.published && <Badge className="bg-amber-100 text-amber-700 text-[10px] shrink-0" data-testid={`event-draft-${e.id}`}>Draft</Badge>}
                        {e.published && e.visibility === "private" && <Badge className="bg-slate-100 text-slate-600 text-[10px] shrink-0">Members only</Badge>}
                      </div>
                      <div className="text-sm text-slate-500">{e.category} · {e.date} · {e.booked_count}/{e.capacity} booked</div>
                    </div>
                  </div>
                  <div className="flex gap-1 shrink-0">
                    <AttendanceDialog event={e} />
                    <EventDialog event={e} onSaved={loadAll} />
                    <Button onClick={() => delItem(`/admin/events/${e.id}`)} size="icon" variant="ghost" className="text-slate-400 hover:text-red-600" data-testid={`del-event-${e.id}`}><Trash2 className="h-4 w-4" /></Button>
                  </div>
                </div>
              ))}
            </div>
          </TabsContent>

          {/* NEWS */}
          <TabsContent value="news" className="mt-6">
            <div className="flex justify-end mb-4"><NewsDialog onSaved={loadAll} /></div>
            <div className="space-y-3">
              {news.map((n) => (
                <div key={n.id} className="rounded-2xl bg-white border border-slate-200 p-5 flex items-center justify-between gap-4" data-testid={`admin-news-${n.id}`}>
                  <div className="flex items-center gap-4 min-w-0">
                    <img src={n.image_url} alt="" className="h-14 w-14 rounded-lg object-cover shrink-0" />
                    <div className="min-w-0"><h3 className="font-bold text-[#0F2744] truncate">{n.title}</h3><p className="text-sm text-slate-500 truncate">{n.excerpt}</p></div>
                  </div>
                  <div className="flex gap-1 shrink-0">
                    <NewsDialog article={n} onSaved={loadAll} />
                    <Button onClick={() => delItem(`/admin/news/${n.id}`)} size="icon" variant="ghost" className="text-slate-400 hover:text-red-600" data-testid={`del-news-${n.id}`}><Trash2 className="h-4 w-4" /></Button>
                  </div>
                </div>
              ))}
            </div>
          </TabsContent>

          {/* GALLERY */}
          <TabsContent value="gallery" className="mt-6">
            {(() => { const galCats = Array.from(new Set(gallery.map((g) => g.category).filter(Boolean))); return (
            <>
            <div className="flex justify-end mb-4"><GalleryDialog onSaved={loadAll} categories={galCats} /></div>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
              {gallery.map((g) => (
                <div key={g.id} className="relative rounded-xl overflow-hidden group" data-testid={`admin-gallery-${g.id}`}>
                  <img src={g.url} alt={g.caption} className="w-full h-36 object-cover" />
                  <div className="absolute top-2 right-2 flex gap-1.5 opacity-0 group-hover:opacity-100 transition-opacity">
                    <GalleryEditDialog item={g} onSaved={loadAll} categories={galCats} />
                    <button onClick={() => delItem(`/admin/gallery/${g.id}`)} className="h-8 w-8 rounded-full bg-red-600 text-white flex items-center justify-center" data-testid={`del-gallery-${g.id}`}><Trash2 className="h-4 w-4" /></button>
                  </div>
                  <div className="absolute bottom-0 inset-x-0 bg-gradient-to-t from-black/70 p-2">
                    <span className="text-xs text-white block truncate">{g.caption || <span className="italic text-white/60">No caption</span>}</span>
                    <span className="text-[10px] text-amber-300 font-semibold uppercase tracking-wide">{g.category || "General"}</span>
                  </div>
                </div>
              ))}
            </div>
            </>
            ); })()}
          </TabsContent>

          {/* ACTIVITY */}
          <TabsContent value="activity" className="mt-6">
            <ActivityPanel />
          </TabsContent>

          {/* DOCUMENTS */}
          <TabsContent value="documents" className="mt-6">
            <DocumentsPanel documents={documents} onChanged={loadAll} onDelete={(id) => delItem(`/admin/documents/${id}`)} />
          </TabsContent>

          {/* CONTENT */}
          <TabsContent value="content" className="mt-6">
            <ContentPanel />
          </TabsContent>

          {/* BACKGROUNDS */}
          <TabsContent value="backgrounds" className="mt-6">
            <BackgroundsPanel />
          </TabsContent>

          {/* ENQUIRIES */}
          <TabsContent value="enquiries" className="mt-6 space-y-3">
            <div className="flex gap-2 flex-wrap" data-testid="enquiry-filters">
              {["all", "open", "pending", "closed"].map((f) => (
                <button key={f} onClick={() => setEnqFilter(f)} data-testid={`enq-filter-${f}`}
                  className={`px-4 py-1.5 rounded-full text-sm font-semibold capitalize transition-colors ${enqFilter === f ? "bg-[#0F2744] text-white" : "bg-white border border-slate-200 text-slate-500 hover:border-slate-300"}`}>
                  {f}{f !== "all" && ` (${enquiries.filter((q) => (q.status || "open") === f).length})`}
                </button>
              ))}
            </div>
            {(() => {
              const list = enquiries.filter((q) => enqFilter === "all" || (q.status || "open") === enqFilter);
              return list.length === 0 ? <Empty text="No enquiries here." /> : list.map((q) => <EnquiryRow key={q.id} q={q} onChanged={loadAll} />);
            })()}
          </TabsContent>

          {/* COMMUNITY GROUPS */}
          <TabsContent value="community" className="mt-6">
            <ChatGroupsManager />
          </TabsContent>

          {/* PAYMENTS */}
          <TabsContent value="payments" className="mt-6">
            <PaymentsTab payments={payments} />
          </TabsContent>

          {/* SETTINGS */}
          <TabsContent value="settings" className="mt-6">
            <SettingsPanel />
          </TabsContent>

          {/* PEOPLE */}
          <TabsContent value="people" className="mt-6">
            <TeamManager />
          </TabsContent>

          {/* ADMINS (owner only) */}
          <TabsContent value="admins" className="mt-6">
            <AdminsManager />
          </TabsContent>

          {/* REPORTS */}
          <TabsContent value="reports" className="mt-6">
            <ReportsPanel />
          </TabsContent>

          {/* FEEDBACK */}
          <TabsContent value="feedback" className="mt-6 space-y-6">
            <FeedbackPanel />
          </TabsContent>

          {/* PRIVACY */}
          <TabsContent value="privacy" className="mt-6">
            <PrivacyPanel members={members} onChanged={loadAll} />
          </TabsContent>

          {/* GUIDES */}
          <TabsContent value="guides" className="mt-6">
            <GuidesPanel />
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}

const FB_STATUS = ["open", "reviewed", "actioned", "closed"];

function DigestDialog() {
  const [open, setOpen] = useState(false);
  const [data, setData] = useState(null);
  useEffect(() => { if (open && !data) api.get("/admin/incidents/digest").then(({ data: d }) => setData(d)).catch(() => {}); }, [open, data]);
  const copy = () => {
    const text = (data?.items || []).map((i, n) => `${n + 1}. ${i.date ? fmt(i.date) + " — " : ""}${i.location || ""}\n   What happened: ${i.description}\n   Lesson: ${i.lessons_learned}`).join("\n\n");
    navigator.clipboard.writeText(`Safety lessons — RoSPA West Midlands\n\n${text}`);
    toast.success("Digest copied to clipboard");
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild><Button variant="outline" size="sm" data-testid="digest-open"><FileDown className="h-4 w-4 mr-1.5" /> Lessons digest</Button></DialogTrigger>
      <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
        <DialogHeader><DialogTitle>Lessons learned digest</DialogTitle></DialogHeader>
        <p className="text-sm text-slate-500 -mt-1">Reviewed incidents with lessons recorded — a ready-made safety briefing to share at club nights.</p>
        {!data ? <div className="py-8 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-[#0F2744]" /></div>
          : data.count === 0 ? <p className="text-sm text-slate-400 py-4">No lessons recorded yet. Add a 'Lessons learned' note to an incident and it will appear here.</p> : (
            <div className="space-y-3">
              {data.items.map((i, n) => (
                <div key={i.id || n} className="rounded-lg border border-slate-200 p-3" data-testid={`digest-item-${n}`}>
                  <div className="text-xs text-slate-400">{i.date ? fmt(i.date) : "Date not given"}{i.location ? ` · ${i.location}` : ""}</div>
                  <p className="text-sm text-slate-600 mt-1">{i.description}</p>
                  <p className="text-sm text-[#0F2744] mt-1"><span className="font-semibold">Lesson:</span> {i.lessons_learned}</p>
                </div>
              ))}
              <Button onClick={copy} className="bg-[#0F2744] hover:bg-[#0B192C] w-full" data-testid="digest-copy">Copy digest to clipboard</Button>
            </div>
          )}
      </DialogContent>
    </Dialog>
  );
}

function FeedbackPanel() {
  const [suggestions, setSuggestions] = useState([]);
  const [incidents, setIncidents] = useState([]);
  const load = () => {
    api.get("/admin/suggestions").then(({ data }) => setSuggestions(data)).catch(() => {});
    api.get("/admin/incidents").then(({ data }) => setIncidents(data)).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const setSuggestionStatus = async (s, status) => {
    try { await api.patch(`/admin/suggestions/${s.id}`, { status }); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const delSuggestion = async (s) => {
    if (!window.confirm("Delete this suggestion?")) return;
    try { await api.delete(`/admin/suggestions/${s.id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const delIncident = async (i) => {
    if (!window.confirm("Delete this incident report?")) return;
    try { await api.delete(`/admin/incidents/${i.id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div className="space-y-8" data-testid="feedback-panel">
      <div>
        <h2 className="font-bold text-[#0F2744] text-lg flex items-center gap-2"><Lightbulb className="h-5 w-5 text-amber-500" /> Member suggestions ({suggestions.length})</h2>
        {suggestions.length === 0 ? <Empty text="No suggestions yet." /> : (
          <div className="mt-4 space-y-3">
            {suggestions.map((s) => (
              <div key={s.id} className="rounded-2xl bg-white border border-slate-200 p-4" data-testid={`admin-suggestion-${s.id}`}>
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <Badge className="bg-slate-100 text-slate-700">{s.category === "event_idea" ? "Event idea" : "Suggestion"}</Badge>
                      <span className="text-xs text-slate-400">{s.user_name} · {fmt(s.created_at)}</span>
                    </div>
                    <p className="text-sm text-slate-700 mt-2 whitespace-pre-wrap">{s.text}</p>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <Select value={s.status} onValueChange={(v) => setSuggestionStatus(s, v)}>
                      <SelectTrigger className="h-8 w-28 capitalize" data-testid={`suggestion-status-${s.id}`}><SelectValue /></SelectTrigger>
                      <SelectContent>{FB_STATUS.map((x) => <SelectItem key={x} value={x} className="capitalize">{x}</SelectItem>)}</SelectContent>
                    </Select>
                    <Button variant="outline" size="icon" onClick={() => delSuggestion(s)} className="border-red-300 text-red-600 hover:bg-red-50" data-testid={`suggestion-delete-${s.id}`}><Trash2 className="h-4 w-4" /></Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <div>
        <div className="flex items-center justify-between gap-3">
          <h2 className="font-bold text-[#0F2744] text-lg flex items-center gap-2"><AlertTriangle className="h-5 w-5 text-red-500" /> Incident reports ({incidents.length})</h2>
          <DigestDialog />
        </div>
        <p className="text-sm text-slate-500 mt-1">Record lessons learned for the committee — kept internal (not shown to members).</p>
        {incidents.length === 0 ? <Empty text="No incident reports yet." /> : (
          <div className="mt-4 space-y-3">
            {incidents.map((i) => <IncidentCard key={i.id} i={i} onChanged={load} onDelete={() => delIncident(i)} />)}
          </div>
        )}
      </div>
    </div>
  );
}

function IncidentCard({ i, onChanged, onDelete }) {
  const [lessons, setLessons] = useState(i.lessons_learned || "");
  const [status, setStatus] = useState(i.status || "open");
  const [saving, setSaving] = useState(false);
  const [note, setNote] = useState("");
  const [notes, setNotes] = useState(i.notes || []);
  const [addingNote, setAddingNote] = useState(false);
  const save = async () => {
    setSaving(true);
    try { await api.patch(`/admin/incidents/${i.id}`, { lessons_learned: lessons, status }); toast.success("Saved"); onChanged(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };
  const addNote = async () => {
    if (!note.trim()) return;
    setAddingNote(true);
    try { const { data } = await api.post(`/admin/incidents/${i.id}/notes`, { text: note }); setNotes((n) => [...n, data.note]); setNote(""); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setAddingNote(false); }
  };
  return (
    <div className="rounded-2xl bg-white border border-slate-200 p-4" data-testid={`admin-incident-${i.id}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="text-xs text-slate-400">{i.user_name} · reported {fmt(i.created_at)}</div>
          <div className="text-sm font-semibold text-[#0F2744] mt-1">{i.date ? fmt(i.date) : "Date not given"}{i.location ? ` · ${i.location}` : ""}</div>
          <p className="text-sm text-slate-700 mt-1 whitespace-pre-wrap">{i.description}</p>
          {(i.injuries || i.vehicles) && <p className="text-xs text-slate-500 mt-1">{i.injuries ? `Injuries: ${i.injuries}. ` : ""}{i.vehicles ? `Vehicles: ${i.vehicles}.` : ""}</p>}
        </div>
        <Button variant="outline" size="icon" onClick={onDelete} className="border-red-300 text-red-600 hover:bg-red-50 shrink-0" data-testid={`incident-delete-${i.id}`}><Trash2 className="h-4 w-4" /></Button>
      </div>
      <div className="mt-3 pt-3 border-t border-slate-100">
        <Label className="text-xs text-slate-400 mb-1 block">Committee comments</Label>
        <div className="space-y-1.5 mb-2">
          {notes.length === 0 && <p className="text-xs text-slate-400">No comments yet.</p>}
          {notes.map((n, idx) => (
            <div key={n.id || idx} className="text-sm bg-slate-50 rounded-lg px-3 py-1.5" data-testid={`incident-note-${i.id}-${idx}`}>
              <span className="text-xs text-slate-400">{n.by}{n.at ? ` · ${fmt(n.at)}` : ""}</span>
              <div className="text-slate-700">{n.text}</div>
            </div>
          ))}
        </div>
        <div className="flex items-center gap-2">
          <Input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Add a comment…" className="h-8" data-testid={`incident-note-input-${i.id}`} />
          <Button onClick={addNote} disabled={addingNote} size="sm" variant="outline" className="shrink-0" data-testid={`incident-note-add-${i.id}`}>{addingNote ? <Loader2 className="h-4 w-4 animate-spin" /> : "Comment"}</Button>
        </div>
      </div>
      <div className="mt-3 pt-3 border-t border-slate-100">
        <Label className="text-xs text-slate-400 mb-1 block">Lessons learned (internal)</Label>
        <Textarea value={lessons} onChange={(e) => setLessons(e.target.value)} rows={2} placeholder="What can the group learn from this?" data-testid={`incident-lessons-${i.id}`} />
        <div className="flex items-center gap-2 mt-2">
          <Select value={status} onValueChange={setStatus}>
            <SelectTrigger className="h-8 w-32 capitalize" data-testid={`incident-status-${i.id}`}><SelectValue /></SelectTrigger>
            <SelectContent>{["open", "reviewed", "closed"].map((x) => <SelectItem key={x} value={x} className="capitalize">{x}</SelectItem>)}</SelectContent>
          </Select>
          <Button onClick={save} disabled={saving} size="sm" className="bg-[#0F2744] hover:bg-[#0B192C]" data-testid={`incident-save-${i.id}`}>{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save"}</Button>
        </div>
      </div>
    </div>
  );
}

function DataExportDialog({ userId, name }) {
  const [open, setOpen] = useState(false);
  const [data, setData] = useState(null);
  useEffect(() => {
    if (open && !data) api.get(`/admin/members/${userId}/data-export`).then(({ data: d }) => setData(d)).catch(() => toast.error("Couldn't load member data."));
  }, [open, data, userId]);
  const download = () => {
    if (!data) return;
    const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url; a.download = `member-data-${name || userId}.json`; document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
  };
  const p = data?.profile || {};
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild><Button variant="outline" size="sm" data-testid={`data-export-${userId}`}>View data</Button></DialogTrigger>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle>Data held for {name}</DialogTitle></DialogHeader>
        {!data ? <div className="py-8 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-[#0F2744]" /></div> : (
          <div className="space-y-3 text-sm">
            <div>
              <p className="font-semibold text-slate-500 mb-1">Profile</p>
              {[["Name", p.name], ["Email", p.email], ["Phone", p.phone], ["Address", p.address], ["Next of kin", p.nok_name], ["Next of kin phone", p.nok_phone], ["Pathway", p.pathway], ["Grade", p.grade], ["Membership paid until", p.membership_paid_until]].map(([k, v]) => (
                <div key={k} className="flex justify-between gap-2 py-1 border-b border-slate-100"><span className="text-slate-500">{k}</span><span className="text-slate-800 text-right">{v || "—"}</span></div>
              ))}
            </div>
            <div><p className="font-semibold text-slate-500 mb-1">Grade history ({(data.grade_history || []).length})</p>{(data.grade_history || []).map((h, idx) => <div key={idx} className="text-slate-700 capitalize">{h.grade}{h.discipline ? ` (${h.discipline})` : ""} — {fmt(h.date)}</div>)}</div>
            <div><p className="font-semibold text-slate-500 mb-1">Event bookings ({(data.bookings || []).length})</p>{(data.bookings || []).map((b, idx) => <div key={idx} className="text-slate-700">{b.event_title || "Event"} — {fmt(b.event_date)} ({b.status})</div>)}</div>
            <div><p className="font-semibold text-slate-500 mb-1">Payments ({(data.payments || []).length})</p>{(data.payments || []).map((t, idx) => <div key={idx} className="text-slate-700 capitalize">{t.kind} — {gbp(t.amount)} ({t.payment_status}) {fmt(t.created_at)}</div>)}</div>
            <Button onClick={download} className="bg-[#0F2744] hover:bg-[#0B192C] w-full" data-testid={`data-export-download-${userId}`}><Download className="h-4 w-4 mr-1.5" /> Download data (JSON)</Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function PrivacyPanel({ members = [], onChanged }) {
  const [rows, setRows] = useState([]);
  const load = () => api.get("/admin/data-requests").then(({ data }) => setRows(data)).catch(() => {});
  useEffect(() => { load(); }, []);
  const complete = async (r) => {
    try { await api.post(`/admin/data-requests/${r.id}/complete`); toast.success("Marked complete"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const open = rows.filter((r) => r.status === "open");
  const done = rows.filter((r) => r.status !== "open");
  const Row = ({ r }) => (
    <div className="rounded-2xl bg-white border border-slate-200 p-4 flex items-start justify-between gap-3" data-testid={`data-request-${r.id}`}>
      <div className="min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <Badge className={r.kind === "deletion" ? "bg-red-100 text-red-700" : "bg-blue-100 text-blue-700"}>{r.kind === "deletion" ? "Erasure" : "Data access"}</Badge>
          <Badge className={r.status === "open" ? "bg-amber-100 text-amber-700" : "bg-green-100 text-green-700"}>{r.status === "open" ? "Open" : "Completed"}</Badge>
          <span className="text-xs text-slate-400">{fmt(r.created_at)}</span>
        </div>
        <p className="text-sm font-semibold text-[#0F2744] mt-1">{r.user_name} <span className="font-normal text-slate-500">{r.user_email}</span></p>
        {r.note && <p className="text-xs text-slate-500 mt-1">Note: {r.note}</p>}
        {r.kind === "deletion" && r.status === "open" && <p className="text-xs text-red-500 mt-1">To action erasure: open the Members tab and delete this member (anonymises & keeps club history).</p>}
      </div>
      <div className="flex flex-col gap-2 shrink-0">
        <DataExportDialog userId={r.user_id} name={r.user_name} />
        {r.status === "open" && <Button size="sm" onClick={() => complete(r)} className="bg-green-600 hover:bg-green-700 text-white" data-testid={`data-request-complete-${r.id}`}>Mark complete</Button>}
      </div>
    </div>
  );
  return (
    <div className="space-y-6" data-testid="privacy-panel">
      <div>
        <h2 className="font-bold text-[#0F2744] text-lg flex items-center gap-2"><ShieldCheck className="h-5 w-5 text-green-600" /> Open GDPR requests ({open.length})</h2>
        {open.length === 0 ? <Empty text="No open data requests." /> : <div className="mt-4 space-y-3">{open.map((r) => <Row key={r.id} r={r} />)}</div>}
      </div>
      {done.length > 0 && (
        <div>
          <h2 className="font-bold text-[#0F2744] text-lg">Completed ({done.length})</h2>
          <div className="mt-4 space-y-3">{done.map((r) => <Row key={r.id} r={r} />)}</div>
        </div>
      )}
    </div>
  );
}

function ReportsPanel() {
  const today = new Date().toISOString().slice(0, 10);
  const yearAgo = new Date(Date.now() - 365 * 86400000).toISOString().slice(0, 10);
  const [frm, setFrm] = useState(yearAgo);
  const [to, setTo] = useState(today);
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = () => {
    setBusy(true);
    api.get(`/admin/reports/summary?frm=${frm}&to=${to}`).then(({ data: d }) => setData(d)).catch((e) => toast.error(formatApiError(e.response?.data?.detail))).finally(() => setBusy(false));
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);
  const quick = (days) => { const f = new Date(Date.now() - days * 86400000).toISOString().slice(0, 10); setFrm(f); setTo(today); };
  const downloadPdf = async () => {
    try {
      const res = await api.get(`/admin/reports/annual.pdf?frm=${frm}&to=${to}`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a"); a.href = url; a.download = `rospa-wm-report-${frm}-to-${to}.pdf`; document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
    } catch { toast.error("Couldn't generate the report pack."); }
  };
  const Stat = ({ label, value }) => (
    <div className="rounded-xl bg-white border border-slate-200 p-4"><div className="text-xs text-slate-500">{label}</div><div className="text-2xl font-extrabold text-[#0F2744] mt-1">{value}</div></div>
  );
  return (
    <div data-testid="reports-panel">
      <h2 className="font-bold text-[#0F2744] text-lg flex items-center gap-2"><FileDown className="h-5 w-5 text-amber-500" /> Annual report pack</h2>
      <p className="text-sm text-slate-500 mt-1 mb-5">Pick a reporting period and generate the figures for your annual RoSPA return — membership, new joiners, tests passed, events and fees collected.</p>
      <div className="rounded-2xl bg-white border border-slate-200 p-5">
        <div className="flex flex-wrap items-end gap-3">
          <div><Label className="text-xs text-slate-400 mb-1 block">From</Label><Input type="date" value={frm} onChange={(e) => setFrm(e.target.value)} data-testid="report-from" /></div>
          <div><Label className="text-xs text-slate-400 mb-1 block">To</Label><Input type="date" value={to} onChange={(e) => setTo(e.target.value)} data-testid="report-to" /></div>
          <Button onClick={load} disabled={busy} className="bg-[#0F2744] hover:bg-[#0B192C]" data-testid="report-update">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Update"}</Button>
          <Button onClick={downloadPdf} className="bg-amber-500 hover:bg-amber-600 text-[#0F2744] font-bold" data-testid="report-download-pdf"><FileDown className="h-4 w-4 mr-1.5" /> Download PDF pack</Button>
        </div>
        <div className="flex flex-wrap gap-2 mt-3">
          {[["Last 12 months", 365], ["Last 6 months", 182], ["Last 3 months", 91], ["This year", Math.floor((Date.now() - new Date(new Date().getFullYear(), 0, 1).getTime()) / 86400000)]].map(([lbl, d]) => (
            <button key={lbl} type="button" onClick={() => quick(d)} className="px-3 py-1 rounded-full text-xs font-semibold bg-slate-100 text-slate-700 hover:bg-slate-200" data-testid={`report-quick-${d}`}>{lbl}</button>
          ))}
        </div>
      </div>
      {data && (
        <div className="mt-5 space-y-5" data-testid="report-summary">
          <p className="text-sm text-slate-500">Period: <strong>{fmt(data.from)}</strong> to <strong>{fmt(data.to)}</strong></p>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <Stat label="Current members" value={data.total_members} />
            <Stat label="New members (period)" value={data.new_members} />
            <Stat label="Tutors / observers" value={data.tutors} />
            <Stat label="Advanced tests passed" value={data.advanced_tests_passed} />
            <Stat label="New applications" value={data.new_applications} />
            <Stat label="Approved" value={data.approved} />
            <Stat label="Declined" value={data.rejected} />
            <Stat label="Events held" value={data.events_held} />
            <Stat label="Event bookings" value={data.event_bookings} />
            <Stat label="Fees collected" value={data.fees_collected} />
          </div>
          <div className="grid sm:grid-cols-3 gap-4" data-testid="report-sections">
            {[["Motorcycle section", "motorcycle"], ["Car section", "car"]].map(([t, k]) => (
              <div key={k} className="rounded-xl bg-white border border-slate-200 p-4">
                <div className="text-sm font-semibold text-slate-500 mb-2">{t}</div>
                {[["Total members", "total"], ["New in period", "new"], ["Current associates", "associates"], ["Full medallists / members", "medallists"]].map(([lbl, f]) => (
                  <div key={f} className="flex justify-between text-sm py-0.5"><span className="text-slate-600">{lbl}</span><span className="font-semibold text-[#0F2744]">{data.sections?.[k]?.[f] ?? 0}</span></div>
                ))}
              </div>
            ))}
            <div className="rounded-xl bg-white border border-slate-200 p-4">
              <div className="text-sm font-semibold text-slate-500 mb-2">Tests passed (period)</div>
              {["bike", "car"].map((disc) => (
                <div key={disc} className="mb-2">
                  <div className="text-xs uppercase tracking-wide text-slate-400">{disc}</div>
                  {["gold", "silver", "bronze"].map((g) => (
                    <div key={g} className="flex justify-between text-sm py-0.5"><span className="capitalize text-slate-600">{g}</span><span className="font-semibold text-[#0F2744]">{data.tests_by_discipline?.[disc]?.[g] ?? 0}</span></div>
                  ))}
                </div>
              ))}
            </div>
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            <div className="rounded-xl bg-white border border-slate-200 p-4"><div className="text-sm font-semibold text-slate-500 mb-2">Members by grade</div>{Object.entries(data.grade_counts || {}).map(([g, n]) => <div key={g} className="flex justify-between text-sm py-0.5"><span className="capitalize text-slate-600">{g === "none" ? "Not yet graded" : g}</span><span className="font-semibold text-[#0F2744]">{n}</span></div>)}</div>
            <div className="rounded-xl bg-white border border-slate-200 p-4"><div className="text-sm font-semibold text-slate-500 mb-2">Members by pathway</div>{Object.entries(data.pathway_counts || {}).map(([p, n]) => <div key={p} className="flex justify-between text-sm py-0.5"><span className="capitalize text-slate-600">{p}</span><span className="font-semibold text-[#0F2744]">{n}</span></div>)}</div>
          </div>
        </div>
      )}
    </div>
  );
}

function GuidesPanel() {
  const [guides, setGuides] = useState([]);
  useEffect(() => { api.get("/admin/guides").then(({ data }) => setGuides(data)).catch(() => {}); }, []);
  return (
    <div data-testid="guides-panel">
      <h2 className="font-bold text-[#0F2744] text-lg">User guides</h2>
      <p className="text-sm text-slate-500 mt-1 mb-5">Illustrated step-by-step PDF guides for visitors, members and administrators. Share them freely with the committee and members.</p>
      <div className="grid md:grid-cols-3 gap-4">
        {guides.map((g) => <GuideCard key={g.slug} {...g} path={`/admin/guides/${g.slug}`} />)}
      </div>
    </div>
  );
}

function ApplicationDialog({ app, canEmergency }) {
  const { t } = useContent();
  const [open, setOpen] = useState(false);
  const rows = [
    ["Pathway", app.pathway],
    ["Email", app.email], ["Phone", app.phone],
    ["Address", [app.address_line1, app.address_line2, app.town, app.county, app.postcode].filter(Boolean).join(", ") || app.address || "—"],
    ["Preferred days", (app.preferred_days || []).join(", ") || "—"],
    ["DVLA check code", app.dvla_check_code || "—"],
    ["Licence (last 8)", app.licence_number || "—"],
    ["Current grade", app.current_grade || "—"],
    ...(app.pathway === "driver" ? [["Approved Driving Instructor", app.adi || "No"]] : []),
    ["Payment", app.payment_status === "paid" ? `${t("fee.join", "£65")} paid` : app.payment_status === "pending" ? "Awaiting payment" : "—"],
    ["Experience", app.experience || "—"],
    ["Heard about us", app.hear_about || "—"],
    ["Message", app.message || "—"],
    ["Submitted", fmt(app.created_at)],
  ];
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild><Button variant="outline" size="sm" data-testid={`view-app-${app.id}`}>View details</Button></DialogTrigger>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle>{app.name}</DialogTitle></DialogHeader>
        <dl className="space-y-1">
          {rows.map(([k, v]) => (
            <div key={k} className="flex flex-col sm:flex-row sm:justify-between gap-1 py-1.5 border-b border-slate-100">
              <dt className="text-sm font-semibold text-slate-500 shrink-0">{k}</dt>
              <dd className="text-sm text-slate-800 sm:text-right sm:max-w-xs break-words">{v}</dd>
            </div>
          ))}
        </dl>
        {canEmergency && <div className="mt-3"><EmergencyReveal url={`/admin/applications/${app.id}/emergency`} testId={`app-dialog-emergency-${app.id}`} /></div>}
      </DialogContent>
    </Dialog>
  );
}

function EmergencyReveal({ url, testId }) {
  const [data, setData] = useState(null);
  const [reason, setReason] = useState("");
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const load = async () => {
    if (!reason.trim()) { toast.error("Please enter a reason for viewing these details."); return; }
    setBusy(true);
    try { const { data: d } = await api.get(`${url}?reason=${encodeURIComponent(reason.trim())}`); setData(d); setOpen(false); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  if (data) {
    return (
      <div className="rounded-lg bg-red-50 border border-red-200 p-3" data-testid={testId}>
        <div className="text-xs font-bold text-red-700 uppercase tracking-wide flex items-center gap-1.5 mb-1"><ShieldAlert className="h-3.5 w-3.5" /> Emergency contact</div>
        <div className="text-sm text-slate-800">{data.nok_name || "—"}</div>
        <div className="text-sm text-slate-600">{data.nok_phone ? <a href={`tel:${data.nok_phone}`} className="text-blue-600">{data.nok_phone}</a> : "—"}</div>
        {data.address && <div className="text-sm text-slate-600 mt-1">{data.address}</div>}
      </div>
    );
  }
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" size="sm" className="border-red-300 text-red-700 hover:bg-red-50" data-testid={`${testId}-btn`}><ShieldAlert className="h-4 w-4 mr-1.5" /> Show emergency contact</Button>
      </DialogTrigger>
      <DialogContent className="max-w-sm">
        <DialogHeader><DialogTitle>Reason for access</DialogTitle></DialogHeader>
        <p className="text-sm text-slate-500">These details are protected. Please record why you need to view them — your name and reason are logged.</p>
        <Textarea value={reason} onChange={(e) => setReason(e.target.value)} rows={2} placeholder="e.g. medical emergency during a ride-out" data-testid={`${testId}-reason`} />
        <DialogFooter><Button onClick={load} disabled={busy} className="bg-red-600 hover:bg-red-700 text-white" data-testid={`${testId}-confirm`}>{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Reveal details"}</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ApplicationStages({ a, onChanged, canEmergency, roleNames = {} }) {
  const { t } = useContent();
  const [busy, setBusy] = useState("");
  const [note, setNote] = useState("");
  const [savingNote, setSavingNote] = useState(false);
  const [payForm, setPayForm] = useState(false);
  const [method, setMethod] = useState("Bank transfer");
  const [reference, setReference] = useState("");
  const [sendingLink, setSendingLink] = useState(false);
  const sendLink = async () => {
    setSendingLink(true);
    try { await api.post(`/admin/applications/${a.id}/send-payment-link`); toast.success("Joining-fee payment link emailed to the applicant"); onChanged(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSendingLink(false); }
  };
  const driver = a.driver_check && a.driver_check.done;
  const payment = a.payment_check && a.payment_check.done;
  const approved = !!a.converted_user_id;
  const steps = [
    { key: "driver_check", label: "Driver check", role: "Membership Secretary", who: roleNames.secretary, done: driver, stamp: a.driver_check },
    { key: "payment_check", label: "Payment check", role: "Treasurer", who: roleNames.treasurer, done: payment, stamp: a.payment_check },
    { key: "approved", label: "Membership approved", role: "Committee", who: "", done: approved, stamp: approved ? { by: a.approved_by, at: a.approved_at } : null },
  ];
  const doneCount = steps.filter((s) => s.done).length;
  const pct = Math.round((doneCount / steps.length) * 100);
  const treasurerLabel = roleNames.treasurer ? `Treasurer (${roleNames.treasurer})` : "treasurer";
  const withWho = approved ? "Member created" : !driver ? `With ${roleNames.secretary ? `${roleNames.secretary} — ` : ""}driver check` : !payment ? `With ${treasurerLabel} — payment check` : "Ready to approve";
  const toggle = async (key, done, extra = {}) => {
    setBusy(key);
    try { await api.post(`/admin/applications/${a.id}/stage`, { stage: key, done, ...extra }); onChanged(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(""); setPayForm(false); }
  };
  const addNote = async () => {
    if (!note.trim()) return;
    setSavingNote(true);
    try { await api.post(`/admin/applications/${a.id}/notes`, { text: note }); setNote(""); toast.success("Note added"); onChanged(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSavingNote(false); }
  };
  const timeline = [
    { label: "Application received", by: "", at: a.created_at },
    ...(driver ? [{ label: "Driver check completed", by: a.driver_check.by, at: a.driver_check.at }] : []),
    ...(payment ? [{ label: `Payment check completed${a.payment_check.method ? ` — ${a.payment_check.method}` : ""}${a.payment_check.reference ? ` (ref ${a.payment_check.reference})` : ""}`, by: a.payment_check.by, at: a.payment_check.at }] : []),
    ...((a.app_notes || []).map((n) => ({ label: `Note: ${n.text}`, by: n.by, at: n.at }))),
    ...(approved ? [{ label: "Membership approved", by: a.approved_by, at: a.approved_at }] : []),
  ].filter((t) => t.at).sort((x, y) => (x.at < y.at ? -1 : 1));

  return (
    <div className="mt-4 pt-4 border-t border-slate-100" data-testid={`app-stages-${a.id}`}>
      <div className="flex items-center justify-between gap-3 mb-2">
        <span className="text-xs font-semibold text-slate-500">Application progress</span>
        <Badge className="bg-[#0F2744] text-white hover:bg-[#0F2744]" data-testid={`app-withwho-${a.id}`}>{withWho}</Badge>
      </div>
      <div className="h-2 rounded-full bg-slate-100 overflow-hidden"><div className="h-full bg-green-500 transition-all" style={{ width: `${pct}%` }} /></div>
      <div className="grid sm:grid-cols-3 gap-2 mt-3">
        {steps.map((s) => (
          <div key={s.key} className={`rounded-xl border p-3 ${s.done ? "border-green-200 bg-green-50" : "border-slate-200"}`} data-testid={`app-step-${a.id}-${s.key}`}>
            <div className="flex items-center gap-2">
              <span className={`h-5 w-5 rounded-full flex items-center justify-center text-white ${s.done ? "bg-green-500" : "bg-slate-300"}`}>{s.done && <Check className="h-3 w-3" />}</span>
              <span className="text-sm font-semibold text-[#0F2744]">{s.label}</span>
            </div>
            <div className="mt-0.5 ml-7 text-[11px] text-slate-400">{s.role}{s.who ? ` (${s.who})` : ""}</div>
            {s.done && s.stamp ? (
              <div className="mt-1 text-xs text-slate-500">
                {s.stamp.by || "—"}{s.stamp.at ? ` · ${fmt(s.stamp.at)}` : ""}
                {s.key === "payment_check" && (s.stamp.method || s.stamp.reference) && (
                  <div className="text-slate-400">{s.stamp.method}{s.stamp.reference ? ` · ref ${s.stamp.reference}` : ""}</div>
                )}
              </div>
            ) : s.key === "payment_check" ? (
              payForm ? (
                <div className="mt-2 space-y-2" data-testid={`app-pay-form-${a.id}`}>
                  <Select value={method} onValueChange={setMethod}>
                    <SelectTrigger className="h-8 text-xs" data-testid={`app-pay-method-${a.id}`}><SelectValue /></SelectTrigger>
                    <SelectContent>{["Bank transfer", "Cash", "Cheque", "Card", "Other"].map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
                  </Select>
                  <Input value={reference} onChange={(e) => setReference(e.target.value)} placeholder="Reference (optional)" className="h-8 text-xs" data-testid={`app-pay-ref-${a.id}`} />
                  <div className="flex gap-2">
                    <Button onClick={() => toggle("payment_check", true, { method, reference })} disabled={busy === "payment_check"} className="h-7 text-xs bg-green-600 hover:bg-green-700 text-white" data-testid={`app-pay-confirm-${a.id}`}>Confirm</Button>
                    <button onClick={() => setPayForm(false)} className="text-xs text-slate-400 hover:underline">Cancel</button>
                  </div>
                </div>
              ) : (
                <div className="mt-2 space-y-1.5">
                  <button onClick={sendLink} disabled={sendingLink} className="block text-xs font-semibold text-blue-700 hover:underline disabled:opacity-50" data-testid={`app-send-paylink-${a.id}`}>
                    {sendingLink ? "Sending…" : (a.payment_link_sent_at ? `Resend ${t("fee.join", "£65")} payment link` : `Send ${t("fee.join", "£65")} payment link`)}
                  </button>
                  {a.payment_link_sent_at && <div className="text-[11px] text-slate-400" data-testid={`app-paylink-sent-${a.id}`}>Link emailed {fmt(a.payment_link_sent_at)}</div>}
                  <button onClick={() => setPayForm(true)} className="block text-xs font-semibold text-green-700 hover:underline" data-testid={`app-mark-${a.id}-payment_check`}>Record payment manually</button>
                </div>
              )
            ) : s.key !== "approved" ? (
              <button onClick={() => toggle(s.key, true)} disabled={busy === s.key} className="mt-2 text-xs font-semibold text-green-700 hover:underline disabled:opacity-50" data-testid={`app-mark-${a.id}-${s.key}`}>
                {busy === s.key ? "Saving…" : "Mark complete"}
              </button>
            ) : <div className="mt-1 text-xs text-slate-400">Use the Approve button</div>}
            {s.done && s.key !== "approved" && (
              <button onClick={() => toggle(s.key, false)} disabled={busy === s.key} className="mt-1 ml-3 text-[11px] text-slate-400 hover:underline" data-testid={`app-undo-${a.id}-${s.key}`}>Undo</button>
            )}
          </div>
        ))}
      </div>

      {timeline.length > 0 && (
        <div className="mt-4">
          <div className="text-xs font-semibold text-slate-500 mb-2 flex items-center gap-1.5"><History className="h-3.5 w-3.5" /> Timeline</div>
          <div className="space-y-1.5">
            {timeline.map((t, i) => (
              <div key={i} className="flex items-start gap-2 text-xs" data-testid={`app-timeline-${a.id}-${i}`}>
                <span className="mt-1 h-1.5 w-1.5 rounded-full bg-amber-500 shrink-0" />
                <span className="text-slate-600">{t.label}{t.by ? ` — ${t.by}` : ""}</span>
                <span className="text-slate-400 ml-auto shrink-0">{fmt(t.at)}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="mt-3 flex gap-2">
        <Input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Add a note on this application…" data-testid={`app-note-input-${a.id}`} />
        <Button onClick={addNote} disabled={savingNote || !note.trim()} className="bg-[#0F2744] hover:bg-[#0B192C]" data-testid={`app-note-add-${a.id}`}>Add note</Button>
      </div>

      {canEmergency && <div className="mt-3"><EmergencyReveal url={`/admin/applications/${a.id}/emergency`} testId={`app-emergency-${a.id}`} /></div>}
    </div>
  );
}


function EnquiryRow({ q, onChanged }) {
  const [status, setStatus] = useState(q.status || "open");
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);
  const [assignee, setAssignee] = useState(q.assignee || "");
  const [reply, setReply] = useState("");
  const [sendingReply, setSendingReply] = useState(false);
  const [templates, setTemplates] = useState([]);
  useEffect(() => { api.get("/admin/reply-templates").then(({ data }) => setTemplates(data.templates || [])).catch(() => {}); }, []);
  const notes = q.notes || [];
  const saveAssignee = async () => {
    try { await api.patch(`/admin/enquiries/${q.id}`, { assignee }); toast.success("Assignee updated"); onChanged(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const changeStatus = async (v) => {
    setStatus(v);
    try { await api.patch(`/admin/enquiries/${q.id}`, { status: v }); toast.success("Status updated"); onChanged(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const addNote = async () => {
    if (!note.trim()) return;
    setSaving(true);
    try { await api.patch(`/admin/enquiries/${q.id}`, { note }); setNote(""); toast.success("Note added"); onChanged(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };
  const sendReply = async () => {
    if (!reply.trim()) return;
    setSendingReply(true);
    try {
      const { data } = await api.post(`/admin/enquiries/${q.id}/reply`, { message: reply });
      toast.success(data.emailed ? `Reply emailed to ${q.email}` : "Reply logged (email not sent in preview)");
      setReply(""); onChanged();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSendingReply(false); }
  };
  return (
    <div className="rounded-2xl bg-white border border-slate-200 p-5" data-testid={`admin-enquiry-${q.id}`}>
      <div className="flex items-center gap-2 flex-wrap">
        <h3 className="font-bold text-[#0F2744]">{q.subject}</h3>
        <Badge className={`${ENQ_BADGE[status] || ENQ_BADGE.open} capitalize`}>{status}</Badge>
        <span className="text-sm text-slate-400 ml-auto">{fmt(q.created_at)}</span>
      </div>
      <div className="text-sm text-slate-500 mt-1">{q.name}{q.email && <> · <a href={`mailto:${q.email}`} className="text-blue-600">{q.email}</a></>}{q.phone && ` · ${q.phone}`}</div>
      <p className="mt-2 text-slate-700">{q.message}</p>
      <div className="mt-4 flex flex-wrap items-end gap-3">
        <div className="w-40"><MiniSelect label="Status" value={status} onChange={changeStatus} options={["open", "pending", "closed"]} testid={`enquiry-status-${q.id}`} /></div>
        <div className="w-52">
          <Label className="text-xs text-slate-400">Assigned to</Label>
          <Input value={assignee} onChange={(e) => setAssignee(e.target.value)} onBlur={saveAssignee} placeholder="Committee member" className="h-9" data-testid={`enquiry-assignee-${q.id}`} />
        </div>
      </div>
      {notes.length > 0 && (
        <div className="mt-4 space-y-2">
          {notes.map((n, i) => (
            <div key={i} className={`rounded-lg border p-3 text-sm ${n.kind === "reply" ? "bg-blue-50 border-blue-100" : "bg-slate-50 border-slate-100"}`} data-testid={`enquiry-note-${q.id}-${i}`}>
              <div className="text-slate-700 whitespace-pre-line">{n.kind === "reply" && <span className="inline-flex items-center gap-1 text-xs font-bold text-blue-700 mr-1"><MessageSquare className="h-3 w-3" /> Reply</span>}{n.text}</div>
              <div className="text-xs text-slate-400 mt-1">{n.by} · {fmt(n.at)}</div>
            </div>
          ))}
        </div>
      )}
      {q.email && (
        <div className="mt-4 rounded-xl border border-blue-100 bg-blue-50/50 p-3">
          <Label className="text-xs font-semibold text-blue-700 flex items-center gap-1.5 mb-1.5"><MessageSquare className="h-3.5 w-3.5" /> Reply by email (sent to {q.email})</Label>
          {templates.length > 0 && (
            <select onChange={(e) => { const t = templates.find((x) => (x.id || x.title) === e.target.value); if (t) setReply((r) => r ? r + "\n\n" + t.body : t.body); e.target.value = ""; }} className="mb-2 h-9 w-full rounded-md border border-slate-200 text-sm px-2 bg-white text-slate-600" data-testid={`enquiry-template-${q.id}`}>
              <option value="">Insert a saved reply…</option>
              {templates.map((t, i) => <option key={i} value={t.id || t.title}>{t.title || "Untitled"}</option>)}
            </select>
          )}
          <Textarea rows={3} value={reply} onChange={(e) => setReply(e.target.value)} placeholder="Type your reply…" data-testid={`enquiry-reply-input-${q.id}`} />
          <div className="mt-2 flex justify-end">
            <Button onClick={sendReply} disabled={sendingReply || !reply.trim()} className="bg-blue-600 hover:bg-blue-700 text-white" data-testid={`enquiry-reply-send-${q.id}`}>
              {sendingReply ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Send className="h-4 w-4 mr-1.5" /> Send reply</>}
            </Button>
          </div>
        </div>
      )}
      <div className="mt-3 flex gap-2">
        <Input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Add a note (what's happened so far)…" data-testid={`enquiry-note-input-${q.id}`} />
        <Button onClick={addNote} disabled={saving || !note.trim()} className="bg-[#0F2744] hover:bg-[#0B192C]" data-testid={`enquiry-note-add-${q.id}`}>Add</Button>
      </div>
    </div>
  );
}

function Stat({ icon: Icon, label, value, sub, onClick, badge }) {
  return (
    <button type="button" onClick={onClick} className="relative text-left w-full rounded-2xl bg-white border border-slate-200 p-4 hover:border-[#0F2744] hover:shadow-sm transition-all" data-testid={`stat-${label.toLowerCase().replace(/\s/g, "-")}`}>
      {badge > 0 && (
        <span className="absolute top-3 right-3 min-w-[22px] h-[22px] px-1.5 rounded-full bg-red-500 text-white text-[11px] font-bold flex items-center justify-center" data-testid={`stat-badge-${label.toLowerCase().replace(/\s/g, "-")}`}>{badge}</span>
      )}
      <Icon className="h-5 w-5 text-amber-500 mb-2" />
      <div className="text-2xl font-extrabold text-[#0F2744]">{value}</div>
      <div className="text-xs text-slate-500">{label}{sub && ` · ${sub}`}</div>
    </button>
  );
}

function Empty({ text }) {
  return <div className="rounded-2xl bg-white border border-slate-200 p-10 text-center text-slate-500">{text}</div>;
}

function RenewalsDue({ members, onChanged }) {
  const { t } = useContent();
  const [busy, setBusy] = useState("");
  const [busyAll, setBusyAll] = useState(false);
  const due = (members || [])
    .filter((m) => ["due_soon", "expired"].includes(m.membership_expiry_status))
    .sort((a, b) => (a.membership_days_left ?? 9999) - (b.membership_days_left ?? 9999));
  if (due.length === 0) return null;
  const send = async (m) => {
    setBusy(m.id);
    try { await api.post(`/admin/members/${m.id}/send-renewal`); toast.success(`Renewal request sent to ${m.email}`); onChanged(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(""); }
  };
  const sendAll = async () => {
    if (!window.confirm(`Send a ${t("fee.renewal", "£20")} renewal request to all ${due.length} members due?`)) return;
    setBusyAll(true);
    try { const { data } = await api.post(`/admin/members/send-renewal-bulk`); toast.success(`Renewal request sent to ${data.sent} member${data.sent === 1 ? "" : "s"}`); onChanged(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusyAll(false); }
  };
  return (
    <div className="rounded-2xl border border-amber-200 bg-amber-50 p-5" data-testid="renewals-due">
      <div className="flex items-center justify-between gap-3 mb-3">
        <div className="flex items-center gap-2">
          <Calendar className="h-5 w-5 text-amber-600" />
          <h3 className="font-bold text-[#0F2744]">Renewals due <span className="text-amber-700">({due.length})</span></h3>
        </div>
        <Button size="sm" onClick={sendAll} disabled={busyAll} className="bg-[#0F2744] hover:bg-[#0B192C]" data-testid="renewal-send-all">
          {busyAll ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Send className="h-3.5 w-3.5 mr-1.5" /> Send to all due</>}
        </Button>
      </div>
      <p className="text-sm text-slate-500 mb-3">Members whose membership has lapsed or is due within 30 days. Chase them individually or all at once.</p>
      <div className="space-y-2">
        {due.map((m) => (
          <div key={m.id} className="flex items-center justify-between gap-3 rounded-xl bg-white border border-amber-100 p-3" data-testid={`renewal-due-${m.id}`}>
            <div className="min-w-0">
              <div className="font-semibold text-[#0F2744] truncate">{m.name}</div>
              <div className="text-xs text-slate-500 truncate">{m.email}</div>
            </div>
            <div className="flex items-center gap-3 shrink-0">
              <Badge className={`${MEMBER_BADGE[m.membership_expiry_status]} text-[10px]`}>
                {m.membership_expiry_status === "expired" ? `Lapsed ${fmt(m.membership_paid_until)}` : `${m.membership_days_left}d left`}
              </Badge>
              <Button size="sm" onClick={() => send(m)} disabled={busy === m.id} className="bg-amber-500 hover:bg-amber-400 text-[#0F2744]" data-testid={`renewal-send-${m.id}`}>
                {busy === m.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Send className="h-3.5 w-3.5 mr-1.5" /> Send request</>}
              </Button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function TutorCapacity({ tutor, onChanged }) {
  const [editing, setEditing] = useState(false);
  const [val, setVal] = useState(tutor.tutor_capacity ?? 6);
  const [saving, setSaving] = useState(false);
  const save = async () => {
    setSaving(true);
    try { await api.patch(`/admin/members/${tutor.id}`, { tutor_capacity: Number(val) || 0 }); toast.success("Capacity updated"); setEditing(false); onChanged(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };
  if (!editing) return (
    <button type="button" onClick={() => setEditing(true)} className="text-xs text-blue-600 hover:underline" data-testid={`tutor-capacity-edit-${tutor.id}`}>Set limit</button>
  );
  return (
    <div className="flex items-center gap-1.5">
      <Input type="number" min="0" value={val} onChange={(e) => setVal(e.target.value)} className="h-7 w-16 text-sm" data-testid={`tutor-capacity-input-${tutor.id}`} />
      <Button size="sm" onClick={save} disabled={saving} className="h-7 bg-[#0F2744] hover:bg-[#0B192C]" data-testid={`tutor-capacity-save-${tutor.id}`}>{saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : "Save"}</Button>
    </div>
  );
}

function PastAppDialog({ r, kind }) {
  const [open, setOpen] = useState(false);
  const dc = r.driver_check || {};
  const pc = r.payment_check || {};
  const notes = r.app_notes || [];
  const info = [
    ["Pathway", r.pathway || "—"], ["Grade at application", r.current_grade || "—"],
    ["Experience", r.experience || "—"], ["Preferred days", (r.preferred_days || []).join(", ") || "—"],
    ["Heard about us", r.hear_about || "—"],
    ...(r.pathway === "driver" ? [["Approved driving instructor", r.adi || "No"]] : []),
    ["Applied", fmt(r.created_at)],
    ...(kind === "approved"
      ? [["Approved by", r.approved_by || "—"], ["Approved on", fmt(r.approved_at)]]
      : [["Declined by", r.rejected_by || "—"], ["Declined on", fmt(r.rejected_at)], ["Reason", r.rejected_reason || "—"]]),
  ];
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild><Button variant="outline" size="sm" data-testid={`past-app-view-${r.id}`}>View</Button></DialogTrigger>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle>{r.name}</DialogTitle></DialogHeader>
        <p className="text-xs text-slate-400 -mt-1">Personal details (address, phone and emergency contact) are hidden on completed applications.</p>
        <dl className="space-y-1 mt-2">
          {info.map(([k, v]) => (
            <div key={k} className="flex justify-between gap-2 py-1.5 border-b border-slate-100">
              <dt className="text-sm font-semibold text-slate-500">{k}</dt>
              <dd className="text-sm text-slate-800 text-right capitalize">{v}</dd>
            </div>
          ))}
        </dl>
        <div className="mt-3">
          <p className="text-sm font-semibold text-slate-500 mb-1">Checks completed</p>
          <div className="text-sm text-slate-700" data-testid={`past-app-driver-${r.id}`}>Driver check: {dc.done ? `completed by ${dc.by || "—"}${dc.at ? ` on ${fmt(dc.at)}` : ""}` : "Not completed"}</div>
          <div className="text-sm text-slate-700" data-testid={`past-app-payment-${r.id}`}>Payment check: {pc.done ? `completed by ${pc.by || "—"}${pc.at ? ` on ${fmt(pc.at)}` : ""}${pc.method ? ` · ${pc.method}` : ""}${pc.reference ? ` · ${pc.reference}` : ""}` : "Not completed"}</div>
        </div>
        {notes.length > 0 && (
          <div className="mt-3">
            <p className="text-sm font-semibold text-slate-500 mb-1">Notes</p>
            {notes.map((n, i) => (
              <div key={n.id || i} className="text-sm text-slate-700 border-b border-slate-100 py-1.5">
                <span className="text-slate-400 text-xs">{n.by || "—"}{n.at ? ` · ${fmt(n.at)}` : ""}</span>
                <div>{n.text}</div>
              </div>
            ))}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function ApprovedHistory() {
  const [rows, setRows] = useState(null);
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  useEffect(() => { api.get("/admin/applications/approved").then(({ data }) => setRows(data)).catch(() => setRows([])); }, []);
  if (!rows || rows.length === 0) return null;
  const filtered = rows.filter((r) => !q || `${r.name || ""} ${r.email || ""}`.toLowerCase().includes(q.toLowerCase()));
  return (
    <div className="rounded-2xl bg-white border border-slate-200 p-5" data-testid="approved-history">
      <button type="button" onClick={() => setOpen(!open)} className="w-full flex items-center justify-between" data-testid="approved-history-toggle">
        <h3 className="font-bold text-[#0F2744] flex items-center gap-2"><ClipboardList className="h-5 w-5 text-green-600" /> Approved applicants <span className="text-green-700">({rows.length})</span></h3>
        <span className="text-sm text-slate-400">{open ? "Hide" : "Show"}</span>
      </button>
      {open && (
        <>
          <div className="mt-4 relative"><Search className="h-4 w-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" /><Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search by name or email…" className="pl-9" data-testid="approved-search" /></div>
          <div className="mt-3 overflow-x-auto">
            <table className="w-full text-sm">
              <thead><tr className="text-left text-slate-400 border-b border-slate-100">
                <th className="py-2 pr-4 font-medium">Name</th><th className="py-2 pr-4 font-medium">Email</th>
                <th className="py-2 pr-4 font-medium">Pathway</th><th className="py-2 pr-4 font-medium">Approved by</th>
                <th className="py-2 pr-4 font-medium">Approved on</th><th className="py-2 font-medium"></th></tr></thead>
              <tbody>
                {filtered.map((r) => (
                  <tr key={r.id} className="border-b border-slate-50" data-testid={`approved-row-${r.id}`}>
                    <td className="py-2 pr-4 font-semibold text-[#0F2744]">{r.name}</td>
                    <td className="py-2 pr-4 text-slate-500">{r.email}</td>
                    <td className="py-2 pr-4 capitalize">{r.pathway}</td>
                    <td className="py-2 pr-4 text-slate-500">{r.approved_by || "—"}</td>
                    <td className="py-2 pr-4 text-slate-500">{fmt(r.approved_at)}</td>
                    <td className="py-2"><PastAppDialog r={r} kind="approved" /></td>
                  </tr>
                ))}
              </tbody>
            </table>
            {filtered.length === 0 && <p className="text-sm text-slate-400 py-3">No matches for “{q}”.</p>}
          </div>
        </>
      )}
    </div>
  );
}

const gbp = (pence) => `£${((pence || 0) / 100).toFixed(2)}`;

function DeclinedHistory() {
  const [rows, setRows] = useState(null);
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  useEffect(() => { api.get("/admin/applications/rejected").then(({ data }) => setRows(data)).catch(() => setRows([])); }, []);
  if (!rows || rows.length === 0) return null;
  const filtered = rows.filter((r) => !q || `${r.name || ""} ${r.email || ""}`.toLowerCase().includes(q.toLowerCase()));
  return (
    <div className="rounded-2xl bg-white border border-slate-200 p-5" data-testid="declined-history">
      <button type="button" onClick={() => setOpen(!open)} className="w-full flex items-center justify-between" data-testid="declined-history-toggle">
        <h3 className="font-bold text-[#0F2744] flex items-center gap-2"><X className="h-5 w-5 text-red-500" /> Declined applicants <span className="text-red-600">({rows.length})</span></h3>
        <span className="text-sm text-slate-400">{open ? "Hide" : "Show"}</span>
      </button>
      {open && (
        <>
          <div className="mt-4 relative"><Search className="h-4 w-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" /><Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search by name or email…" className="pl-9" data-testid="declined-search" /></div>
          <div className="mt-3 overflow-x-auto">
            <table className="w-full text-sm">
              <thead><tr className="text-left text-slate-400 border-b border-slate-100">
                <th className="py-2 pr-4 font-medium">Name</th><th className="py-2 pr-4 font-medium">Email</th>
                <th className="py-2 pr-4 font-medium">Pathway</th><th className="py-2 pr-4 font-medium">Declined by</th>
                <th className="py-2 pr-4 font-medium">Declined on</th><th className="py-2 pr-4 font-medium">Reason</th><th className="py-2 font-medium"></th></tr></thead>
              <tbody>
                {filtered.map((r) => (
                  <tr key={r.id} className="border-b border-slate-50" data-testid={`declined-row-${r.id}`}>
                    <td className="py-2 pr-4 font-semibold text-[#0F2744]">{r.name}</td>
                    <td className="py-2 pr-4 text-slate-500">{r.email}</td>
                    <td className="py-2 pr-4 capitalize">{r.pathway}</td>
                    <td className="py-2 pr-4 text-slate-500">{r.rejected_by || "—"}</td>
                    <td className="py-2 pr-4 text-slate-500">{fmt(r.rejected_at)}</td>
                    <td className="py-2 pr-4 text-slate-500">{r.rejected_reason || "—"}</td>
                    <td className="py-2"><PastAppDialog r={r} kind="declined" /></td>
                  </tr>
                ))}
              </tbody>
            </table>
            {filtered.length === 0 && <p className="text-sm text-slate-400 py-3">No matches for “{q}”.</p>}
          </div>
        </>
      )}
    </div>
  );
}

const DECLINE_PRESETS = ["Outside our area", "Incomplete details", "Not yet licensed", "No tutor capacity currently", "Duplicate application"];

function RejectDialog({ application, onReject }) {
  const [open, setOpen] = useState(false);
  const [selected, setSelected] = useState([]);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const toggle = (p) => setSelected((s) => (s.includes(p) ? s.filter((x) => x !== p) : [...s, p]));
  const submit = async () => {
    setBusy(true);
    try { await onReject({ presets: selected, note: note.trim() }); setOpen(false); setSelected([]); setNote(""); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" className="border-red-300 text-red-600 hover:bg-red-50" data-testid={`reject-app-${application.id}`}>
          <X className="h-4 w-4 mr-1.5" /> Reject
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader><DialogTitle>Decline {application.name}'s application</DialogTitle></DialogHeader>
        <p className="text-sm text-slate-500 -mt-1">They'll get a polite decline email. Any <strong>quick reasons</strong> you pick are included in that email; the free-text note stays private and is <strong>not</strong> emailed.</p>
        <div>
          <Label className="text-xs text-slate-400 mb-2 block">Quick reasons (included in the email)</Label>
          <div className="flex flex-wrap gap-2">
            {DECLINE_PRESETS.map((p) => (
              <button key={p} type="button" onClick={() => toggle(p)} data-testid={`decline-preset-${p.toLowerCase().replace(/\s/g, "-")}`}
                className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition-colors ${selected.includes(p) ? "bg-[#0F2744] text-white border-[#0F2744]" : "bg-white text-slate-600 border-slate-200 hover:border-slate-300"}`}>
                {p}
              </button>
            ))}
          </div>
        </div>
        <div>
          <Label className="text-xs text-slate-400 mb-1.5 block">Note (private — not emailed)</Label>
          <Textarea rows={3} value={note} onChange={(e) => setNote(e.target.value)} placeholder="Add any extra detail…" data-testid="decline-note" />
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
          <Button onClick={submit} disabled={busy} className="bg-red-600 hover:bg-red-700 text-white" data-testid={`decline-submit-${application.id}`}>
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Decline & notify"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function CapacityDashboard({ members }) {
  const tutors = members.filter((x) => x.is_tutor);
  if (tutors.length === 0) return null;
  return (
    <div className="rounded-2xl bg-white border border-slate-200 p-5" data-testid="capacity-dashboard">
      <h3 className="font-bold text-[#0F2744] mb-3">Tutor capacity overview</h3>
      <div className="grid sm:grid-cols-2 gap-x-6 gap-y-3">
        {tutors.map((tu) => {
          const count = members.filter((x) => x.tutor_assigned === tu.name).length;
          const cap = tu.tutor_capacity ?? 6;
          const pct = Math.min(100, Math.round((count / Math.max(cap, 1)) * 100));
          const over = count > cap, near = !over && count >= cap;
          const bar = over ? "bg-red-500" : near ? "bg-amber-500" : "bg-green-500";
          return (
            <div key={tu.id} data-testid={`capacity-row-${tu.id}`}>
              <div className="flex justify-between text-sm mb-1">
                <span className="font-medium text-[#0F2744] truncate">{tu.name}</span>
                <span className={`font-semibold ${over ? "text-red-600" : near ? "text-amber-600" : "text-slate-500"}`}>{count}/{cap}{over ? " · over" : ""}</span>
              </div>
              <div className="h-2 rounded-full bg-slate-100 overflow-hidden"><div className={`h-full ${bar}`} style={{ width: `${pct}%` }} /></div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

const TIER_LABEL = { head: "Head of Training", senior: "Advanced Tutor", assessor: "Advanced Tutor", tutor: "Advanced Tutor" };

const ADMIN_FUNCTIONS = ["applications", "members", "tutors", "events", "news", "gallery", "documents", "content", "backgrounds", "enquiries", "community", "payments", "settings", "people", "activity", "emergency", "pii"];

function AdminsManager() {
  const [data, setData] = useState(null);
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", permissions: [] });
  const [templates, setTemplates] = useState([]);
  const [tplName, setTplName] = useState("");
  const load = () => api.get("/admin/admins").then(({ data }) => setData(data)).catch(() => setData({ admins: [], functions: ADMIN_FUNCTIONS }));
  const loadTemplates = () => api.get("/admin/role-templates").then(({ data }) => setTemplates(data)).catch(() => {});
  useEffect(() => { load(); loadTemplates(); }, []);
  if (!data) return <div className="flex justify-center py-10"><Loader2 className="h-6 w-6 animate-spin text-[#0F2744]" /></div>;
  const toggle = (perms, k) => (perms.includes(k) ? perms.filter((x) => x !== k) : [...perms, k]);
  const label = (k) => k === "pii" ? "View contact details (address/phone)" : (k.charAt(0).toUpperCase() + k.slice(1)).replace(/_/g, " ");
  const saveTemplate = async () => {
    if (!tplName.trim()) { toast.error("Give the template a name"); return; }
    if (!form.permissions.length) { toast.error("Tick at least one function first"); return; }
    try { const { data: r } = await api.post("/admin/role-templates", { name: tplName, permissions: form.permissions }); toast.success(r.updated ? "Template updated" : "Template saved"); setTplName(""); loadTemplates(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const deleteTemplate = async (t) => {
    if (!window.confirm(`Delete template "${t.name}"?`)) return;
    try { await api.delete(`/admin/role-templates/${t.id}`); toast.success("Template deleted"); loadTemplates(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const applyTemplate = async (a, t) => {
    try { await api.patch(`/admin/admins/${a.id}`, { permissions: t.permissions }); toast.success(`Applied "${t.name}" to ${a.name || a.email}`); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const create = async () => {
    if (!form.email.trim() || !form.name.trim()) { toast.error("Name and email are required"); return; }
    try { await api.post("/admin/admins", form); toast.success("Admin invited — set-password email sent"); setForm({ name: "", email: "", permissions: [] }); setAdding(false); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const savePerms = async (a, permissions) => {
    try { await api.patch(`/admin/admins/${a.id}`, { permissions }); toast.success("Permissions updated"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const removeAdmin = async (a) => {
    if (!window.confirm(`Remove admin access for ${a.name || a.email}?`)) return;
    try { await api.delete(`/admin/admins/${a.id}`); toast.success("Admin removed"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  return (
    <div className="space-y-4" data-testid="admins-manager">
      <div className="flex justify-between items-center">
        <h3 className="font-bold text-[#0F2744]">Admin accounts</h3>
        <Button onClick={() => setAdding(!adding)} className="bg-[#0F2744] hover:bg-[#0B192C]" data-testid="add-admin-btn"><Plus className="h-4 w-4 mr-1.5" /> Add admin</Button>
      </div>
      {adding && (
        <div className="rounded-2xl bg-white border border-slate-200 p-5 space-y-3" data-testid="add-admin-form">
          <div className="grid sm:grid-cols-2 gap-3">
            <div><Label className="text-xs text-slate-400">Name</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="admin-name-input" /></div>
            <div><Label className="text-xs text-slate-400">Email</Label><Input value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="admin-email-input" /></div>
          </div>
          <div>
            <Label className="text-xs text-slate-400 mb-2 block">Start from a role template</Label>
            <div className="flex flex-wrap gap-2" data-testid="template-picker">
              {templates.map((t) => (
                <button key={t.id} type="button" onClick={() => setForm({ ...form, permissions: t.permissions })} data-testid={`apply-template-${t.id}`}
                  className="px-3 py-1.5 rounded-full text-xs font-semibold border border-amber-300 bg-amber-50 text-amber-800 hover:bg-amber-100">{t.name}</button>
              ))}
              {templates.length === 0 && <span className="text-xs text-slate-400">No templates yet</span>}
            </div>
          </div>
          <div>
            <Label className="text-xs text-slate-400 mb-2 block">Functions they can use</Label>
            <div className="flex flex-wrap gap-2">
              {data.functions.map((k) => (
                <button key={k} type="button" onClick={() => setForm({ ...form, permissions: toggle(form.permissions, k) })} data-testid={`newperm-${k}`}
                  className={`px-3 py-1.5 rounded-full text-xs font-semibold border ${form.permissions.includes(k) ? "bg-[#0F2744] text-white border-[#0F2744]" : "bg-white text-slate-600 border-slate-200"}`}>{label(k)}</button>
              ))}
            </div>
            <div className="flex items-center gap-2 mt-3">
              <Input value={tplName} onChange={(e) => setTplName(e.target.value)} placeholder="Save these functions as a template, e.g. Treasurer" className="max-w-sm text-sm" data-testid="template-name-input" />
              <Button type="button" variant="outline" size="sm" onClick={saveTemplate} data-testid="save-template-btn"><Save className="h-4 w-4 mr-1" /> Save template</Button>
            </div>
          </div>
          <div className="flex justify-end gap-2"><Button variant="outline" onClick={() => setAdding(false)}>Cancel</Button><Button onClick={create} className="bg-green-600 hover:bg-green-700 text-white" data-testid="save-admin-btn">Invite admin</Button></div>
        </div>
      )}
      {data.admins.map((a) => (
        <div key={a.id} className="rounded-2xl bg-white border border-slate-200 p-5" data-testid={`admin-row-${a.id}`}>
          <div className="flex justify-between items-start gap-3">
            <div><div className="font-bold text-[#0F2744]">{a.name || a.email} {a.is_owner && <Badge className="bg-amber-100 text-amber-700 ml-1">Owner</Badge>}</div><div className="text-sm text-slate-500">{a.email}</div></div>
            {!a.is_owner && <Button variant="outline" onClick={() => removeAdmin(a)} className="border-red-300 text-red-600 hover:bg-red-50" data-testid={`remove-admin-${a.id}`}><Trash2 className="h-4 w-4" /></Button>}
          </div>
          {a.is_owner ? <p className="mt-3 text-sm text-slate-500">Full access to every function (cannot be changed).</p> : (
            <div className="mt-3">
              <Label className="text-xs text-slate-400 mb-2 block">Functions they can use (tap to toggle)</Label>
              <div className="flex flex-wrap gap-2">
                {data.functions.map((k) => {
                  const on = (a.permissions || []).includes(k);
                  return <button key={k} type="button" onClick={() => savePerms(a, toggle(a.permissions || [], k))} data-testid={`perm-${a.id}-${k}`}
                    className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition-colors ${on ? "bg-[#0F2744] text-white border-[#0F2744]" : "bg-white text-slate-600 border-slate-200 hover:border-slate-300"}`}>{label(k)}</button>;
                })}
              </div>
              {templates.length > 0 && (
                <div className="flex items-center gap-2 mt-3 flex-wrap">
                  <span className="text-xs text-slate-400">Apply template:</span>
                  {templates.map((t) => (
                    <button key={t.id} type="button" onClick={() => applyTemplate(a, t)} data-testid={`apply-template-${a.id}-${t.id}`}
                      className="px-2.5 py-1 rounded-full text-xs font-semibold border border-amber-300 bg-amber-50 text-amber-800 hover:bg-amber-100">{t.name}</button>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      ))}
      <div className="rounded-2xl bg-white border border-slate-200 p-5" data-testid="templates-manager">
        <h3 className="font-bold text-[#0F2744]">Role templates</h3>
        <p className="text-xs text-slate-500 mt-1 mb-3">Saved permission sets. Pick one when adding an admin, or apply it to an existing admin above.</p>
        <div className="space-y-2">
          {templates.map((t) => (
            <TemplateRow key={t.id} t={t} functions={data.functions} onChanged={loadTemplates} />
          ))}
        </div>
      </div>
    </div>
  );
}

function RebalanceSuggestion({ tutor, associates, tutors, members, onChanged }) {
  const [busy, setBusy] = useState(false);
  const cap = tutor.tutor_capacity ?? 6;
  if (associates.length <= cap) return null;
  const loadOf = (t) => members.filter((x) => x.tutor_assigned === t.name).length;
  const candidates = tutors.filter((t) => t.id !== tutor.id && loadOf(t) < (t.tutor_capacity ?? 6));
  const over = associates.length - cap;
  if (candidates.length === 0) return (
    <div className="mt-3 rounded-xl bg-amber-50 border border-amber-200 p-3 text-xs text-amber-800" data-testid={`rebalance-${tutor.id}`}>Over capacity by {over}. No other tutor has spare capacity right now.</div>
  );
  const mover = associates[associates.length - 1];
  const samePath = candidates.filter((t) => t.pathway === mover.pathway || t.pathway === "both");
  const pool = (samePath.length ? samePath : candidates).slice().sort((a, b) => ((b.tutor_capacity ?? 6) - loadOf(b)) - ((a.tutor_capacity ?? 6) - loadOf(a)));
  const target = pool[0];
  const move = async () => {
    setBusy(true);
    try { await api.patch(`/admin/members/${mover.id}`, { tutor_assigned: target.name }); toast.success(`Moved ${mover.name} → ${target.name}`); onChanged(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  return (
    <div className="mt-3 rounded-xl bg-amber-50 border border-amber-200 p-3 flex items-center justify-between gap-3" data-testid={`rebalance-${tutor.id}`}>
      <div className="text-xs text-amber-800">Over capacity by {over}. Suggest moving <strong>{mover.name}</strong> to <strong>{target.name}</strong> ({loadOf(target)}/{target.tutor_capacity ?? 6}).</div>
      <Button size="sm" onClick={move} disabled={busy} className="bg-amber-500 hover:bg-amber-400 text-[#0F2744] shrink-0" data-testid={`rebalance-move-${tutor.id}`}>
        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Move"}
      </Button>
    </div>
  );
}

function ReceiptsRegister() {
  const [data, setData] = useState(null);
  const [downloading, setDownloading] = useState(false);
  useEffect(() => { api.get("/admin/receipts/register").then(({ data }) => setData(data)).catch(() => setData({ rows: [], total: 0, year: new Date().getFullYear() })); }, []);
  if (!data) return null;
  const exportCsv = async () => {
    setDownloading(true);
    try {
      const res = await api.get("/admin/receipts/register.csv", { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "text/csv" }));
      const a = document.createElement("a");
      a.href = url; a.download = `rospa-wm-receipts-${data.year}.csv`;
      document.body.appendChild(a); a.click(); a.remove(); window.URL.revokeObjectURL(url);
    } catch (e) { toast.error("Couldn't export the register"); }
    finally { setDownloading(false); }
  };
  return (
    <div className="rounded-2xl bg-white border border-slate-200 p-5" data-testid="receipts-register">
      <div className="flex items-center justify-between mb-3">
        <h3 className="font-bold text-[#0F2744]">Receipts register <span className="text-slate-400 font-normal">· {data.year} · {gbp(data.total)}</span></h3>
        <Button size="sm" variant="outline" onClick={exportCsv} disabled={downloading || data.rows.length === 0} data-testid="receipts-export-csv">
          {downloading ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Download className="h-4 w-4 mr-1.5" /> Export CSV</>}
        </Button>
      </div>
      {data.rows.length === 0 ? <p className="text-sm text-slate-400">No receipts yet this year.</p> : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead><tr className="text-left text-slate-400 border-b border-slate-100">
              <th className="py-2 pr-4 font-medium">Receipt no.</th><th className="py-2 pr-4 font-medium">Date</th>
              <th className="py-2 pr-4 font-medium">Name</th><th className="py-2 pr-4 font-medium">Type</th>
              <th className="py-2 font-medium text-right">Amount</th></tr></thead>
            <tbody>
              {data.rows.map((r) => (
                <tr key={r.session_id} className="border-b border-slate-50" data-testid={`register-row-${r.session_id}`}>
                  <td className="py-2 pr-4 font-semibold text-[#0F2744]">{r.receipt_number}</td>
                  <td className="py-2 pr-4 text-slate-500">{fmt(r.date)}</td>
                  <td className="py-2 pr-4">{r.name}</td>
                  <td className="py-2 pr-4 capitalize text-slate-500">{r.kind === "renewal" ? "Renewal" : "Joining"}</td>
                  <td className="py-2 text-right font-semibold text-[#0F2744]">{gbp(r.amount)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function TeamManager() {
  const [people, setPeople] = useState(null);
  const load = () => api.get("/admin/team").then(({ data }) => setPeople(data)).catch(() => {});
  useEffect(() => { load(); }, []);
  if (!people) return <div className="flex justify-center py-10"><Loader2 className="h-6 w-6 animate-spin text-[#0F2744]" /></div>;
  const committee = people.filter((p) => p.section === "committee");
  const training = people.filter((p) => p.section === "training");
  const trainingDrivers = people.filter((p) => p.section === "training_driver");
  const Group = ({ label, list, section }) => (
    <div>
      <div className="flex items-center justify-between mb-3">
        <h3 className="font-bold text-[#0F2744]">{label}</h3>
        <TeamDialog section={section} onSaved={load} />
      </div>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {list.map((p) => (
          <div key={p.id} className="rounded-2xl bg-white border border-slate-200 p-4 flex gap-3" data-testid={`team-row-${p.id}`}>
            <TeamPhoto p={p} onChanged={load} />
            <div className="min-w-0 flex-1">
              <div className="font-bold text-[#0F2744] truncate">{p.name}</div>
              <div className="text-xs font-semibold text-amber-600 truncate">{p.role}{p.tier ? ` · ${TIER_LABEL[p.tier] || p.tier}` : ""}</div>
              {p.grade && <div className="text-xs text-slate-500 truncate">{p.grade}</div>}
              <div className="mt-2 flex gap-1.5">
                <TeamDialog member={p} onSaved={load} />
                <Button variant="outline" size="icon" className="h-7 w-7 text-red-500 hover:bg-red-50" data-testid={`team-delete-${p.id}`}
                  onClick={async () => { if (!window.confirm(`Remove ${p.name}?`)) return; await api.delete(`/admin/team/${p.id}`); toast.success("Removed"); load(); }}>
                  <Trash2 className="h-3.5 w-3.5" />
                </Button>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
  return (
    <div className="space-y-8" data-testid="team-manager">
      <p className="text-sm text-slate-500 max-w-2xl">Add and edit the people shown on the About page (Committee) and the Rider Training Team page. Upload a photo, set their role, grade and a short bio.</p>
      <Group label="Committee (About page)" list={committee} section="committee" />
      <Group label="Training Team (Riders → Training)" list={training} section="training" />
      <Group label="Training Team (Drivers → Driver training)" list={trainingDrivers} section="training_driver" />
    </div>
  );
}

function TeamPhoto({ p, onChanged }) {
  const [busy, setBusy] = useState(false);
  const ref = useRef(null);
  const upload = async (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    setBusy(true);
    try { const fd = new FormData(); fd.append("file", f); await api.post(`/admin/team/${p.id}/photo`, fd, { headers: { "Content-Type": "multipart/form-data" } }); toast.success("Photo updated"); onChanged(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setBusy(false); if (ref.current) ref.current.value = ""; }
  };
  const remove = async () => {
    if (!window.confirm(`Remove ${p.name}'s photo?`)) return;
    setBusy(true);
    try { await api.delete(`/admin/team/${p.id}/photo`); toast.success("Photo removed"); onChanged(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Couldn't remove photo"); }
    finally { setBusy(false); }
  };
  return (
    <div className="relative shrink-0">
      {p.photo ? <img src={resolvePhoto(p.photo)} alt={p.name} className="h-16 w-16 rounded-xl object-cover" />
        : <div className="h-16 w-16 rounded-xl bg-[#0F2744] text-white text-sm font-bold flex items-center justify-center">{memInitials(p.name)}</div>}
      <input ref={ref} type="file" accept="image/*" className="hidden" onChange={upload} data-testid={`team-photo-input-${p.id}`} />
      <button onClick={() => ref.current?.click()} disabled={busy} className="absolute -bottom-1 -right-1 h-6 w-6 rounded-full bg-amber-500 text-[#0F2744] flex items-center justify-center shadow hover:bg-amber-400 disabled:opacity-50" title="Change photo" data-testid={`team-photo-btn-${p.id}`}>
        {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : <Camera className="h-3 w-3" />}
      </button>
      {p.photo && !busy && (
        <button onClick={remove} className="absolute -top-1 -right-1 h-6 w-6 rounded-full bg-red-500 text-white flex items-center justify-center shadow hover:bg-red-600" title="Remove photo" data-testid={`team-photo-remove-${p.id}`}>
          <Trash2 className="h-3 w-3" />
        </button>
      )}
    </div>
  );
}

function TeamDialog({ member, section, onSaved }) {
  const editing = !!member;
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ name: "", role: "", section: section || "committee", tier: "", grade: "", bio: "", order: 0 });
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    if (open) setF(editing
      ? { name: member.name || "", role: member.role || "", section: member.section, tier: member.tier || "", grade: member.grade || "", bio: member.bio || "", order: member.order || 0 }
      : { name: "", role: "", section: section || "committee", tier: "", grade: "", bio: "", order: 0 });
  }, [open, member, section, editing]);
  const save = async () => {
    if (!f.name.trim()) { toast.error("Please add a name"); return; }
    setSaving(true);
    try {
      if (editing) await api.patch(`/admin/team/${member.id}`, f);
      else await api.post("/admin/team", f);
      toast.success(editing ? "Saved" : "Added"); setOpen(false); onSaved();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        {editing
          ? <Button variant="outline" size="icon" className="h-7 w-7" title="Edit" data-testid={`team-edit-${member.id}`}><Pencil className="h-3.5 w-3.5" /></Button>
          : <Button size="sm" className="bg-[#0F2744] hover:bg-[#0B192C]" data-testid={`team-add-${section}`}><Plus className="h-4 w-4 mr-1.5" /> Add person</Button>}
      </DialogTrigger>
      <DialogContent className="max-w-md max-h-[85vh] overflow-y-auto">
        <DialogHeader><DialogTitle>{editing ? "Edit person" : "Add person"}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div><Label className="mb-1.5 block">Name</Label><Input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="team-name-input" /></div>
          <div><Label className="mb-1.5 block">Role / title</Label><Input value={f.role} onChange={(e) => setF({ ...f, role: e.target.value })} placeholder="e.g. Chair, Head of Training" data-testid="team-role-input" /></div>
          <div>
            <Label className="mb-1.5 block">Section</Label>
            <Select value={f.section} onValueChange={(v) => setF({ ...f, section: v, tier: v === "committee" ? "" : f.tier })}>
              <SelectTrigger data-testid="team-section-select"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="committee">Committee (About page)</SelectItem><SelectItem value="training">Training Team (Riders)</SelectItem><SelectItem value="training_driver">Training Team (Drivers)</SelectItem></SelectContent>
            </Select>
          </div>
          {(f.section === "training" || f.section === "training_driver") && (
            <div>
              <Label className="mb-1.5 block">Tier</Label>
              <Select value={f.tier} onValueChange={(v) => setF({ ...f, tier: v })}>
                <SelectTrigger data-testid="team-tier-select"><SelectValue placeholder="Choose a tier" /></SelectTrigger>
                <SelectContent><SelectItem value="head">Head of Training</SelectItem><SelectItem value="tutor">Advanced Tutor</SelectItem></SelectContent>
              </Select>
            </div>
          )}
          <div><Label className="mb-1.5 block">Grade / qualification (optional)</Label><Input value={f.grade} onChange={(e) => setF({ ...f, grade: e.target.value })} placeholder="e.g. RoSPA Advanced Tutor (Gold)" data-testid="team-grade-input" /></div>
          <div><Label className="mb-1.5 block">Short bio (optional)</Label><textarea value={f.bio} onChange={(e) => setF({ ...f, bio: e.target.value })} rows={3} className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm" data-testid="team-bio-input" /></div>
          <div><Label className="mb-1.5 block">Order</Label><Input type="number" value={f.order} onChange={(e) => setF({ ...f, order: parseInt(e.target.value) || 0 })} data-testid="team-order-input" /></div>
          {!editing && <p className="text-xs text-slate-400">Save first, then use the camera button on the card to add a photo.</p>}
          <Button onClick={save} disabled={saving} className="w-full bg-[#0F2744] hover:bg-[#0B192C]" data-testid="team-save-btn">{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : editing ? "Save changes" : "Add person"}</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
const PAY_BADGE = { paid: "bg-green-100 text-green-700", pending: "bg-amber-100 text-amber-700", expired: "bg-slate-100 text-slate-500", failed: "bg-red-100 text-red-700" };

function SettingsPanel() {
  const [s, setS] = useState(null);
  const [saving, setSaving] = useState(false);
  useEffect(() => { api.get("/admin/settings").then(({ data }) => setS(data)).catch(() => {}); }, []);
  if (!s) return <div className="flex justify-center py-10"><Loader2 className="h-6 w-6 animate-spin text-[#0F2744]" /></div>;
  const save = async () => {
    setSaving(true);
    try { const { data } = await api.put("/admin/settings", s); setS(data); toast.success("Settings saved"); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };
  return (
    <div className="max-w-xl space-y-5" data-testid="settings-panel">
      <div className="rounded-2xl bg-white border border-slate-200 p-6">
        <h3 className="font-bold text-[#0F2744] flex items-center gap-2"><Settings className="h-4 w-4 text-amber-500" /> Membership term</h3>
        <p className="text-sm text-slate-500 mt-1">Choose how long a membership runs after someone joins or renews.</p>
        <div className="mt-4 space-y-3">
          <label className={`flex items-start gap-3 rounded-xl border-2 p-4 cursor-pointer transition-colors ${s.membership_term_mode === "rolling" ? "border-[#0F2744] bg-slate-50" : "border-slate-200 hover:border-slate-300"}`}>
            <input type="radio" name="term" checked={s.membership_term_mode === "rolling"} onChange={() => setS({ ...s, membership_term_mode: "rolling" })} data-testid="term-rolling" className="mt-1" />
            <div><div className="font-semibold text-[#0F2744]">One year from the payment date</div><div className="text-sm text-slate-500">e.g. join on 10 June → paid until 10 June next year.</div></div>
          </label>
          <label className={`flex items-start gap-3 rounded-xl border-2 p-4 cursor-pointer transition-colors ${s.membership_term_mode === "fixed" ? "border-[#0F2744] bg-slate-50" : "border-slate-200 hover:border-slate-300"}`}>
            <input type="radio" name="term" checked={s.membership_term_mode === "fixed"} onChange={() => setS({ ...s, membership_term_mode: "fixed" })} data-testid="term-fixed" className="mt-1" />
            <div className="flex-1">
              <div className="font-semibold text-[#0F2744]">Fixed club year</div>
              <div className="text-sm text-slate-500 mb-2">Everyone's membership runs until the same date each year (matches the April subscription).</div>
              {s.membership_term_mode === "fixed" && (
                <div className="flex items-center gap-2"><Label className="text-xs text-slate-400">Renews on</Label>
                  <Input value={s.membership_fixed_date || ""} onChange={(e) => setS({ ...s, membership_fixed_date: e.target.value })} placeholder="MM-DD (e.g. 03-31)" className="h-9 w-40" data-testid="term-fixed-date" />
                </div>
              )}
            </div>
          </label>
        </div>
        <Button onClick={save} disabled={saving} className="mt-5 bg-[#0F2744] hover:bg-[#0B192C]" data-testid="settings-save">{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save settings"}</Button>
      </div>

      <div className="rounded-2xl bg-white border border-slate-200 p-6">
        <h3 className="font-bold text-[#0F2744] flex items-center gap-2"><Settings className="h-4 w-4 text-amber-500" /> Membership fees</h3>
        <p className="text-sm text-slate-500 mt-1">Set the amounts members are charged by card (Stripe), in pounds.</p>
        <div className="mt-4 grid grid-cols-2 gap-3">
          <div>
            <Label className="text-xs text-slate-400 mb-1 block">Joining fee (£)</Label>
            <Input type="number" step="0.01" value={((s.join_fee_pence ?? 6500) / 100).toString()} onChange={(e) => setS({ ...s, join_fee_pence: Math.round((parseFloat(e.target.value) || 0) * 100) })} data-testid="fee-join" />
          </div>
          <div>
            <Label className="text-xs text-slate-400 mb-1 block">Annual renewal (£)</Label>
            <Input type="number" step="0.01" value={((s.renewal_fee_pence ?? 2000) / 100).toString()} onChange={(e) => setS({ ...s, renewal_fee_pence: Math.round((parseFloat(e.target.value) || 0) * 100) })} data-testid="fee-renewal" />
          </div>
        </div>
        <Button onClick={save} disabled={saving} className="mt-5 bg-[#0F2744] hover:bg-[#0B192C]" data-testid="settings-save-fees">{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save fees"}</Button>
      </div>

      <div className="rounded-2xl bg-white border border-slate-200 p-6">
        <h3 className="font-bold text-[#0F2744] flex items-center gap-2"><FileText className="h-4 w-4 text-amber-500" /> Document folders</h3>
        <p className="text-sm text-slate-500 mt-1">Folders members and admins can file documents under.</p>
        <div className="mt-4 space-y-2">
          {(s.document_folders || []).map((fld, i) => (
            <div key={i} className="flex items-center gap-2">
              <Input value={fld} onChange={(e) => { const n = [...s.document_folders]; n[i] = e.target.value; setS({ ...s, document_folders: n }); }} data-testid={`folder-${i}`} />
              <Button variant="outline" size="icon" onClick={() => setS({ ...s, document_folders: s.document_folders.filter((_, x) => x !== i) })} className="border-red-300 text-red-600 hover:bg-red-50" data-testid={`folder-del-${i}`}><Trash2 className="h-4 w-4" /></Button>
            </div>
          ))}
          <Button variant="outline" size="sm" onClick={() => setS({ ...s, document_folders: [...(s.document_folders || []), ""] })} data-testid="folder-add">+ Add folder</Button>
        </div>
        <Button onClick={save} disabled={saving} className="mt-5 bg-[#0F2744] hover:bg-[#0B192C]" data-testid="settings-save-folders">{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save folders"}</Button>
      </div>

      <div className="rounded-2xl bg-white border border-slate-200 p-6">
        <h3 className="font-bold text-[#0F2744] flex items-center gap-2"><Users className="h-4 w-4 text-amber-500" /> Committee roles</h3>
        <p className="text-sm text-slate-500 mt-1">Names shown against each application stage (e.g. "Treasurer (Gary)") and used for the Associate Controller step.</p>
        <div className="mt-4 space-y-3">
          <div><Label className="text-xs text-slate-400">Membership Secretary (driver check)</Label>
            <Input value={s.role_membership_secretary || ""} onChange={(e) => setS({ ...s, role_membership_secretary: e.target.value })} placeholder="e.g. Paul Timmins" className="h-9" data-testid="role-secretary" /></div>
          <div><Label className="text-xs text-slate-400">Membership Secretary email (overdue driver-check alerts)</Label>
            <Input value={s.role_membership_secretary_email || ""} onChange={(e) => setS({ ...s, role_membership_secretary_email: e.target.value })} placeholder="e.g. paul@rospabikers.club" className="h-9" data-testid="role-secretary-email" /></div>
          <div><Label className="text-xs text-slate-400">Treasurer (payment check)</Label>
            <Input value={s.role_treasurer || ""} onChange={(e) => setS({ ...s, role_treasurer: e.target.value })} placeholder="e.g. Gary Forster" className="h-9" data-testid="role-treasurer" /></div>
          <div><Label className="text-xs text-slate-400">Treasurer email (overdue payment-check alerts)</Label>
            <Input value={s.role_treasurer_email || ""} onChange={(e) => setS({ ...s, role_treasurer_email: e.target.value })} placeholder="e.g. gary@rospabikers.club" className="h-9" data-testid="role-treasurer-email" /></div>
          <div><Label className="text-xs text-slate-400">Associate Controller (post-approval)</Label>
            <Input value={s.role_associate_controller || ""} onChange={(e) => setS({ ...s, role_associate_controller: e.target.value })} placeholder="e.g. John Watts" className="h-9" data-testid="role-controller" /></div>
          <div><Label className="text-xs text-slate-400">Associate Controller email (for new-member alerts)</Label>
            <Input value={s.associate_controller_email || ""} onChange={(e) => setS({ ...s, associate_controller_email: e.target.value })} placeholder="e.g. john@rospabikers.club" className="h-9" data-testid="role-controller-email" /></div>
        </div>
        <Button onClick={save} disabled={saving} className="mt-5 bg-[#0F2744] hover:bg-[#0B192C]" data-testid="settings-save-roles">{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save settings"}</Button>
      </div>

      <div className="rounded-2xl bg-white border border-slate-200 p-6">
        <h3 className="font-bold text-[#0F2744] flex items-center gap-2"><MessageSquare className="h-4 w-4 text-amber-500" /> Enquiries inbox</h3>
        <p className="text-sm text-slate-500 mt-1">The reply-to address used when you reply to enquiries from the portal. Replies from members land in this inbox. Use a verified domain address (e.g. enquiries@yourdomain) once your domain is set up.</p>
        <div className="mt-4"><Label className="text-xs text-slate-400">Enquiries reply-to email</Label>
          <Input value={s.enquiries_email || ""} onChange={(e) => setS({ ...s, enquiries_email: e.target.value })} placeholder="enquiries@yourdomain.co.uk" className="h-9" data-testid="enquiries-email" /></div>
        <Button onClick={save} disabled={saving} className="mt-5 bg-[#0F2744] hover:bg-[#0B192C]" data-testid="settings-save-enquiries">{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save settings"}</Button>
      </div>

      <div className="rounded-2xl bg-white border border-slate-200 p-6">
        <h3 className="font-bold text-[#0F2744] flex items-center gap-2"><MessageSquare className="h-4 w-4 text-amber-500" /> Enquiry reply templates</h3>
        <p className="text-sm text-slate-500 mt-1">Saved canned replies. When replying to an enquiry, pick one to drop it straight into your message.</p>
        <div className="mt-4 space-y-3">
          {(s.reply_templates || []).map((t, i) => (
            <div key={i} className="rounded-xl border border-slate-200 p-3 space-y-2" data-testid={`tpl-row-${i}`}>
              <div className="flex items-center gap-2">
                <Input value={t.title || ""} onChange={(e) => { const n = [...s.reply_templates]; n[i] = { ...n[i], title: e.target.value }; setS({ ...s, reply_templates: n }); }} placeholder="Template name, e.g. Thanks for your enquiry" className="h-9" data-testid={`tpl-title-${i}`} />
                <Button variant="outline" size="sm" onClick={() => setS({ ...s, reply_templates: s.reply_templates.filter((_, x) => x !== i) })} className="border-red-300 text-red-600 hover:bg-red-50" data-testid={`tpl-del-${i}`}><Trash2 className="h-4 w-4" /></Button>
              </div>
              <Textarea rows={3} value={t.body || ""} onChange={(e) => { const n = [...s.reply_templates]; n[i] = { ...n[i], body: e.target.value }; setS({ ...s, reply_templates: n }); }} placeholder="The reply text…" data-testid={`tpl-body-${i}`} />
            </div>
          ))}
          <Button variant="outline" size="sm" onClick={() => setS({ ...s, reply_templates: [...(s.reply_templates || []), { id: Math.random().toString(36).slice(2), title: "", body: "" }] })} data-testid="tpl-add">+ Add template</Button>
        </div>
        <Button onClick={save} disabled={saving} className="mt-5 bg-[#0F2744] hover:bg-[#0B192C]" data-testid="settings-save-templates">{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save templates"}</Button>
      </div>

      <div className="rounded-2xl bg-white border border-slate-200 p-6">
        <h3 className="font-bold text-[#0F2744] flex items-center gap-2"><MessageSquare className="h-4 w-4 text-amber-500" /> Chairman's welcome letter</h3>
        <p className="text-sm text-slate-500 mt-1">Sent automatically to every new member when their application is approved, alongside the "set your password" email. Use <span className="font-mono">{"{name}"}</span> to insert the member's name. Leave a blank line between paragraphs.</p>
        <div className="mt-4 space-y-3">
          <div><Label className="text-xs text-slate-400">Subject</Label>
            <Input value={s.welcome_letter_subject || ""} onChange={(e) => setS({ ...s, welcome_letter_subject: e.target.value })} className="h-9" data-testid="welcome-subject" /></div>
          <div><Label className="text-xs text-slate-400">Message</Label>
            <Textarea rows={12} value={s.welcome_letter_body || ""} onChange={(e) => setS({ ...s, welcome_letter_body: e.target.value })} data-testid="welcome-body" /></div>
        </div>
        <Button onClick={save} disabled={saving} className="mt-5 bg-[#0F2744] hover:bg-[#0B192C]" data-testid="settings-save-welcome">{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save welcome letter"}</Button>
      </div>
    </div>
  );
}

function PaymentsTab({ payments }) {
  const [busy, setBusy] = useState("");
  if (!payments) return <div className="flex justify-center py-10"><Loader2 className="h-6 w-6 animate-spin text-[#0F2744]" /></div>;
  const s = payments.summary || {};
  const rows = payments.transactions || [];
  const download = async (r) => {
    setBusy(`d-${r.session_id}`);
    try {
      const res = await api.get(`/admin/payments/${r.session_id}/receipt`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url; a.download = `receipt-${r.name.replace(/\s/g, "-").toLowerCase()}.pdf`;
      document.body.appendChild(a); a.click(); a.remove(); window.URL.revokeObjectURL(url);
    } catch (e) { toast.error("Couldn't download the receipt"); }
    finally { setBusy(""); }
  };
  const resend = async (r) => {
    setBusy(`r-${r.session_id}`);
    try { const { data } = await api.post(`/admin/payments/${r.session_id}/resend-receipt`); toast.success(`Receipt re-sent to ${data.email}`); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(""); }
  };
  return (
    <div className="space-y-5" data-testid="payments-tab">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Stat icon={CreditCard} label="Total collected" value={gbp(s.total_paid)} />
        <Stat icon={Check} label="Paid" value={s.count_paid || 0} />
        <Stat icon={Loader2} label="Pending" value={s.count_pending || 0} />
        <Stat icon={ClipboardList} label="All payments" value={s.count_total || 0} />
      </div>
      {rows.length === 0 ? <Empty text="No payments yet." /> : (
        <div className="rounded-2xl bg-white border border-slate-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="payments-table">
              <thead className="bg-slate-50 text-slate-500">
                <tr>
                  <th className="text-left font-semibold px-4 py-3">Member</th>
                  <th className="text-left font-semibold px-4 py-3">Type</th>
                  <th className="text-left font-semibold px-4 py-3">Amount</th>
                  <th className="text-left font-semibold px-4 py-3">Status</th>
                  <th className="text-left font-semibold px-4 py-3">Date</th>
                  <th className="text-left font-semibold px-4 py-3">Receipt</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.session_id} className="border-t border-slate-100" data-testid={`payment-row-${r.session_id}`}>
                    <td className="px-4 py-3">
                      <div className="font-semibold text-[#0F2744]">{r.name}</div>
                      <div className="text-xs text-slate-400">{r.email}</div>
                    </td>
                    <td className="px-4 py-3 capitalize text-slate-600">{r.kind === "renewal" ? "Renewal" : "Joining"}{r.pathway ? ` · ${r.pathway}` : ""}</td>
                    <td className="px-4 py-3 font-semibold text-[#0F2744]">{gbp(r.amount)}</td>
                    <td className="px-4 py-3"><Badge className={`${PAY_BADGE[r.payment_status] || PAY_BADGE.pending} capitalize`}>{r.payment_status}</Badge></td>
                    <td className="px-4 py-3 text-slate-500">{fmt((r.updated_at || r.created_at || "").slice(0, 10))}</td>
                    <td className="px-4 py-3">
                      {r.payment_status === "paid" ? (
                        <div className="flex items-center gap-2">
                          <Button size="sm" variant="outline" onClick={() => download(r)} disabled={busy === `d-${r.session_id}`} className="h-8" data-testid={`receipt-download-${r.session_id}`}>
                            {busy === `d-${r.session_id}` ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
                          </Button>
                          <Button size="sm" variant="outline" onClick={() => resend(r)} disabled={busy === `r-${r.session_id}`} className="h-8" title="Re-send receipt by email" data-testid={`receipt-resend-${r.session_id}`}>
                            {busy === `r-${r.session_id}` ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                          </Button>
                        </div>
                      ) : <span className="text-xs text-slate-400">—</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
      <ReceiptsRegister />
    </div>
  );
}

function ChatGroupsManager() {
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const load = () => { api.get("/admin/chat/groups").then(({ data }) => setGroups(data)).catch(() => {}).finally(() => setLoading(false)); };
  useEffect(load, []);
  if (loading) return <div className="flex justify-center py-10"><Loader2 className="h-6 w-6 animate-spin text-[#0F2744]" /></div>;
  return (
    <div className="space-y-4" data-testid="chat-groups-manager">
      <div className="flex items-center justify-between gap-3">
        <p className="text-sm text-slate-500 max-w-xl">Create, rename or remove the chat groups members see in the Community area. "Open to all" groups are visible to every member; otherwise only committee/admin (Committee only) or specifically authorised members can see them.</p>
        <ChatGroupDialog onSaved={load} />
      </div>
      <div className="space-y-3">
        {groups.map((g) => (
          <div key={g.key} className="rounded-2xl bg-white border border-slate-200 p-5 flex items-center justify-between gap-4" data-testid={`chat-group-row-${g.key}`}>
            <div className="min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <h3 className="font-bold text-[#0F2744] truncate">{g.name}</h3>
                {g.open ? <Badge className="bg-green-100 text-green-700 hover:bg-green-100">Open to all</Badge>
                  : g.committee_only ? <Badge className="bg-amber-100 text-amber-700 hover:bg-amber-100">Committee only</Badge>
                  : <Badge className="bg-slate-100 text-slate-600 hover:bg-slate-100">Authorised members</Badge>}
                {g.protected && <Badge variant="outline" className="text-xs">Default</Badge>}
              </div>
              <p className="text-xs text-slate-400 mt-1">Key: {g.key}{!g.open && !g.committee_only && ` · ${g.member_count} authorised`}</p>
            </div>
            <div className="flex gap-2 shrink-0">
              <ChatGroupDialog group={g} onSaved={load} />
              {!g.protected && (
                <Button variant="outline" size="icon" className="text-red-500 hover:bg-red-50" data-testid={`chat-group-delete-${g.key}`}
                  onClick={async () => { if (!window.confirm(`Delete "${g.name}" and all its messages?`)) return; try { await api.delete(`/admin/chat/groups/${g.key}`); toast.success("Group deleted"); load(); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } }}>
                  <Trash2 className="h-4 w-4" />
                </Button>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function ChatGroupDialog({ group, onSaved }) {
  const editing = !!group;
  const [open, setOpen] = useState(false);
  const [name, setName] = useState(group?.name || "");
  const [visibility, setVisibility] = useState(group?.open ? "open" : group?.committee_only ? "committee" : "authorised");
  const [saving, setSaving] = useState(false);
  useEffect(() => { if (open) { setName(group?.name || ""); setVisibility(group?.open ? "open" : group?.committee_only ? "committee" : "authorised"); } }, [open, group]);
  const save = async () => {
    if (!name.trim()) { toast.error("Please give the group a name"); return; }
    setSaving(true);
    const body = { name, open: visibility === "open", committee_only: visibility === "committee" };
    try {
      if (editing) await api.patch(`/admin/chat/groups/${group.key}`, body);
      else await api.post("/admin/chat/groups", body);
      toast.success(editing ? "Group updated" : "Group created"); setOpen(false); onSaved();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        {editing
          ? <Button variant="outline" size="icon" title="Edit group" data-testid={`chat-group-edit-${group.key}`}><Pencil className="h-4 w-4" /></Button>
          : <Button className="bg-[#0F2744] hover:bg-[#0B192C]" data-testid="chat-group-add-btn"><Plus className="h-4 w-4 mr-1.5" /> New group</Button>}
      </DialogTrigger>
      <DialogContent className="max-w-md">
        <DialogHeader><DialogTitle>{editing ? "Edit chat group" : "New chat group"}</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div>
            <Label className="mb-1.5 block">Group name</Label>
            <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Committee Group" data-testid="chat-group-name-input" />
          </div>
          <div>
            <Label className="mb-1.5 block">Who can see this group?</Label>
            <Select value={visibility} onValueChange={setVisibility}>
              <SelectTrigger data-testid="chat-group-visibility"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="open">Open to all members</SelectItem>
                <SelectItem value="committee">Committee &amp; admins only</SelectItem>
                <SelectItem value="authorised">Only authorised members (set per group)</SelectItem>
              </SelectContent>
            </Select>
            {visibility === "authorised" && <p className="text-xs text-slate-400 mt-1.5">After saving, open the group in the Community area to pick authorised members.</p>}
          </div>
          <Button onClick={save} disabled={saving} className="w-full bg-[#0F2744] hover:bg-[#0B192C]" data-testid="chat-group-save-btn">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : editing ? "Save changes" : "Create group"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function ResetPwDialog({ m }) {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [temp, setTemp] = useState("");
  const doReset = async (mode) => {
    setLoading(true); setTemp("");
    try {
      const { data } = await api.post(`/admin/members/${m.id}/reset-password`, { mode });
      if (mode === "email") { toast.success(`Reset link emailed to ${m.email}`); setOpen(false); }
      else { setTemp(data.temp_password); toast.success("Temporary password created"); }
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setLoading(false); }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) setTemp(""); }}>
      <DialogTrigger asChild><Button variant="outline" size="icon" title="Reset password" data-testid={`reset-pw-${m.id}`}><KeyRound className="h-4 w-4" /></Button></DialogTrigger>
      <DialogContent className="max-w-md">
        <DialogHeader><DialogTitle>Reset password — {m.name}</DialogTitle></DialogHeader>
        <p className="text-sm text-slate-500">Choose how to reset this member's password.</p>
        <div className="flex flex-col gap-3 mt-2">
          <Button onClick={() => doReset("temp")} disabled={loading} className="bg-[#0F2744] hover:bg-[#0B192C]" data-testid={`reset-pw-temp-${m.id}`}>
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Set a temporary password"}
          </Button>
          <Button onClick={() => doReset("email")} disabled={loading} variant="outline" data-testid={`reset-pw-email-${m.id}`}>Email a reset link to the member</Button>
        </div>
        {temp && (
          <div className="mt-4 rounded-lg bg-amber-50 border border-amber-200 p-4" data-testid={`temp-pw-${m.id}`}>
            <div className="text-xs text-slate-500 mb-1">Temporary password (share securely — shown once):</div>
            <div className="flex items-center gap-2">
              <code className="flex-1 font-mono text-lg font-bold text-[#0F2744]">{temp}</code>
              <Button size="sm" variant="outline" onClick={() => { navigator.clipboard.writeText(temp); toast.success("Copied"); }} data-testid={`temp-pw-copy-${m.id}`}>Copy</Button>
            </div>
            <p className="text-xs text-slate-500 mt-2">Ask the member to sign in with this, then change it from their account.</p>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

const DAYS = ["Saturday", "Sunday", "Weekdays"];
function DaysPicker({ value, onChange, testid }) {
  const v = value || [];
  return (
    <div className="flex gap-1.5 flex-wrap" data-testid={testid}>
      {DAYS.map((d) => {
        const on = v.includes(d);
        return (
          <button type="button" key={d} onClick={() => onChange(on ? v.filter((x) => x !== d) : [...v, d])}
            className={`px-2.5 py-1 rounded-full text-xs font-semibold border transition-colors ${on ? "bg-[#0F2744] text-white border-[#0F2744]" : "bg-white text-slate-500 border-slate-200 hover:border-slate-300"}`}>
            {d}
          </button>
        );
      })}
    </div>
  );
}

function ImportMembersDialog({ onDone }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const API = process.env.REACT_APP_BACKEND_URL;
  const upload = async (e) => {
    const f = e.target.files?.[0]; e.target.value = ""; if (!f) return;
    setBusy(true); setResult(null);
    try { const fd = new FormData(); fd.append("file", f); const { data } = await api.post("/admin/members/import", fd, { headers: { "Content-Type": "multipart/form-data" } }); setResult(data); toast.success(`${data.created} members imported`); onDone(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) setResult(null); }}>
      <DialogTrigger asChild><Button variant="outline" data-testid="import-members-btn"><Upload className="h-4 w-4 mr-1.5" /> Import members</Button></DialogTrigger>
      <DialogContent>
        <DialogHeader><DialogTitle>Import existing members</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <p className="text-sm text-slate-600">Download the template, fill in one member per row, then upload it. Rows whose email already exists are skipped. Imported members set their own password via "Forgot password".</p>
          <a href={`${API}/api/admin/members/import-template`} className="inline-flex items-center gap-1.5 text-sm font-semibold text-[#0F2744] underline" data-testid="import-template-link"><FileText className="h-4 w-4" /> Download CSV template</a>
          <label className="block border-2 border-dashed border-slate-300 rounded-xl p-6 text-center cursor-pointer hover:border-amber-500">
            <input type="file" accept=".csv" className="hidden" onChange={upload} data-testid="import-file" />
            {busy ? <Loader2 className="h-6 w-6 animate-spin mx-auto text-[#0F2744]" /> : <><Upload className="h-6 w-6 mx-auto text-slate-400" /><div className="mt-2 text-sm font-semibold text-[#0F2744]">Upload completed CSV</div></>}
          </label>
          {result && <div className="text-sm bg-slate-50 rounded-lg p-3 space-y-1" data-testid="import-result"><div className="font-semibold text-green-700">{result.created} member(s) created</div>{result.skipped?.length > 0 && <div className="text-slate-500">Skipped (already exist): {result.skipped.join(", ")}</div>}{result.errors?.length > 0 && <div className="text-red-600">{result.errors.join("; ")}</div>}</div>}
        </div>
      </DialogContent>
    </Dialog>
  );
}

function MemberContact({ m }) {
  const [vals, setVals] = useState(m.pii_hidden ? null : { address: [m.address_line1, m.address_line2, m.town, m.county, m.postcode].filter(Boolean).join(", ") || m.address || "—", phone: m.phone || "—" });
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [status, setStatus] = useState(null);
  const reveal = async () => {
    setBusy(true);
    try { const { data } = await api.get(`/admin/members/${m.id}/pii-reveal`); setVals({ address: data.address || "—", phone: data.phone || "—" }); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const request = async () => {
    if (!reason.trim()) { toast.error("Please enter a reason."); return; }
    setBusy(true);
    try { const { data } = await api.post(`/admin/members/${m.id}/pii-request`, { reason }); setStatus(data.status); if (data.status === "approved") reveal(); else toast.success("Request sent for admin approval"); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  if (vals) return (
    <div className="mt-2 rounded-lg bg-slate-50 border border-slate-200 p-3" data-testid={`member-contact-${m.id}`}>
      <div className="flex justify-between gap-2 py-1 text-sm"><span className="font-semibold text-slate-500">Phone</span><span className="text-slate-800">{vals.phone}</span></div>
      <div className="flex justify-between gap-2 py-1 text-sm"><span className="font-semibold text-slate-500">Address</span><span className="text-slate-800 text-right">{vals.address}</span></div>
    </div>
  );
  return (
    <div className="mt-3 rounded-lg bg-amber-50 border border-amber-200 p-3" data-testid={`member-contact-locked-${m.id}`}>
      <div className="text-xs font-bold text-amber-800 uppercase tracking-wide flex items-center gap-1.5 mb-1"><ShieldAlert className="h-3.5 w-3.5" /> Address &amp; phone protected</div>
      {status === "pending" ? (
        <p className="text-sm text-slate-600">Your request has been sent. An admin needs to approve it before the details show.</p>
      ) : (
        <>
          <p className="text-sm text-slate-600 mb-2">Enter a reason to request these details. An admin will approve it.</p>
          <Input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Reason (e.g. arranging an observed ride)" className="h-9" data-testid={`pii-reason-${m.id}`} />
          <div className="flex gap-2 mt-2">
            <Button size="sm" onClick={request} disabled={busy} className="bg-[#0F2744] hover:bg-[#0B192C]" data-testid={`pii-request-${m.id}`}>{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Request access"}</Button>
            <Button size="sm" variant="outline" onClick={reveal} disabled={busy} data-testid={`pii-reveal-${m.id}`}>I have approval — reveal</Button>
          </div>
        </>
      )}
    </div>
  );
}

function PiiRequests() {
  const [data, setData] = useState(null);
  const load = () => api.get("/admin/pii-requests").then(({ data }) => setData(data)).catch(() => {});
  useEffect(() => { load(); }, []);
  if (!data) return null;
  const pending = (data.requests || []).filter((r) => r.status === "pending");
  if (!pending.length) return null;
  const decide = async (id, decision) => {
    try { await api.post(`/admin/pii-requests/${id}/${decision}`); toast.success(decision === "approve" ? "Approved" : "Denied"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  return (
    <div className="rounded-2xl bg-amber-50 border border-amber-200 p-4" data-testid="pii-requests">
      <div className="font-bold text-amber-900 text-sm mb-2">Contact-detail access requests ({pending.length})</div>
      <div className="space-y-2">
        {pending.map((r) => (
          <div key={r.id} className="flex flex-wrap items-center justify-between gap-2 bg-white rounded-lg border border-amber-100 p-2.5" data-testid={`pii-req-${r.id}`}>
            <div className="text-sm min-w-0">
              <span className="font-semibold text-[#0F2744]">{r.requester_name}</span> wants <span className="font-semibold">{r.member_name}</span>'s address &amp; phone
              <div className="text-xs text-slate-500 truncate">Reason: {r.reason}</div>
            </div>
            {data.can_approve && r.requested_by !== data.me ? (
              <div className="flex gap-1.5 shrink-0">
                <Button size="sm" onClick={() => decide(r.id, "approve")} className="bg-green-600 hover:bg-green-700 h-8" data-testid={`pii-approve-${r.id}`}>Approve</Button>
                <Button size="sm" variant="outline" onClick={() => decide(r.id, "deny")} className="h-8" data-testid={`pii-deny-${r.id}`}>Deny</Button>
              </div>
            ) : <span className="text-xs text-slate-400 shrink-0">{r.requested_by === data.me ? "Awaiting another admin" : "View only"}</span>}
          </div>
        ))}
      </div>
    </div>
  );
}

function MemberDialog({ m, canEmergency, roleNames = {} }) {
  const [open, setOpen] = useState(false);
  const history = [...(m.grade_history || [])].sort((a, b) => (a.date < b.date ? 1 : -1));
  const rows = [
    ["Email", m.email],
    ["Role", m.role], ["Member status", m.member_status],
    ["Membership paid until", fmt(m.membership_paid_until) || "—"],
    ...(m.approved_by ? [["Approved by", `${m.approved_by}${m.approved_at ? ` · ${fmt(m.approved_at)}` : ""}`]] : []),
    ["Pathway", m.pathway || "—"], ["Grade", m.grade || "none"],
    ["Preferred days", (m.preferred_days || []).join(", ") || "—"],
    ["Tutor availability", m.is_tutor ? ((m.tutor_days || []).join(", ") || "Yes") : "No"],
    ["Assigned tutor", m.tutor_assigned || "—"], ["Re-certification", fmt(m.recert_date)],
    ["Joined", fmt(m.created_at)],
  ];
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild><Button variant="outline" size="sm" data-testid={`view-member-${m.id}`}>View</Button></DialogTrigger>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle>{m.name}</DialogTitle></DialogHeader>
        <dl className="space-y-1">
          {rows.map(([k, v]) => (
            <div key={k} className="flex justify-between gap-2 py-1.5 border-b border-slate-100">
              <dt className="text-sm font-semibold text-slate-500">{k}</dt>
              <dd className="text-sm text-slate-800 text-right capitalize">{v}</dd>
            </div>
          ))}
        </dl>
        <MemberContact m={m} />
        {history.length > 0 && (
          <div className="mt-2">
            <div className="text-sm font-semibold text-slate-500 mb-1">Grade history</div>
            {history.map((h) => (
              <div key={h.id} className="text-sm text-slate-700 capitalize">{h.grade} — passed {fmt(h.date)}</div>
            ))}
          </div>
        )}
        {canEmergency && <div className="mt-3"><EmergencyReveal url={`/admin/members/${m.id}/emergency`} testId={`member-emergency-${m.id}`} /></div>}
        {open && <MemberLogs m={m} roleNames={roleNames} onChanged={onChangedNoop} />}
      </DialogContent>
    </Dialog>
  );
}

const onChangedNoop = () => {};

function MemberLogs({ m, roleNames = {} }) {
  const [data, setData] = useState(null);
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);
  const [ctrl, setCtrl] = useState(m.associate_controller || null);
  const [busyCtrl, setBusyCtrl] = useState(false);
  const load = () => api.get(`/admin/members/${m.id}/logs`).then(({ data: d }) => setData(d)).catch(() => setData({ change_log: [], notes: [] }));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [m.id]);
  const addNote = async () => {
    if (!note.trim()) return;
    setSaving(true);
    try { await api.post(`/admin/members/${m.id}/notes`, { text: note }); setNote(""); toast.success("Note added"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };
  const sendController = async () => {
    setBusyCtrl(true);
    try {
      const { data: r } = await api.post(`/admin/members/${m.id}/controller`, { done: true, controller: roleNames.controller || "" });
      setCtrl({ done: true, by: "You", at: new Date().toISOString(), controller: r.controller });
      toast.success("Marked sent to Associate Controller");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusyCtrl(false); }
  };
  const logs = data?.change_log || [];
  const notes = data?.notes || [];
  const controllerLabel = roleNames.controller ? `Associate Controller (${roleNames.controller})` : "Associate Controller";

  const events = [
    { label: "Joined the club", at: m.created_at },
    ...(m.approved_by ? [{ label: `Approved by ${m.approved_by}`, at: m.approved_at }] : []),
    ...[...(m.grade_history || [])].map((h) => ({ label: `Passed ${h.grade}`, at: h.date })),
    ...(ctrl && ctrl.done ? [{ label: `Sent to ${ctrl.controller ? `Associate Controller (${ctrl.controller})` : "Associate Controller"}`, by: ctrl.by, at: ctrl.at }] : []),
    ...logs.map((l) => ({ label: `Updated ${(l.changes || []).map((c) => String(c.field).replace(/_/g, " ")).join(", ")}`, by: l.by, at: l.at })),
    ...notes.map((n) => ({ label: `Note: ${n.text}`, by: n.by, at: n.at })),
  ].filter((e) => e.at).sort((x, y) => (x.at < y.at ? -1 : 1));

  return (
    <div className="mt-4 pt-4 border-t border-slate-100" data-testid={`member-logs-${m.id}`}>
      <div className="rounded-xl border border-slate-200 p-3 mb-4" data-testid={`member-controller-${m.id}`}>
        <div className="text-sm font-semibold text-[#0F2744]">{controllerLabel}</div>
        {ctrl && ctrl.done ? (
          <div className="text-xs text-green-700 mt-1 flex items-center gap-1.5"><Check className="h-3.5 w-3.5" /> Sent{ctrl.by ? ` by ${ctrl.by}` : ""}{ctrl.at ? ` · ${fmt(ctrl.at)}` : ""}</div>
        ) : (
          <Button onClick={sendController} disabled={busyCtrl} size="sm" className="mt-2 bg-[#0F2744] hover:bg-[#0B192C]" data-testid={`member-controller-send-${m.id}`}>
            {busyCtrl ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Send className="h-4 w-4 mr-1.5" /> Send to {roleNames.controller || "Associate Controller"}</>}
          </Button>
        )}
      </div>

      <div className="text-sm font-semibold text-slate-500 mb-2">Notes on file</div>
      <div className="flex gap-2">
        <Input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Add a note about this member…" data-testid={`member-note-input-${m.id}`} />
        <Button onClick={addNote} disabled={saving || !note.trim()} className="bg-[#0F2744] hover:bg-[#0B192C]" data-testid={`member-note-add-${m.id}`}>Add</Button>
      </div>

      <div className="text-sm font-semibold text-slate-500 mt-4 mb-2 flex items-center gap-1.5"><History className="h-3.5 w-3.5" /> Member timeline</div>
      {events.length === 0 ? (
        <p className="text-xs text-slate-400">No history yet.</p>
      ) : (
        <div className="relative pl-4 border-l-2 border-slate-100 space-y-3">
          {events.map((e, i) => (
            <div key={i} className="relative" data-testid={`member-timeline-${m.id}-${i}`}>
              <span className="absolute -left-[21px] top-1 h-2.5 w-2.5 rounded-full bg-amber-500 ring-2 ring-white" />
              <div className="text-sm text-[#0F2744]">{e.label}</div>
              <div className="text-xs text-slate-400">{fmt(e.at)}{e.by ? ` · ${e.by}` : ""}</div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function MemberPhoto({ m, onChanged }) {
  const [busy, setBusy] = useState(false);
  const inputRef = useRef(null);
  const upload = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setBusy(true);
    try {
      const fd = new FormData(); fd.append("file", f);
      await api.post(`/admin/members/${m.id}/photo`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Photo updated"); onChanged();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Couldn't upload photo"); }
    finally { setBusy(false); if (inputRef.current) inputRef.current.value = ""; }
  };
  const remove = async () => {
    setBusy(true);
    try { await api.delete(`/admin/members/${m.id}/photo`); toast.success("Photo removed"); onChanged(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  return (
    <div className="relative shrink-0">
      {m.photo ? (
        <img src={resolvePhoto(m.photo)} alt={m.name} className="h-12 w-12 rounded-xl object-cover" data-testid={`member-photo-${m.id}`} />
      ) : (
        <div className="h-12 w-12 rounded-xl bg-[#0F2744] text-white text-sm font-bold flex items-center justify-center" data-testid={`member-photo-${m.id}`}>{memInitials(m.name)}</div>
      )}
      <input ref={inputRef} type="file" accept="image/*" className="hidden" onChange={upload} data-testid={`member-photo-input-${m.id}`} />
      <button onClick={() => inputRef.current?.click()} disabled={busy}
        className="absolute -bottom-1 -right-1 h-6 w-6 rounded-full bg-amber-500 text-[#0F2744] flex items-center justify-center shadow hover:bg-amber-400 disabled:opacity-50"
        title="Change photo" data-testid={`member-photo-btn-${m.id}`}>
        {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : <Camera className="h-3 w-3" />}
      </button>
      {m.photo && !busy && (
        <button onClick={remove} className="absolute -top-1 -right-1 h-5 w-5 rounded-full bg-red-500 text-white flex items-center justify-center shadow hover:bg-red-600" title="Remove photo" data-testid={`member-photo-remove-${m.id}`}>
          <Trash2 className="h-2.5 w-2.5" />
        </button>
      )}
    </div>
  );
}

function MemberRow({ m, tutors = [], tutorLoads = {}, canEmergency, roleNames = {}, onChanged }) {
  const { t } = useContent();
  const [role, setRole] = useState(m.role);
  const [status, setStatus] = useState(m.member_status);
  const [tutor, setTutor] = useState(m.tutor_assigned || "");
  const [preferredDays, setPreferredDays] = useState(m.preferred_days || []);
  const [isTutor, setIsTutor] = useState(!!m.is_tutor);
  const [tutorDays, setTutorDays] = useState(m.tutor_days || []);
  const [name, setName] = useState(m.name || "");
  const [phone, setPhone] = useState(m.phone || "");
  const dirty = role !== m.role || status !== m.member_status || tutor !== (m.tutor_assigned || "")
    || JSON.stringify(preferredDays) !== JSON.stringify(m.preferred_days || [])
    || isTutor !== !!m.is_tutor
    || JSON.stringify(tutorDays) !== JSON.stringify(m.tutor_days || [])
    || name !== (m.name || "") || phone !== (m.phone || "");
  const history = [...(m.grade_history || [])].sort((a, b) => (a.date < b.date ? 1 : -1));

  const save = async () => {
    try { await api.patch(`/admin/members/${m.id}`, { role, member_status: status, tutor_assigned: tutor, preferred_days: preferredDays, is_tutor: isTutor, tutor_days: tutorDays, name, phone }); toast.success("Member updated"); onChanged(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const sendRenewal = async () => {
    if (!window.confirm(`Email a ${t("fee.renewal", "£20")} renewal payment request to ${m.name}?`)) return;
    try { await api.post(`/admin/members/${m.id}/send-renewal`); toast.success(`Renewal request sent to ${m.email}`); onChanged(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const resendInvite = async () => {
    if (!window.confirm(`Resend the "set your password" invite email to ${m.name} (${m.email})?`)) return;
    try { await api.post(`/admin/members/${m.id}/resend-invite`); toast.success(`Invite re-sent to ${m.email}`); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const deleteMember = async () => {
    if (!window.confirm(`Delete ${m.name}? Their personal details will be anonymised but club history (payments, attendance) is kept for records. This cannot be undone.`)) return;
    try { await api.delete(`/admin/members/${m.id}`); toast.success("Member anonymised and removed"); onChanged(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div className="rounded-2xl bg-white border border-slate-200 p-5" data-testid={`admin-member-${m.id}`}>
      <div className="flex flex-col lg:flex-row lg:items-center gap-4">
        <div className="flex items-center gap-3 lg:w-56 min-w-0">
          <MemberPhoto m={m} onChanged={onChanged} />
          <div className="min-w-0">
            <h3 className="font-bold text-[#0F2744] truncate">{m.name}</h3>
            <p className="text-sm text-slate-500 truncate">{m.email}</p>
            <div className="mt-1 flex items-center gap-2 flex-wrap">
              <span className="text-xs capitalize font-semibold text-[#0F2744]">Grade: {m.grade || "none"}</span>
              <Badge className={`${RECERT_BADGE[m.recert_status] || RECERT_BADGE.none} text-[10px]`}>
                {m.recert_status === "none" ? "No grade" : `Re-cert ${fmt(m.recert_date)}`}
              </Badge>
              {m.membership_expiry_status && m.membership_expiry_status !== "none" && (
                <Badge className={`${MEMBER_BADGE[m.membership_expiry_status]} text-[10px]`} data-testid={`member-membership-${m.id}`}>
                  {m.membership_expiry_status === "expired" ? `Lapsed ${fmt(m.membership_paid_until)}` : `Paid to ${fmt(m.membership_paid_until)}`}
                </Badge>
              )}
            </div>
          </div>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 flex-1">
          <MiniSelect label="Role" value={role} onChange={setRole} options={["member", "tutor", "committee", "admin"]} testid={`member-role-${m.id}`} />
          <MiniSelect label="Status" value={status} onChange={setStatus} options={["associate", "full", "committee", "lapsed"]} testid={`member-status-${m.id}`} />
          <div>
            <Label className="text-xs text-slate-400">Assigned Tutor</Label>
            <Select value={tutor || "none"} onValueChange={(v) => setTutor(v === "none" ? "" : v)}>
              <SelectTrigger className="h-9" data-testid={`member-tutor-${m.id}`}><SelectValue placeholder="Unassigned" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="none">Unassigned</SelectItem>
                {tutor && !tutors.some((t) => t.name === tutor) && <SelectItem value={tutor}>{tutor}</SelectItem>}
                {tutors.map((tu) => {
                  const load = tutorLoads[tu.name];
                  const full = load && load.count >= load.capacity;
                  return <SelectItem key={tu.id} value={tu.name}>{tu.name}{load ? ` (${load.count}/${load.capacity})${full ? " — full" : ""}` : ""}</SelectItem>;
                })}
              </SelectContent>
            </Select>
            {(() => {
              const load = tutor && tutorLoads[tutor];
              if (!load || load.count < load.capacity) return null;
              const isChange = tutor !== (m.tutor_assigned || "");
              return <p className={`mt-1 text-xs font-medium ${isChange ? "text-red-600" : "text-amber-600"}`} data-testid={`tutor-capacity-warning-${m.id}`}>⚠ {tutor} is {isChange ? "at" : "over"} capacity ({load.count}/{load.capacity})</p>;
            })()}
          </div>
        </div>
        <div className="flex gap-2 shrink-0">
          <MemberDialog m={m} canEmergency={canEmergency} roleNames={roleNames} />
          <ResetPwDialog m={m} />
          {m.role === "member" && (
            <Button variant="outline" size="icon" onClick={resendInvite} className="border-blue-400 text-blue-600 hover:bg-blue-50" title="Resend set-password invite email" data-testid={`resend-invite-${m.id}`}><UserPlus className="h-4 w-4" /></Button>
          )}
          <Button variant="outline" size="icon" onClick={sendRenewal} className="border-amber-500 text-amber-600 hover:bg-amber-50" title={`Send ${t("fee.renewal", "£20")} renewal request`} data-testid={`send-renewal-${m.id}`}><Send className="h-4 w-4" /></Button>
          <GradeManager member={m} history={history} onChanged={onChanged} />
          {!m.is_owner && !m.is_deleted && <Button variant="outline" size="icon" onClick={deleteMember} className="border-red-400 text-red-600 hover:bg-red-50" title="Delete / anonymise member" data-testid={`delete-member-${m.id}`}><Trash2 className="h-4 w-4" /></Button>}
          <Button disabled={!dirty} onClick={save} className="bg-[#0F2744] hover:bg-[#0B192C]" data-testid={`save-member-${m.id}`}>Save</Button>
        </div>
      </div>
      <div className="mt-4 pt-4 border-t border-slate-100 grid sm:grid-cols-2 gap-4">
        <div>
          <Label className="text-xs text-slate-400 block mb-1.5">Full name</Label>
          <Input value={name} onChange={(e) => setName(e.target.value)} className="h-9" data-testid={`member-name-${m.id}`} />
        </div>
        <div>
          <Label className="text-xs text-slate-400 block mb-1.5">Phone</Label>
          <Input value={phone} onChange={(e) => setPhone(e.target.value)} className="h-9" placeholder="07700 900000" data-testid={`member-phone-${m.id}`} />
        </div>
      </div>
      <div className="mt-4 grid sm:grid-cols-2 gap-4">
        <div>
          <Label className="text-xs text-slate-400 block mb-1.5">Preferred training days</Label>
          <DaysPicker value={preferredDays} onChange={setPreferredDays} testid={`member-days-${m.id}`} />
        </div>
        <div>
          <label className="flex items-center gap-2 text-sm font-semibold text-[#0F2744] mb-1.5">
            <input type="checkbox" checked={isTutor} onChange={(e) => setIsTutor(e.target.checked)} data-testid={`member-istutor-${m.id}`} /> Available as a tutor
          </label>
          {isTutor && <DaysPicker value={tutorDays} onChange={setTutorDays} testid={`member-tutordays-${m.id}`} />}
        </div>
      </div>
      {!isTutor && preferredDays.length > 0 && (
        <div className="mt-3 text-xs text-slate-500" data-testid={`member-matches-${m.id}`}>
          <span className="font-semibold text-[#0F2744]">Matching tutors:</span> {(() => {
            const matches = tutors.filter((tu) => tu.id !== m.id && (tu.tutor_days || []).some((d) => preferredDays.includes(d)));
            return matches.length ? matches.map((tu) => `${tu.name} (${(tu.tutor_days || []).filter((d) => preferredDays.includes(d)).join("/")})`).join(", ") : "none with overlapping days yet";
          })()}
        </div>
      )}
    </div>
  );
}

function GradeManager({ member, history, onChanged }) {
  const [open, setOpen] = useState(false);
  const [grade, setGrade] = useState("bronze");
  const [date, setDate] = useState("");
  const [discipline, setDiscipline] = useState(member.pathway === "driver" ? "car" : "bike");
  const [saving, setSaving] = useState(false);

  const add = async () => {
    if (!date) { toast.error("Please choose the pass date."); return; }
    setSaving(true);
    try { await api.post(`/admin/members/${member.id}/grades`, { grade, date, discipline }); toast.success("Grade pass recorded"); setDate(""); onChanged(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const remove = async (entryId) => {
    try { await api.delete(`/admin/members/${member.id}/grades/${entryId}`); toast.success("Removed"); onChanged(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" className="border-amber-500 text-amber-600 hover:bg-amber-50" data-testid={`grades-btn-${member.id}`}>
          <RefreshCw className="h-4 w-4 mr-1.5" /> Grades
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle>Grades & re-certification — {member.name}</DialogTitle></DialogHeader>
        <div className="rounded-xl bg-slate-50 border border-slate-200 p-4 mb-4 text-sm">
          <div>Last passed: <strong>{fmt(member.date_passed)}</strong></div>
          <div>Re-certification due (3 yrs): <strong>{fmt(member.recert_date)}</strong> {member.days_to_recert != null && member.recert_date && `(${member.days_to_recert < 0 ? `${Math.abs(member.days_to_recert)} days overdue` : `${member.days_to_recert} days left`})`}</div>
        </div>
        <Label className="mb-2 block text-slate-700 font-semibold">Grade history</Label>
        <div className="space-y-2 mb-5">
          {history.length === 0 && <p className="text-sm text-slate-500">No grades recorded.</p>}
          {history.map((h) => (
            <div key={h.id} className="flex items-center justify-between rounded-lg border border-slate-200 p-3" data-testid={`admin-grade-${h.id}`}>
              <div className="text-sm"><span className="font-bold capitalize text-[#0F2744]">{h.grade}</span>{h.discipline ? <span className="text-slate-500"> · {h.discipline}</span> : ""} · passed {fmt(h.date)}</div>
              <Button onClick={() => remove(h.id)} size="icon" variant="ghost" className="text-slate-400 hover:text-red-600" data-testid={`del-grade-${h.id}`}><Trash2 className="h-4 w-4" /></Button>
            </div>
          ))}
        </div>
        <Label className="mb-2 block text-slate-700 font-semibold">Record a new pass</Label>
        <div className="flex flex-col sm:flex-row gap-3">
          <Select value={discipline} onValueChange={setDiscipline}>
            <SelectTrigger className="capitalize sm:w-28" data-testid={`new-grade-discipline-${member.id}`}><SelectValue /></SelectTrigger>
            <SelectContent>{["bike", "car"].map((d) => <SelectItem key={d} value={d} className="capitalize">{d}</SelectItem>)}</SelectContent>
          </Select>
          <Select value={grade} onValueChange={setGrade}>
            <SelectTrigger className="capitalize" data-testid={`new-grade-select-${member.id}`}><SelectValue /></SelectTrigger>
            <SelectContent>{["bronze", "silver", "gold"].map((g) => <SelectItem key={g} value={g} className="capitalize">{g}</SelectItem>)}</SelectContent>
          </Select>
          <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} data-testid={`new-grade-date-${member.id}`} />
          <Button onClick={add} disabled={saving} className="bg-[#0F2744] hover:bg-[#0B192C] shrink-0" data-testid={`add-grade-${member.id}`}>
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Add pass"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function MiniSelect({ label, value, onChange, options, testid }) {
  return (
    <div>
      <Label className="text-xs text-slate-400">{label}</Label>
      <Select value={value} onValueChange={onChange}>
        <SelectTrigger className="h-9 capitalize" data-testid={testid}><SelectValue /></SelectTrigger>
        <SelectContent>{options.map((o) => <SelectItem key={o} value={o} className="capitalize">{o}</SelectItem>)}</SelectContent>
      </Select>
    </div>
  );
}

function AttendanceDialog({ event }) {
  const [open, setOpen] = useState(false);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);

  const load = () => {
    setLoading(true);
    api.get(`/admin/bookings/${event.id}`).then(({ data }) => setRows(data)).catch(() => {}).finally(() => setLoading(false));
  };
  useEffect(() => { if (open) load(); /* eslint-disable-next-line */ }, [open]);

  const toggle = async (b) => {
    try {
      await api.patch(`/admin/bookings/${b.id}/attendance`, { attended: !b.attended });
      setRows((rs) => rs.map((r) => (r.id === b.id ? { ...r, attended: !r.attended } : r)));
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const exportCsv = async () => {
    try {
      const res = await api.get(`/admin/events/${event.id}/attendance.csv`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `attendance_${event.title.replace(/\s/g, "_")}.csv`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch { toast.error("Could not export the register."); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button size="icon" variant="ghost" className="text-slate-400 hover:text-[#0F2744]" data-testid={`attendance-btn-${event.id}`}><ClipboardList className="h-4 w-4" /></Button>
      </DialogTrigger>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle>Attendance — {event.title}</DialogTitle></DialogHeader>
        {loading ? (
          <div className="flex justify-center py-8"><Loader2 className="h-6 w-6 animate-spin text-[#0F2744]" /></div>
        ) : rows.length === 0 ? (
          <p className="text-slate-500 py-6 text-center">No bookings for this event yet.</p>
        ) : (
          <div className="space-y-2">
            {rows.map((b) => (
              <label key={b.id} className="flex items-center justify-between rounded-lg border border-slate-200 p-3 cursor-pointer" data-testid={`attendance-row-${b.id}`}>
                <div className="min-w-0">
                  <div className="font-semibold text-[#0F2744] truncate">{b.user_name}</div>
                  <div className="text-xs text-slate-500 truncate">{b.user_email} · {b.status}</div>
                </div>
                <input
                  type="checkbox"
                  checked={!!b.attended}
                  onChange={() => toggle(b)}
                  className="h-5 w-5 accent-[#0F2744] shrink-0"
                  data-testid={`attendance-check-${b.id}`}
                />
              </label>
            ))}
          </div>
        )}
        <DialogFooter>
          <Button onClick={exportCsv} disabled={rows.length === 0} className="bg-amber-500 hover:bg-amber-600 text-[#0F2744] font-bold" data-testid={`export-attendance-${event.id}`}>
            <Download className="h-4 w-4 mr-2" /> Export CSV
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function DocumentsPanel({ documents, onChanged, onDelete }) {
  const [open, setOpen] = useState(false);
  const [file, setFile] = useState(null);
  const [title, setTitle] = useState("");
  const [category, setCategory] = useState("Governance");
  const [description, setDescription] = useState("");
  const [uploading, setUploading] = useState(false);

  const upload = async () => {
    if (!file || !title) { toast.error("Please choose a file and enter a title."); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("title", title);
      fd.append("category", category);
      fd.append("description", description);
      await api.post("/admin/documents/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Document uploaded");
      setOpen(false); setFile(null); setTitle(""); setDescription("");
      onChanged();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setUploading(false); }
  };

  const download = async (d) => {
    try {
      const res = await api.get(`/documents/${d.id}/download`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = d.original_filename || `${d.title}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch { toast.error("Download unavailable."); }
  };

  return (
    <div>
      <div className="flex justify-end mb-4">
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button className="bg-amber-500 hover:bg-amber-600 text-[#0F2744] font-bold" data-testid="upload-doc-btn"><Upload className="h-4 w-4 mr-1.5" /> Upload document</Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>Upload a document</DialogTitle></DialogHeader>
            <div className="space-y-4">
              <div>
                <Label className="mb-1.5 block text-slate-700">File (PDF, DOCX…)</Label>
                <Input type="file" accept=".pdf,.doc,.docx,.txt,.csv,.xlsx" onChange={(e) => setFile(e.target.files?.[0] || null)} data-testid="doc-file-input" />
              </div>
              <div><Label className="mb-1.5 block text-slate-700">Title</Label><Input value={title} onChange={(e) => setTitle(e.target.value)} data-testid="doc-title-input" /></div>
              <div>
                <Label className="mb-1.5 block text-slate-700">Category</Label>
                <Select value={category} onValueChange={setCategory}>
                  <SelectTrigger data-testid="doc-category-select"><SelectValue /></SelectTrigger>
                  <SelectContent>{["Governance", "Training", "Safeguarding", "Forms", "General"].map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label className="mb-1.5 block text-slate-700">Description</Label><Textarea rows={2} value={description} onChange={(e) => setDescription(e.target.value)} data-testid="doc-desc-input" /></div>
            </div>
            <DialogFooter>
              <Button onClick={upload} disabled={uploading} className="bg-[#0F2744] hover:bg-[#0B192C]" data-testid="save-doc-btn">
                {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Upload"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <div className="space-y-3">
        {documents.length === 0 && <Empty text="No documents yet. Upload the constitution, handbooks and forms." />}
        {documents.map((d) => (
          <div key={d.id} className="rounded-2xl bg-white border border-slate-200 p-5 flex items-center justify-between gap-4" data-testid={`admin-doc-${d.id}`}>
            <div className="flex items-center gap-4 min-w-0">
              <div className="h-11 w-11 rounded-xl bg-blue-100 flex items-center justify-center shrink-0"><FileText className="h-6 w-6 text-blue-600" /></div>
              <div className="min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <h3 className="font-bold text-[#0F2744] truncate">{d.title}</h3>
                  <Badge variant="outline" className="text-xs">{d.category}</Badge>
                </div>
                <p className="text-sm text-slate-500 truncate">{d.original_filename || d.description}</p>
              </div>
            </div>
            <div className="flex gap-1 shrink-0">
              {d.storage_path && <Button onClick={() => download(d)} size="icon" variant="ghost" className="text-slate-400 hover:text-[#0F2744]" data-testid={`download-admin-doc-${d.id}`}><Download className="h-4 w-4" /></Button>}
              <Button onClick={() => onDelete(d.id)} size="icon" variant="ghost" className="text-slate-400 hover:text-red-600" data-testid={`del-doc-${d.id}`}><Trash2 className="h-4 w-4" /></Button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function ImageField({ value, onChange, testId, aspect = 16 / 9 }) {
  const [busy, setBusy] = useState(false);
  const [pending, setPending] = useState(null);
  const [galOpen, setGalOpen] = useState(false);
  const [gallery, setGallery] = useState([]);
  const upload = async (file) => {
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/admin/images/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      onChange(`${BACKEND}${data.path}`);
      toast.success("Image uploaded");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  useEffect(() => { if (galOpen && gallery.length === 0) api.get("/gallery").then(({ data }) => setGallery(data)).catch(() => {}); }, [galOpen, gallery.length]);
  return (
    <FI label="Image">
      <ImageCropDialog file={pending} defaultAspect={aspect} onCancel={() => setPending(null)} onDone={(f) => { setPending(null); upload(f); }} />
      <div className="flex items-center gap-3">
        <div className="h-16 w-24 rounded-lg bg-slate-100 border border-slate-200 overflow-hidden shrink-0 flex items-center justify-center">
          {value ? <img src={value} alt="" className="h-full w-full object-cover" data-testid={`${testId}-preview`} /> : <ImageIcon className="h-5 w-5 text-slate-400" />}
        </div>
        <div className="flex-1 space-y-2 min-w-0">
          <div className="flex gap-2 flex-wrap">
            <label className="inline-flex items-center gap-1.5 rounded-lg bg-[#0F2744] text-white text-sm font-semibold px-3 py-2 cursor-pointer hover:bg-[#0B192C]">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />} Upload
              <input type="file" accept="image/*" className="hidden" onChange={(e) => { setPending(e.target.files[0] || null); e.target.value = ""; }} data-testid={`${testId}-upload`} />
            </label>
            <Dialog open={galOpen} onOpenChange={setGalOpen}>
              <DialogTrigger asChild><Button type="button" variant="outline" size="sm" className="h-auto" data-testid={`${testId}-gallery`}><ImageIcon className="h-4 w-4 mr-1.5" /> Gallery</Button></DialogTrigger>
              <DialogContent className="max-w-lg max-h-[80vh] overflow-y-auto">
                <DialogHeader><DialogTitle>Choose from the website gallery</DialogTitle></DialogHeader>
                <div className="grid grid-cols-3 gap-2">
                  {gallery.map((g) => (
                    <button key={g.id} type="button" onClick={() => { onChange(g.url); setGalOpen(false); }} className="rounded-lg overflow-hidden border border-slate-200 hover:ring-2 ring-amber-500" data-testid={`${testId}-gallery-${g.id}`}>
                      <img src={g.url} alt={g.caption || ""} className="h-24 w-full object-cover" />
                    </button>
                  ))}
                </div>
                {gallery.length === 0 && <p className="text-sm text-slate-400">No gallery images yet.</p>}
              </DialogContent>
            </Dialog>
            {value && <Button type="button" variant="ghost" size="sm" className="text-slate-500" onClick={() => onChange("")} data-testid={`${testId}-clear`}>Remove</Button>}
          </div>
          <Input value={value} onChange={(e) => onChange(e.target.value)} placeholder="…or paste an image URL" className="text-xs" data-testid={testId} />
        </div>
      </div>
    </FI>
  );
}


const emptyEvent = { title: "", category: "Group Ride", description: "", date: "", time: "", location: "", capacity: 20, price: "Free", image_url: "", visibility: "private", published: true };

function EventDialog({ event, onSaved }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(event || emptyEvent);
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      const payload = { ...form, capacity: Number(form.capacity) };
      if (event) await api.put(`/admin/events/${event.id}`, payload);
      else await api.post("/admin/events", payload);
      toast.success(event ? "Event updated" : "Event created");
      setOpen(false); onSaved();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        {event
          ? <Button size="icon" variant="ghost" className="text-slate-400 hover:text-[#0F2744]" data-testid={`edit-event-${event.id}`}><Pencil className="h-4 w-4" /></Button>
          : <Button className="bg-amber-500 hover:bg-amber-600 text-[#0F2744] font-bold" data-testid="new-event-btn"><Plus className="h-4 w-4 mr-1.5" /> New event</Button>}
      </DialogTrigger>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle>{event ? "Edit event" : "New event"}</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <FI label="Title"><Input value={form.title} onChange={(e) => set("title", e.target.value)} data-testid="event-title" /></FI>
          <div className="grid grid-cols-2 gap-3">
            <FI label="Category">
              <Select value={form.category} onValueChange={(v) => set("category", v)}>
                <SelectTrigger data-testid="event-category"><SelectValue /></SelectTrigger>
                <SelectContent>{["Group Ride", "Driver Training", "Social", "Tutor Workshop"].map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
              </Select>
            </FI>
            <FI label="Price"><Input value={form.price} onChange={(e) => set("price", e.target.value)} data-testid="event-price" /></FI>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <FI label="Date"><Input type="date" value={form.date} onChange={(e) => set("date", e.target.value)} data-testid="event-date" /></FI>
            <FI label="Time"><Input value={form.time} onChange={(e) => set("time", e.target.value)} placeholder="09:00" data-testid="event-time" /></FI>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <FI label="Location"><Input value={form.location} onChange={(e) => set("location", e.target.value)} data-testid="event-location" /></FI>
            <FI label="Capacity"><Input type="number" value={form.capacity} onChange={(e) => set("capacity", e.target.value)} data-testid="event-capacity" /></FI>
          </div>
          <ImageField value={form.image_url} onChange={(v) => set("image_url", v)} testId="event-image" />
          <FI label="Visibility on the public website">
            <Select value={form.visibility || "private"} onValueChange={(v) => set("visibility", v)}>
              <SelectTrigger data-testid="event-visibility"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="public">Public — full details shown to everyone</SelectItem>
                <SelectItem value="private">Members only — public sees title, description &amp; date</SelectItem>
              </SelectContent>
            </Select>
          </FI>
          <FI label="Description"><Textarea rows={3} value={form.description} onChange={(e) => set("description", e.target.value)} data-testid="event-description" /></FI>
        </div>
        <DialogFooter><Button onClick={save} disabled={saving} className="bg-[#0F2744] hover:bg-[#0B192C]" data-testid="save-event-btn">{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save event"}</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

const emptyNews = { title: "", excerpt: "", body: "", image_url: "", author: "Committee", published: true, publish_from: "", publish_until: "" };

function NewsDialog({ article, onSaved }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(article || emptyNews);
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      if (article) await api.put(`/admin/news/${article.id}`, form);
      else await api.post("/admin/news", form);
      toast.success(article ? "Article updated" : "Article published");
      setOpen(false); onSaved();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        {article
          ? <Button size="icon" variant="ghost" className="text-slate-400 hover:text-[#0F2744]" data-testid={`edit-news-${article.id}`}><Pencil className="h-4 w-4" /></Button>
          : <Button className="bg-amber-500 hover:bg-amber-600 text-[#0F2744] font-bold" data-testid="new-news-btn"><Plus className="h-4 w-4 mr-1.5" /> New article</Button>}
      </DialogTrigger>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle>{article ? "Edit article" : "New article"}</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <FI label="Title"><Input value={form.title} onChange={(e) => set("title", e.target.value)} data-testid="news-title" /></FI>
          <ImageField value={form.image_url} onChange={(v) => set("image_url", v)} testId="news-image" />
          <FI label="Excerpt"><Textarea rows={2} value={form.excerpt} onChange={(e) => set("excerpt", e.target.value)} data-testid="news-excerpt" /></FI>
          <FI label="Body"><Textarea rows={6} value={form.body} onChange={(e) => set("body", e.target.value)} data-testid="news-body" /></FI>
          <div className="grid grid-cols-2 gap-3">
            <FI label="Publish from (optional)"><Input type="date" value={form.publish_from || ""} onChange={(e) => set("publish_from", e.target.value)} data-testid="news-publish-from" /></FI>
            <FI label="Publish until (optional)"><Input type="date" value={form.publish_until || ""} onChange={(e) => set("publish_until", e.target.value)} data-testid="news-publish-until" /></FI>
          </div>
          <p className="text-xs text-slate-400 -mt-1">Leave dates blank to show immediately and indefinitely. The article only appears on the public site within this window.</p>
        </div>
        <DialogFooter><Button onClick={save} disabled={saving} className="bg-[#0F2744] hover:bg-[#0B192C]" data-testid="save-news-btn">{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save article"}</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

const GALLERY_PRESETS = ["Riders", "Drivers", "Training", "Events", "Social", "Awards", "General"];

function CategoryField({ value, onChange, categories, testId }) {
  const opts = Array.from(new Set([...GALLERY_PRESETS, ...categories]));
  const [adding, setAdding] = useState(!!value && !opts.includes(value));
  if (adding) {
    return (
      <div className="flex gap-2">
        <Input autoFocus value={value} onChange={(e) => onChange(e.target.value)} placeholder="Type a new category" data-testid={testId} />
        <Button type="button" variant="outline" onClick={() => { setAdding(false); onChange("General"); }} data-testid={`${testId}-pick`}>Pick existing</Button>
      </div>
    );
  }
  return (
    <Select value={opts.includes(value) ? value : ""} onValueChange={(v) => { if (v === "__new__") { onChange(""); setAdding(true); } else onChange(v); }}>
      <SelectTrigger data-testid={testId}><SelectValue placeholder="Choose a category" /></SelectTrigger>
      <SelectContent>
        {opts.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
        <SelectItem value="__new__" className="text-amber-700 font-semibold">➕ Add new category…</SelectItem>
      </SelectContent>
    </Select>
  );
}

function GalleryDialog({ onSaved, categories = [] }) {
  const [open, setOpen] = useState(false);
  const [urls, setUrls] = useState([]);
  const [caption, setCaption] = useState("");
  const [category, setCategory] = useState("General");
  const [urlInput, setUrlInput] = useState("");
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);

  const reset = () => { setUrls([]); setCaption(""); setCategory("General"); setUrlInput(""); };

  const uploadFiles = async (e) => {
    const files = Array.from(e.target.files || []); e.target.value = "";
    if (!files.length) return;
    setUploading(true);
    try {
      const out = [];
      for (const f of files) {
        const fd = new FormData(); fd.append("file", f);
        const { data } = await api.post("/admin/images/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        out.push({ url: data.path, kind: "photo" });
      }
      setUrls((u) => [...u, ...out]);
      toast.success(`${out.length} photo${out.length > 1 ? "s" : ""} ready`);
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setUploading(false); }
  };
  const uploadVideo = async (e) => {
    const f = e.target.files?.[0]; e.target.value = ""; if (!f) return;
    setUploading(true);
    try { const fd = new FormData(); fd.append("file", f); const { data } = await api.post("/admin/media/upload", fd, { headers: { "Content-Type": "multipart/form-data" } }); setUrls((u) => [...u, { url: data.path, kind: "video" }]); toast.success("Video ready"); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setUploading(false); }
  };
  const addUrl = () => { if (urlInput.trim()) { const isVid = /(youtube|youtu\.be|vimeo|\.mp4|\.webm)/i.test(urlInput); setUrls((u) => [...u, { url: urlInput.trim(), kind: isVid ? "video" : "photo" }]); setUrlInput(""); } };
  const removeUrl = (i) => setUrls((u) => u.filter((_, idx) => idx !== i));

  const save = async () => {
    if (!urls.length) { toast.error("Add at least one photo."); return; }
    setSaving(true);
    try {
      const cat = category.trim() || "General";
      await api.post("/admin/gallery/bulk", { items: urls.map((it) => ({ url: it.url, caption, category: cat, type: it.kind })) });
      toast.success(`${urls.length} image${urls.length > 1 ? "s" : ""} added`);
      setOpen(false); reset(); onSaved();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) reset(); }}>
      <DialogTrigger asChild><Button className="bg-amber-500 hover:bg-amber-600 text-[#0F2744] font-bold" data-testid="new-gallery-btn"><Plus className="h-4 w-4 mr-1.5" /> Add images</Button></DialogTrigger>
      <DialogContent className="max-w-lg">
        <DialogHeader><DialogTitle>Add gallery images</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <label className="block border-2 border-dashed border-slate-300 rounded-xl p-6 text-center cursor-pointer hover:border-amber-500 transition-colors">
            <input type="file" accept="image/*" multiple className="hidden" onChange={uploadFiles} data-testid="gallery-files" />
            {uploading ? <Loader2 className="h-6 w-6 animate-spin mx-auto text-[#0F2744]" />
              : <><Upload className="h-6 w-6 mx-auto text-slate-400" /><div className="mt-2 text-sm font-semibold text-[#0F2744]">Click to upload photos</div><div className="text-xs text-slate-400">You can select several at once</div></>}
          </label>
          <label className="block border-2 border-dashed border-slate-300 rounded-xl p-3 text-center cursor-pointer hover:border-amber-500 transition-colors">
            <input type="file" accept="video/*" className="hidden" onChange={uploadVideo} data-testid="gallery-video-file" />
            <span className="inline-flex items-center gap-1.5 text-sm font-semibold text-[#0F2744]"><Upload className="h-4 w-4" /> Upload a video</span>
          </label>
          <div className="flex gap-2">
            <Input value={urlInput} onChange={(e) => setUrlInput(e.target.value)} onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), addUrl())} placeholder="…or paste an image or YouTube/Vimeo link" className="h-9" data-testid="gallery-url-input" />
            <Button type="button" variant="outline" onClick={addUrl} data-testid="gallery-add-url">Add</Button>
          </div>
          {urls.length > 0 && (
            <div className="grid grid-cols-4 gap-2" data-testid="gallery-queue">
              {urls.map((it, i) => (
                <div key={i} className="relative">
                  {it.kind === "video"
                    ? <div className="h-16 w-full rounded-md border border-slate-200 bg-[#0F2744] flex items-center justify-center text-white text-[10px] font-bold">VIDEO</div>
                    : <img src={it.url} alt="" className="h-16 w-full object-cover rounded-md border border-slate-200" />}
                  <button onClick={() => removeUrl(i)} className="absolute -top-1.5 -right-1.5 h-5 w-5 rounded-full bg-red-600 text-white flex items-center justify-center shadow" data-testid={`gallery-queue-remove-${i}`}><X className="h-3 w-3" /></button>
                </div>
              ))}
            </div>
          )}
          <FI label="Caption (optional — applied to all)"><Input value={caption} onChange={(e) => setCaption(e.target.value)} data-testid="gallery-caption" /></FI>
          <FI label="Category"><CategoryField value={category} onChange={setCategory} categories={categories} testId="gallery-category" /></FI>
        </div>
        <DialogFooter><Button onClick={save} disabled={saving || uploading || !urls.length} className="bg-[#0F2744] hover:bg-[#0B192C]" data-testid="save-gallery-btn">{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : `Add ${urls.length || ""} image${urls.length === 1 ? "" : "s"}`.trim()}</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function GalleryEditDialog({ item, onSaved, categories = [] }) {
  const [open, setOpen] = useState(false);
  const [caption, setCaption] = useState(item.caption || "");
  const [category, setCategory] = useState(item.category || "General");
  const [saving, setSaving] = useState(false);

  useEffect(() => { if (open) { setCaption(item.caption || ""); setCategory(item.category || "General"); } }, [open, item]);

  const save = async () => {
    setSaving(true);
    try { await api.patch(`/admin/gallery/${item.id}`, { caption, category: category.trim() || "General" }); toast.success("Photo updated"); setOpen(false); onSaved(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <button className="h-8 w-8 rounded-full bg-white/90 text-[#0F2744] flex items-center justify-center hover:bg-white" data-testid={`edit-gallery-${item.id}`}><Pencil className="h-4 w-4" /></button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader><DialogTitle>Edit photo details</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <img src={item.url} alt={caption} className="w-full h-40 object-cover rounded-lg" />
          <FI label="Caption"><Input value={caption} onChange={(e) => setCaption(e.target.value)} data-testid="edit-gallery-caption" /></FI>
          <FI label="Category"><CategoryField value={category} onChange={setCategory} categories={categories} testId="edit-gallery-category" /></FI>
        </div>
        <DialogFooter><Button onClick={save} disabled={saving} className="bg-[#0F2744] hover:bg-[#0B192C]" data-testid="save-gallery-edit-btn">{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save changes"}</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

const ACTIVITY_AREA_COLOR = {
  members: "bg-blue-100 text-blue-700", applications: "bg-indigo-100 text-indigo-700",
  payments: "bg-green-100 text-green-700", settings: "bg-slate-200 text-slate-700",
  admins: "bg-red-100 text-red-700", gallery: "bg-amber-100 text-amber-700",
  events: "bg-purple-100 text-purple-700", news: "bg-pink-100 text-pink-700",
};

function ActivityPanel() {
  const [rows, setRows] = useState(null);
  const [q, setQ] = useState("");
  const [area, setArea] = useState("all");
  const load = () => api.get("/admin/activity").then(({ data }) => setRows(data)).catch(() => setRows([]));
  useEffect(() => { load(); }, []);

  if (rows === null) return <div className="flex justify-center py-12"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div>;

  const areas = Array.from(new Set(rows.map((r) => r.area).filter(Boolean))).sort();
  const ql = q.trim().toLowerCase();
  const filtered = rows.filter((r) =>
    (area === "all" || r.area === area) &&
    (!ql || [r.actor_name, r.action, r.detail, r.actor_email].some((f) => (f || "").toLowerCase().includes(ql)))
  );

  return (
    <div className="rounded-2xl bg-white border border-slate-200 p-4 sm:p-6" data-testid="activity-panel">
      <div className="flex items-center justify-between mb-4 gap-3 flex-wrap">
        <div>
          <h2 className="text-lg font-bold text-[#0F2744] flex items-center gap-2"><History className="h-5 w-5" /> Activity log</h2>
          <p className="text-xs text-slate-500 mt-0.5">A record of who changed what in the admin console, most recent first.</p>
        </div>
        <Button variant="outline" size="sm" onClick={load} data-testid="activity-refresh"><RefreshCw className="h-4 w-4 mr-1.5" /> Refresh</Button>
      </div>
      <div className="flex flex-col sm:flex-row gap-2 mb-4">
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search by person or what changed…" className="h-9 sm:max-w-xs" data-testid="activity-search" />
        <Select value={area} onValueChange={setArea}>
          <SelectTrigger className="h-9 sm:w-48" data-testid="activity-area-filter"><SelectValue placeholder="All areas" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All areas</SelectItem>
            {areas.map((ar) => <SelectItem key={ar} value={ar} className="capitalize">{ar}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
      {rows.length === 0 ? (
        <p className="text-sm text-slate-500 py-8 text-center">No activity recorded yet.</p>
      ) : filtered.length === 0 ? (
        <p className="text-sm text-slate-500 py-8 text-center" data-testid="activity-no-match">No activity matches your search.</p>
      ) : (
        <div className="divide-y divide-slate-100">
          {filtered.map((r) => (
            <div key={r.id} className="flex items-start gap-3 py-3" data-testid={`activity-row-${r.id}`}>
              <span className={`mt-0.5 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wide shrink-0 ${ACTIVITY_AREA_COLOR[r.area] || "bg-slate-100 text-slate-600"}`}>{r.area}</span>
              <div className="min-w-0 flex-1">
                <div className="text-sm text-[#0F2744] font-medium">{r.action}</div>
                {r.detail && <div className="text-xs text-slate-600 mt-0.5" data-testid={`activity-detail-${r.id}`}>{r.detail}</div>}
                <div className="text-xs text-slate-500">by {r.actor_name}</div>
              </div>
              <div className="text-xs text-slate-400 shrink-0 text-right">{fmt(r.created_at)}<br />{new Date(r.created_at).toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" })}</div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function TemplateRow({ t, functions, onChanged }) {
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState(t.name);
  const [perms, setPerms] = useState(t.permissions || []);
  const [saving, setSaving] = useState(false);
  const label = (k) => k === "pii" ? "View contact details (address/phone)" : (k.charAt(0).toUpperCase() + k.slice(1)).replace(/_/g, " ");
  const toggle = (k) => setPerms((p) => (p.includes(k) ? p.filter((x) => x !== k) : [...p, k]));
  const save = async () => {
    if (!name.trim()) { toast.error("Give the template a name"); return; }
    setSaving(true);
    try { await api.patch(`/admin/role-templates/${t.id}`, { name, permissions: perms }); toast.success("Template updated"); setEditing(false); onChanged(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };
  const del = async () => {
    if (!window.confirm(`Delete template "${t.name}"?`)) return;
    try { await api.delete(`/admin/role-templates/${t.id}`); toast.success("Template deleted"); onChanged(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  if (!editing) {
    return (
      <div className="flex items-center justify-between gap-3 rounded-xl border border-slate-100 bg-slate-50 px-4 py-2.5" data-testid={`template-row-${t.id}`}>
        <div className="min-w-0"><div className="text-sm font-semibold text-[#0F2744]">{t.name}</div><div className="text-xs text-slate-500 truncate">{(t.permissions || []).map(label).join(", ") || "No functions"}</div></div>
        <div className="flex items-center gap-1 shrink-0">
          <Button variant="ghost" size="icon" onClick={() => { setName(t.name); setPerms(t.permissions || []); setEditing(true); }} className="text-slate-400 hover:text-[#0F2744]" data-testid={`edit-template-${t.id}`}><Pencil className="h-4 w-4" /></Button>
          <Button variant="ghost" size="icon" onClick={del} className="text-slate-400 hover:text-red-600" data-testid={`delete-template-${t.id}`}><Trash2 className="h-4 w-4" /></Button>
        </div>
      </div>
    );
  }
  return (
    <div className="rounded-xl border-2 border-[#0F2744]/30 bg-white px-4 py-3" data-testid={`template-edit-${t.id}`}>
      <Input value={name} onChange={(e) => setName(e.target.value)} className="h-9 mb-2 max-w-sm" data-testid={`template-name-${t.id}`} />
      <div className="flex flex-wrap gap-1.5">
        {functions.map((k) => (
          <button key={k} type="button" onClick={() => toggle(k)} data-testid={`template-perm-${t.id}-${k}`}
            className={`px-3 py-1.5 rounded-full text-xs font-semibold border ${perms.includes(k) ? "bg-[#0F2744] text-white border-[#0F2744]" : "bg-white text-slate-600 border-slate-200"}`}>{label(k)}</button>
        ))}
      </div>
      <div className="mt-3 flex gap-2">
        <Button onClick={save} disabled={saving} className="bg-[#0F2744] hover:bg-[#0B192C]" data-testid={`template-save-${t.id}`}>{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save"}</Button>
        <Button variant="outline" onClick={() => setEditing(false)} data-testid={`template-cancel-${t.id}`}>Cancel</Button>
      </div>
    </div>
  );
}

function BulkResendButton() {
  const [busy, setBusy] = useState(false);
  const run = async () => {
    if (!window.confirm("Resend the set-password invite to all members who haven't set their password yet?")) return;
    setBusy(true);
    try { const { data } = await api.post("/admin/members/resend-invite-bulk"); toast.success(`Invite re-sent to ${data.sent} member${data.sent === 1 ? "" : "s"}`); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  return (
    <Button variant="outline" onClick={run} disabled={busy} className="border-blue-400 text-blue-600 hover:bg-blue-50" data-testid="bulk-resend-invite">
      {busy ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : <UserPlus className="h-4 w-4 mr-1.5" />} Resend invites to all un-activated
    </Button>
  );
}

function AwaitingTutor({ members, tutors = [], onChanged }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState("");
  const list = (members || []).filter((m) => m.role === "member" && ["full", "associate"].includes(m.member_status) && !(m.tutor_assigned || "").trim());
  const assign = async (m, tutorName) => {
    setBusy(m.id);
    try { await api.patch(`/admin/members/${m.id}`, { tutor_assigned: tutorName }); toast.success(`${m.name} allocated to ${tutorName}`); onChanged && onChanged(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(""); }
  };
  if (list.length === 0) return null;
  return (
    <div className="rounded-2xl bg-white border border-amber-200 p-4" data-testid="awaiting-tutor-panel">
      <button type="button" onClick={() => setOpen(!open)} className="w-full flex items-center justify-between" data-testid="awaiting-tutor-toggle">
        <h3 className="font-bold text-[#0F2744] flex items-center gap-2"><UserPlus className="h-5 w-5 text-amber-500" /> Awaiting tutor allocation <span className="text-amber-700">({list.length})</span></h3>
        <ChevronDown className={`h-5 w-5 text-slate-400 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="mt-3 divide-y divide-slate-100">
          {list.map((m) => (
            <div key={m.id} className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 py-2.5 text-sm" data-testid={`awaiting-tutor-${m.id}`}>
              <div className="min-w-0">
                <div className="font-semibold text-[#0F2744] truncate">{m.name}</div>
                <div className="text-xs text-slate-500 truncate">{m.email} · {m.pathway || "—"}{m.approved_at ? ` · approved ${fmt(m.approved_at)}` : ""}</div>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                {m.associate_controller && m.associate_controller.done
                  ? <Badge className="bg-green-100 text-green-700 text-[10px]">Controller notified</Badge>
                  : <Badge className="bg-amber-100 text-amber-700 text-[10px]">New</Badge>}
                <Select onValueChange={(v) => assign(m, v)} disabled={busy === m.id}>
                  <SelectTrigger className="h-8 w-44 text-xs" data-testid={`allocate-tutor-${m.id}`}><SelectValue placeholder={busy === m.id ? "Saving…" : "Assign a tutor…"} /></SelectTrigger>
                  <SelectContent>
                    {tutors.length === 0 ? <div className="px-3 py-2 text-xs text-slate-400">No tutors available</div>
                      : tutors.map((t) => <SelectItem key={t.id} value={t.name}>{t.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function FI({ label, children }) {
  return <div><Label className="mb-1.5 block text-slate-700">{label}</Label>{children}</div>;
}

const GROUP_LABELS = {
  home: "Home page", riders: "Riders page", drivers: "Drivers page",
  certification: "Certification page", about: "About page", contact: "Contact page",
  join: "Join page", footer: "Footer", resources: "Resources page",
  assessment: "Free Assessment page",
};

const BG_SECTIONS = [
  { key: "home", label: "Home hero" },
  { key: "riders", label: "Riders page" },
  { key: "drivers", label: "Drivers page" },
  { key: "events", label: "Events page" },
  { key: "news", label: "News page" },
  { key: "gallery", label: "Gallery page" },
  { key: "about", label: "About page" },
  { key: "contact", label: "Contact page" },
  { key: "resources", label: "Resources page" },
  { key: "certification", label: "Certification page" },
  { key: "join", label: "Join page" },
  { key: "legal", label: "Legal pages" },
  { key: "member", label: "Member dashboard" },
  { key: "admin", label: "Admin dashboard" },
];

function BackgroundsPanel() {
  const { refresh } = useContent();
  const [content, setContent] = useState({});
  const reload = () => api.get("/content").then(({ data }) => setContent(data)).catch(() => {});
  useEffect(() => { reload(); }, []);
  return (
    <div data-testid="backgrounds-panel">
      <p className="text-sm text-slate-500 max-w-2xl mb-5">Add one or more background images per section. With more than one, they gently fade through on a loop (just like the homepage). Pick a rotation speed, use the arrows to set the order — the first image loads first — and crop/straighten each before it saves. You can add several at once. Leave empty to use the built-in default.</p>
      <div className="space-y-5">
        {BG_SECTIONS.map((s) => (
          <SectionBackgrounds key={s.key} section={s} content={content} onSaved={() => { reload(); refresh(); }} />
        ))}
      </div>
    </div>
  );
}

function SectionBackgrounds({ section, content, onSaved }) {
  const BACKEND = process.env.REACT_APP_BACKEND_URL;
  const resolve = (v) => (v && v.startsWith("/") ? `${BACKEND}${v}` : v);
  const fromContent = () => {
    const arr = content[`img.${section.key}.slides`];
    if (Array.isArray(arr)) return arr;
    if (section.key === "home") return ["img.home.slide0", "img.home.slide1", "img.home.slide2", "img.home.slide3"].map((k) => content[k]).filter(Boolean);
    const single = content[`img.${section.key}`];
    return single ? [single] : [];
  };
  const [list, setList] = useState(fromContent);
  const listRef = useRef(fromContent());
  const [busy, setBusy] = useState(false);
  const [queue, setQueue] = useState([]);
  const speed = content[`img.${section.key}.speed`] || "medium";
  useEffect(() => { const l = fromContent(); setList(l); listRef.current = l; /* eslint-disable-next-line */ }, [content]);

  const setSpeed = async (val) => {
    setBusy(true);
    try { await api.put("/admin/content", { updates: { [`img.${section.key}.speed`]: val } }); toast.success("Rotation speed updated — live now"); onSaved(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const persist = async (next) => {
    setBusy(true);
    try { await api.put("/admin/content", { updates: { [`img.${section.key}.slides`]: next } }); setList(next); listRef.current = next; toast.success("Backgrounds updated — live now"); onSaved(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const uploadCropped = async (file) => {
    setBusy(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await api.post("/admin/images/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      await persist([...listRef.current, data.path]);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); setBusy(false); }
  };
  const onCropDone = async (file) => { await uploadCropped(file); setQueue((q) => q.slice(1)); };
  const move = (i, d) => { const n = [...list]; const j = i + d; if (j < 0 || j >= n.length) return; [n[i], n[j]] = [n[j], n[i]]; persist(n); };
  const remove = (i) => persist(list.filter((_, idx) => idx !== i));

  return (
    <div className="rounded-2xl bg-white border border-slate-200 p-4" data-testid={`bg-section-${section.key}`}>
      <ImageCropDialog file={queue[0] || null} defaultAspect={21 / 9} onCancel={() => setQueue([])} onDone={onCropDone} />
      <div className="flex items-center justify-between mb-3 gap-3 flex-wrap">
        <h3 className="font-bold text-[#0F2744]">{section.label} <span className="text-xs font-normal text-slate-400">({list.length} image{list.length === 1 ? "" : "s"}{list.length > 1 ? " · rotating" : ""}{queue.length > 0 ? ` · ${queue.length} to crop` : ""})</span></h3>
        <div className="flex items-center gap-2">
          {list.length > 1 && (
            <div className="inline-flex rounded-lg border border-slate-200 overflow-hidden" data-testid={`bg-speed-${section.key}`}>
              {["slow", "medium", "fast"].map((v) => (
                <button key={v} onClick={() => setSpeed(v)} disabled={busy} className={`text-xs font-semibold py-1.5 px-2.5 capitalize ${speed === v ? "bg-[#0F2744] text-white" : "bg-white text-slate-500 hover:bg-slate-50"}`} data-testid={`bg-speed-${section.key}-${v}`}>{v}</button>
              ))}
            </div>
          )}
          <label>
            <input type="file" accept="image/*" multiple className="hidden" onChange={(e) => { const files = Array.from(e.target.files || []); if (files.length) setQueue((q) => [...q, ...files]); e.target.value = ""; }} data-testid={`bg-add-${section.key}`} />
            <span className="inline-flex items-center gap-1.5 rounded-lg bg-[#0F2744] text-white text-sm font-semibold py-1.5 px-3 cursor-pointer hover:bg-[#0B192C]">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Upload className="h-4 w-4" /> Add images</>}</span>
          </label>
        </div>
      </div>
      {list.length === 0 ? (
        <p className="text-xs text-slate-400">Using the built-in default image. Add one or more to override and rotate.</p>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
          {list.map((src, i) => (
            <div key={i} className="relative rounded-xl overflow-hidden group border border-slate-100" data-testid={`bg-slide-${section.key}-${i}`}>
              <img src={resolve(src)} alt="" className="w-full h-24 object-cover" />
              <div className="absolute inset-x-0 top-0 flex justify-between p-1">
                <span className="text-[10px] bg-black/60 text-white rounded px-1.5 py-0.5">{i === 0 ? "First" : i + 1}</span>
                <button onClick={() => remove(i)} disabled={busy} className="h-6 w-6 rounded-full bg-red-600 text-white flex items-center justify-center" data-testid={`bg-del-${section.key}-${i}`}><Trash2 className="h-3.5 w-3.5" /></button>
              </div>
              <div className="absolute inset-x-0 bottom-0 flex justify-center gap-1 p-1 bg-gradient-to-t from-black/60">
                <button onClick={() => move(i, -1)} disabled={busy || i === 0} className="h-6 w-6 rounded bg-white/90 text-[#0F2744] flex items-center justify-center disabled:opacity-30" data-testid={`bg-up-${section.key}-${i}`}><ChevronUp className="h-4 w-4" /></button>
                <button onClick={() => move(i, 1)} disabled={busy || i === list.length - 1} className="h-6 w-6 rounded bg-white/90 text-[#0F2744] flex items-center justify-center disabled:opacity-30" data-testid={`bg-down-${section.key}-${i}`}><ChevronDown className="h-4 w-4" /></button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

const ART = "https://customer-assets-39nsmqrw.emergentagent.net/job_rospa-wm-portal/artifacts/";
const INLINE_IMAGES = [
  { group: "Rider Training page", key: "img.ridertraining.hero", label: "Hero background", def: `${ART}17pxmkkt_above-the-clouds-Medium.jpeg` },
  { group: "Rider Training page", key: "img.ridertraining.groupride", label: "Observed rides photo", def: `${ART}uc3d0luq_Through-the-forest.jpeg` },
  { group: "Rider Training page", key: "img.ridertraining.collage", label: "One-to-one coaching photo", def: `${ART}jyt8bv4m_Skills-day-watching-closely-Medium.jpg` },
  { group: "Rider Training page", key: "img.ridertraining.slow", label: "Slow-riding photo", def: `${ART}gu08wcps_skills-day-Medium.jpg` },
  { group: "Rider Training page", key: "img.ridertraining.hgv", label: "Awareness session photo", def: "https://images.pexels.com/photos/13008065/pexels-photo-13008065.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940" },
  { group: "Rider Training page", key: "img.ridertraining.circuit", label: "Group ride / training-day photo", def: `${ART}iggs94a1_IPSGA-Medium.jpeg` },
  { group: "Rider Training page", key: "img.ridertraining.scenic1", label: "Continuous development photo", def: "https://images.unsplash.com/photo-1690540293162-bfb097e68a34?crop=entropy&cs=srgb&fm=jpg&q=85&w=940" },
  { group: "Rider Training page", key: "img.ridertraining.scenic2", label: "Photo strip — large", def: "https://images.unsplash.com/photo-1690540293148-affdded8f45a?crop=entropy&cs=srgb&fm=jpg&q=85&w=940" },
  { group: "Rider Training page", key: "img.ridertraining.scenic3", label: "Photo strip — small", def: "https://images.unsplash.com/photo-1690540293130-2ca04fbf815b?crop=entropy&cs=srgb&fm=jpg&q=85&w=940" },
  { group: "Driver Training page", key: "img.drivertraining.hero", label: "Hero background", def: "https://images.pexels.com/photos/13861/IMG_3496bfree.jpg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940" },
  { group: "Free Assessment page", key: "img.assessment.rider", label: "Advanced Riding photo", def: "https://images.unsplash.com/photo-1598548841213-9cdbcdf8ec47?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600" },
  { group: "Free Assessment page", key: "img.assessment.driver", label: "Advanced Driving photo", def: "https://images.unsplash.com/photo-1553782097-130fef5d3e27?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600" },
  { group: "Free Assessment page", key: "img.assessment.map", label: "Coverage map", def: "https://static.prod-images.emergentagent.com/jobs/51fe5950-e829-4696-b661-6142eda1be8b/images/d6dbaae201e609b24ee074593b6f9749de833de96e299cece4f6910b93159cfc.jpeg" },
  { group: "Free Assessment page", key: "img.assessment.loop0", label: "Big-screen background 1", def: "https://images.unsplash.com/photo-1762342842947-57da5a00426d?crop=entropy&cs=srgb&fm=jpg&q=85&w=1920" },
  { group: "Free Assessment page", key: "img.assessment.loop1", label: "Big-screen background 2", def: "https://images.unsplash.com/photo-1630406144797-821be1f35d75?crop=entropy&cs=srgb&fm=jpg&q=85&w=1920" },
  { group: "Free Assessment page", key: "img.assessment.loop2", label: "Big-screen background 3", def: "https://images.unsplash.com/photo-1764862024029-aafc09b47fb5?crop=entropy&cs=srgb&fm=jpg&q=85&w=1920" },
  { group: "Riders page", key: "img.pathway.teaser1", label: "Training teaser — large", def: `${ART}17pxmkkt_above-the-clouds-Medium.jpeg` },
  { group: "Riders page", key: "img.pathway.teaser2", label: "Training teaser — small 1", def: `${ART}gu08wcps_skills-day-Medium.jpg` },
  { group: "Riders page", key: "img.pathway.teaser3", label: "Training teaser — small 2", def: `${ART}jyt8bv4m_Skills-day-watching-closely-Medium.jpg` },
  { group: "Home page", key: "img.home.card.rider", label: "Advanced Riders card photo", def: `${ART}y77k4wfe_image.png` },
  { group: "Home page", key: "img.home.card.driver", label: "Advanced Drivers card photo", def: "https://images.unsplash.com/photo-1449965408869-eaa3f722e40d?auto=format&fit=crop&q=85&w=800" },
];

function ContentImages({ data, onSaved }) {
  const BACKEND = process.env.REACT_APP_BACKEND_URL;
  const resolveImg = (v) => (v && v.startsWith("/") ? `${BACKEND}${v}` : v);
  const [cropKey, setCropKey] = useState(null);
  const [file, setFile] = useState(null);
  const [busyKey, setBusyKey] = useState(null);

  const groups = {};
  INLINE_IMAGES.forEach((im) => { (groups[im.group] = groups[im.group] || []).push(im); });

  const pick = (key) => (e) => { const f = e.target.files?.[0]; e.target.value = ""; if (f) { setCropKey(key); setFile(f); } };
  const onCropDone = async (cropped) => {
    const key = cropKey; setFile(null); setCropKey(null); setBusyKey(key);
    try {
      const fd = new FormData(); fd.append("file", cropped);
      const { data: up } = await api.post("/admin/images/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      await api.put("/admin/content", { updates: { [key]: up.path } });
      toast.success("Image updated — live now"); onSaved();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setBusyKey(null); }
  };
  const revert = async (key) => {
    setBusyKey(key);
    try { await api.put("/admin/content", { updates: { [key]: "" } }); toast.success("Reverted to original — live now"); onSaved(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setBusyKey(null); }
  };

  return (
    <div className="rounded-2xl bg-white border border-slate-200 p-5" data-testid="content-images">
      <h3 className="font-bold text-[#0F2744] mb-1">Page photos</h3>
      <p className="text-sm text-slate-500 mb-4">Replace any in-page photo with your own — crop and straighten before it saves. "Revert" returns to the original built-in photo. Changes go live instantly.</p>
      <ImageCropDialog file={file} defaultAspect={16 / 9} onCancel={() => { setFile(null); setCropKey(null); }} onDone={onCropDone} />
      <div className="space-y-6">
        {Object.keys(groups).map((g) => (
          <div key={g}>
            <h4 className="text-xs uppercase tracking-wide text-slate-400 font-semibold mb-3">{g}</h4>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
              {groups[g].map((im) => {
                const current = data[im.key] || im.def;
                const overridden = !!data[im.key];
                const busy = busyKey === im.key;
                return (
                  <div key={im.key} className="rounded-xl border border-slate-100 overflow-hidden" data-testid={`img-card-${im.key}`}>
                    <div className="relative h-28 bg-slate-100">
                      <img src={resolveImg(current)} alt={im.label} className="w-full h-full object-cover" />
                      {busy && <div className="absolute inset-0 bg-white/60 flex items-center justify-center"><Loader2 className="h-5 w-5 animate-spin text-[#0F2744]" /></div>}
                      {overridden && <span className="absolute top-1 left-1 text-[10px] bg-[#0F2744] text-white rounded px-1.5 py-0.5">Custom</span>}
                    </div>
                    <div className="p-2.5">
                      <div className="text-xs font-semibold text-[#0F2744] leading-tight min-h-[2rem]">{im.label}</div>
                      <div className="flex items-center gap-2 mt-2">
                        <label className="flex-1">
                          <input type="file" accept="image/*" className="hidden" onChange={pick(im.key)} data-testid={`img-replace-${im.key}`} />
                          <span className="inline-flex w-full justify-center items-center gap-1 rounded-lg bg-[#0F2744] text-white text-xs font-semibold py-1.5 cursor-pointer hover:bg-[#0B192C]"><Upload className="h-3.5 w-3.5" /> Replace</span>
                        </label>
                        {overridden && <button onClick={() => revert(im.key)} disabled={busy} className="text-xs font-semibold text-slate-500 hover:text-red-600 px-1.5" data-testid={`img-revert-${im.key}`}>Revert</button>}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function ContentPanel() {
  const { refresh } = useContent();
  const [data, setData] = useState(null);
  const [draft, setDraft] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/content").then(({ data }) => { setData(data); setDraft(data); }).catch(() => {});
  }, []);

  if (!data) return <div className="flex justify-center py-16"><Loader2 className="h-8 w-8 animate-spin text-[#0F2744]" /></div>;

  const dirty = Object.keys(draft).some((k) => draft[k] !== data[k]);
  const groups = {};
  Object.keys(draft).sort().forEach((k) => {
    if (k.startsWith("img.")) return;
    const g = k.split(".")[0];
    (groups[g] = groups[g] || []).push(k);
  });

  const prettyLabel = (key) => key.split(".").slice(1).join(" · ").replace(/([A-Z])/g, " $1");

  const save = async () => {
    const updates = {};
    Object.keys(draft).forEach((k) => { if (draft[k] !== data[k]) updates[k] = draft[k]; });
    if (Object.keys(updates).length === 0) return;
    setSaving(true);
    try {
      await api.put("/admin/content", { updates });
      setData({ ...draft });
      toast.success("Content saved — changes are live");
      refresh();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const reloadImages = () => api.get("/content").then(({ data: d }) => {
    setData((prev) => ({ ...prev, ...Object.fromEntries(Object.entries(d).filter(([k]) => k.startsWith("img."))) }));
    setDraft((prev) => ({ ...prev, ...Object.fromEntries(Object.entries(d).filter(([k]) => k.startsWith("img."))) }));
    refresh();
  }).catch(() => {});

  return (
    <div data-testid="content-panel">
      <div className="flex items-center justify-between mb-5 flex-wrap gap-3">
        <p className="text-sm text-slate-500 max-w-2xl">Edit any wording below and press Save. Changes go live across the website instantly — no developer needed.</p>
        <Button onClick={save} disabled={!dirty || saving} className="bg-[#0F2744] hover:bg-[#0B192C]" data-testid="save-content-btn">
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save changes"}
        </Button>
      </div>
      <div className="space-y-6">
        <ContentImages data={data} onSaved={reloadImages} />
        {Object.keys(groups).map((g) => (
          <div key={g} className="rounded-2xl bg-white border border-slate-200 p-5">
            <h3 className="font-bold text-[#0F2744] mb-4">{GROUP_LABELS[g] || g}</h3>
            <div className="space-y-4">
              {groups[g].map((key) => (
                <div key={key}>
                  <Label className="mb-1.5 block text-xs uppercase tracking-wide text-slate-400 capitalize">{prettyLabel(key)}</Label>
                  <Textarea
                    rows={draft[key] && draft[key].length > 90 ? 3 : 1}
                    value={draft[key] || ""}
                    onChange={(e) => setDraft((d) => ({ ...d, [key]: e.target.value }))}
                    data-testid={`content-${key}`}
                  />
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
      <div className="flex justify-end mt-6">
        <Button onClick={save} disabled={!dirty || saving} className="bg-[#0F2744] hover:bg-[#0B192C]" data-testid="save-content-btn-bottom">
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save changes"}
        </Button>
      </div>
    </div>
  );
}
